/**
 * BayBackground — 2.5D Full-Fidelity Hangar Engine Module
 * 
 * 100% Feature-Parity with sandbox/the-bay/bay.js:
 * - 5 Baked static planes: bg, lights (overhead lamps & ambient shafts), monitors (catwalk), hero (A9 dock), fg
 * - 2 Dynamic FX canvases:
 *     bgCanvas (depth 8): sweeping spotlight, god rays, deep bay activity, rover, patrolling technician,
 *                         Human marine fireteam marching patrol, welding drone with arc flashes & sparks,
 *                         Vanguard tank heat shimmer & hazard beacons
 *     fgCanvas (depth 18): A9 Piranha rotating main rotor, overhead gantry crane with cargo,
 *                          catwalk crew (technician, Shade Infiltrator with laser optic, Deck Officer in uniform,
 *                          fluttering mission papers), 3D Holo-Globe with orbiting blip, ground fog / cryo haze,
 *                          pilot with 16s coffee routine & rising steam, marine sentry with salute,
 *                          hangar mechanic, Piranha hydraulics, strobes, FLIR mouse/gyro tracking,
 *                          sensor optic laser scan with target acquisition brackets [TRG: X,Y],
 *                          holographic cockpit HUD, dynamic rim lighting, thermal engine haze,
 *                          live monitor displays (Monitor 1: rotating 3D wireframe A9 Piranha mesh + telemetry,
 *                          Monitor 2: Plasma gauge + Fugazi status, Monitor 3: rotating radar sweep),
 *                          horizontal anamorphic Panavision lens flares, concentric Airy diffraction bokeh discs,
 *                          cinematic Teal & Orange color grading, night shift lighting pass.
 * - Hardware-accelerated CSS translate3d micro-parallax across all 7 layers.
 * - Atomic lifecycle management: resume() when modal opens, pause() when modal closes / match starts (0% CPU/GPU overhead in matches).
 */

const BASE_WIDTH = 1024;
const BASE_HEIGHT = 576;
const SCALE = 2;
const CANVAS_WIDTH = 2048;
const CANVAS_HEIGHT = 1152;

const HELI_MODEL = Object.freeze({
  nodes: Object.freeze([
    [-28, 0, 0], [28, 0, 0], [0, -8, 0], [0, 9, 0],
    [0, -18, 0],
    [-36, -18, 0], [36, -18, 0], [0, -18, -36], [0, -18, 36],
    [42, -5, 0], [52, -14, 0],
    [-14, 4, -18], [-14, 4, 18],
  ]),
  edges: Object.freeze([
    [0, 1], [0, 2], [1, 2], [0, 3], [1, 3],
    [2, 4], [5, 6], [7, 8],
    [1, 9], [9, 10],
    [3, 11], [3, 12],
  ]),
});

// 24x24 Sprite Matrix Spans for Sul Defective Loaf (DefectiveLoaf_Front from unitSprites.js)
const DEFECTIVE_LOAF_SPANS = [
  ['#facc15', [[5,0,1,1],[18,0,1,1],[4,1,1,1],[8,1,1,1],[15,1,1,1],[19,1,1,1],[11,2,2,1],[11,14,2,1]]],
  ['#ca8a04', [[5,1,1,1],[18,1,1,1],[7,2,1,1],[16,2,1,1],[11,3,2,1],[9,4,1,1],[14,4,1,1],[6,7,1,1],[8,7,1,1],[15,7,1,1],[17,7,1,1],[6,8,1,1],[17,8,1,1],[7,9,1,1],[16,9,1,1],[7,10,1,1],[16,10,1,1],[7,19,1,1],[16,19,1,1]]],
  ['#a3e635', [[3,2,1,1],[20,2,1,1],[2,3,1,1],[21,3,1,1],[11,5,2,1]]],
  ['#84cc16', [[4,2,1,1],[8,2,1,1],[15,2,1,1],[19,2,1,1],[7,3,1,1],[16,3,1,1],[11,4,2,1],[10,5,1,1],[13,5,1,1]]],
  ['#65a30d', [[5,2,1,1],[18,2,1,1],[3,3,1,1],[6,3,1,1],[8,3,1,1],[15,3,1,1],[17,3,1,1],[20,3,1,1],[4,4,1,1],[19,4,1,1],[9,5,1,1],[14,5,1,1],[9,6,1,1],[14,6,1,1]]],
  ['#4d7c0f', [[5,3,1,1],[18,3,1,1],[5,4,1,1],[7,4,1,1],[16,4,1,1],[18,4,1,1],[6,5,1,1],[8,5,1,1],[15,5,1,1],[17,5,1,1],[8,6,1,1],[15,6,1,1]]],
  ['#365314', [[8,4,1,1],[15,4,1,1],[7,5,1,1],[16,5,1,1],[7,6,1,1],[16,6,1,1],[8,17,1,1],[15,17,1,1],[7,20,1,1],[16,20,1,1]]],
  ['#a21caf', [[10,6,1,1],[13,6,1,1],[9,9,1,1],[14,9,1,1],[11,10,2,1]]],
  ['#c026d3', [[11,6,2,1],[9,7,1,1],[14,7,1,1]]],
  ['#eab308', [[7,7,1,1],[16,7,1,1],[8,8,1,1],[15,8,1,1],[8,10,1,1],[15,10,1,1]]],
  ['#e879f9', [[10,7,1,1],[13,7,1,1]]],
  ['#fae8ff', [[11,7,2,1]]],
  ['#fde047', [[7,8,1,1],[16,8,1,1],[8,9,1,1],[15,9,1,1],[9,10,1,1],[14,10,1,1]]],
  ['#d946ef', [[9,8,1,1],[14,8,1,1],[10,9,1,1],[13,9,1,1]]],
  ['#ffffff', [[10,8,4,1]]],
  ['#b45309', [[6,9,1,1],[17,9,1,1],[7,17,1,1],[16,17,1,1]]],
  ['#f0abfc', [[11,9,2,1],[12,19,1,1]]],
  ['#701a75', [[10,10,1,1],[13,10,1,1]]],
  ['#1e40af', [[7,11,1,1],[16,11,1,1],[8,13,1,1],[15,13,1,1],[9,14,1,1],[14,14,1,1],[10,15,1,1],[13,15,1,1]]],
  ['#2563eb', [[8,11,1,1],[15,11,1,1],[9,12,1,1],[14,12,1,1]]],
  ['#3b82f6', [[9,11,1,1],[14,11,1,1],[8,12,1,1],[10,12,1,1],[13,12,1,1],[15,12,1,1]]],
  ['#60a5fa', [[10,11,1,1],[13,11,1,1],[11,12,2,1]]],
  ['#93c5fd', [[11,11,2,1]]],
  ['#1e3a8a', [[7,12,1,1],[16,12,1,1],[7,13,1,1],[16,13,1,1],[8,14,1,1],[15,14,1,1],[9,15,1,1],[14,15,1,1],[9,16,1,1],[14,16,1,1]]],
  ['#1d4ed8', [[9,13,1,1],[14,13,1,1],[11,15,2,1]]],
  ['#0f172a', [[10,13,4,1],[10,14,1,1],[13,14,1,1],[8,15,1,1],[15,15,1,1]]],
  ['#991b1b', [[7,16,1,1],[16,16,1,1]]],
  ['#7f1d1d', [[8,16,1,1],[15,16,1,1],[6,17,1,1],[17,17,1,1]]],
  ['#4338ca', [[10,16,1,1],[13,16,1,1],[10,18,1,1]]],
  ['#6366f1', [[11,16,2,1],[10,17,1,1],[13,17,1,1]]],
  ['#818cf8', [[11,17,2,1],[13,18,1,1]]],
  ['#450a0a', [[6,18,1,1],[17,18,1,1]]],
  ['#78350f', [[7,18,1,1],[16,18,1,1]]],
  ['#a855f7', [[11,18,1,1]]],
  ['#c084fc', [[12,18,1,1]]],
];

let _alienTankLoafCache = null;

function getBakedAlienTankLoaf() {
  if (_alienTankLoafCache) return _alienTankLoafCache;
  if (typeof document === 'undefined' || !document.createElement) return null;
  const cvs = document.createElement('canvas');
  cvs.width = 34;
  cvs.height = 32;
  const cctx = cvs.getContext('2d');
  const S = 1.35;
  for (let i = 0; i < DEFECTIVE_LOAF_SPANS.length; i++) {
    const [col, spans] = DEFECTIVE_LOAF_SPANS[i];
    cctx.fillStyle = col;
    for (let j = 0; j < spans.length; j++) {
      const [sx, sy, sw, sh] = spans[j];
      cctx.fillRect(sx * S, sy * S, sw * S + 0.05, sh * S + 0.05);
    }
  }
  _alienTankLoafCache = cvs;
  return cvs;
}

const APOSTATE_SPANS_IDLE = [["#c084fc",[[11,0,2,1]]],["#e879f9",[[7,1,1,1],[16,1,1,1]]],["#382e29",[[9,1,1,1],[15,1,1,1],[7,2,1,1],[17,2,1,1],[6,3,1,1],[10,3,1,1],[13,3,1,1],[17,3,1,1],[5,4,1,1],[9,4,1,1],[14,4,1,1],[18,4,1,1],[6,5,1,1],[8,5,1,1],[13,5,1,1],[15,5,1,1]]],["#504040",[[10,1,1,1],[14,1,1,1],[8,2,1,1],[16,2,1,1],[7,3,1,1],[16,3,1,1],[6,4,1,1],[8,4,1,1],[10,4,1,1],[13,4,1,1],[15,4,1,1],[17,4,1,1]]],["#6e594d",[[11,1,1,1],[13,1,1,1],[9,2,1,1],[11,2,1,1],[13,2,1,1],[15,2,1,1],[8,3,1,1],[14,3,1,1],[7,4,1,1],[11,4,2,1],[16,4,1,1],[10,7,1,1],[13,7,1,1]]],["#856a5d",[[12,1,1,1],[10,2,1,1],[12,2,1,1],[14,2,1,1],[9,3,1,1],[11,3,2,1],[15,3,1,1],[10,9,4,1]]],["#38bdf8",[[5,2,1,1]]],["#241e1a",[[6,2,1,1],[18,2,1,1],[4,4,1,1],[19,4,1,1],[5,5,1,1],[7,5,1,1],[14,5,1,1],[16,5,1,1]]],["#1a1512",[[4,3,1,1],[19,3,1,1],[9,5,1,1],[12,5,1,1],[17,5,1,1],[9,11,1,1],[14,11,1,1]]],["#2e2520",[[5,3,1,1],[18,3,1,1],[8,7,1,1],[15,7,1,1]]],["#9a6e58",[[2,4,1,1],[21,4,1,1],[2,6,1,1],[21,6,1,1],[1,8,1,1],[4,8,1,1],[11,8,2,1],[20,8,1,1],[22,8,1,1],[1,10,1,1],[3,10,1,1],[20,10,1,1],[22,10,1,1],[0,12,1,1],[3,12,1,1],[21,12,1,1],[23,12,1,1],[2,14,1,1],[21,14,1,1]]],["#3b322a",[[3,4,1,1],[20,4,1,1],[2,5,1,1],[21,5,1,1]]],["#5a4035",[[3,5,1,1],[20,5,1,1],[3,6,1,1],[20,6,1,1],[6,7,1,1],[11,7,2,1],[17,7,1,1],[2,8,1,1],[5,8,1,1],[10,8,1,1],[13,8,1,1],[19,8,1,1],[21,8,1,1],[6,9,1,1],[8,9,1,1],[15,9,1,1],[17,9,1,1],[2,10,1,1],[4,10,1,1],[10,10,1,1],[13,10,1,1],[19,10,1,1],[21,10,1,1],[6,11,1,1],[17,11,1,1],[1,12,1,1],[4,12,1,1],[20,12,1,1],[22,12,1,1],[0,13,1,1],[6,13,1,1],[17,13,1,1],[23,13,1,1],[3,14,1,1],[20,14,1,1],[0,15,1,1],[2,15,1,1],[7,15,1,1],[17,15,1,1],[21,15,1,1],[23,15,1,1],[0,17,1,1],[3,17,1,1],[7,17,1,1],[16,17,1,1],[20,17,1,1],[23,17,1,1],[1,19,1,1],[4,19,1,1],[7,19,1,1],[16,19,1,1],[19,19,1,1],[22,19,1,1],[1,21,1,1],[4,21,1,1],[7,21,1,1],[16,21,1,1],[19,21,1,1],[22,21,1,1]]],["#7a5848",[[4,5,1,1],[19,5,1,1],[1,7,1,1],[4,7,1,1],[19,7,1,1],[22,7,1,1],[6,8,1,1],[8,8,2,1],[14,8,2,1],[17,8,1,1],[1,9,1,1],[3,9,1,1],[20,9,1,1],[22,9,1,1],[6,10,1,1],[11,10,2,1],[17,10,1,1],[0,11,1,1],[3,11,1,1],[20,11,1,1],[23,11,1,1],[6,12,1,1],[17,12,1,1],[2,13,1,1],[21,13,1,1],[0,14,1,1],[6,14,1,1],[17,14,1,1],[23,14,1,1],[0,16,1,1],[3,16,1,1],[7,16,1,1],[16,16,1,1],[20,16,1,1],[23,16,1,1],[1,18,1,1],[3,18,1,1],[7,18,1,1],[16,18,1,1],[20,18,1,1],[22,18,1,1],[1,20,1,1],[4,20,1,1],[7,20,1,1],[16,20,1,1],[19,20,1,1],[22,20,1,1],[1,22,1,1],[5,22,1,1],[7,22,1,1],[16,22,1,1],[18,22,1,1],[22,22,1,1]]],["#06b6d4",[[10,5,2,1],[7,6,1,1],[11,6,2,1],[16,6,1,1],[11,11,2,1],[12,15,1,1]]],["#4a3d35",[[4,6,1,1],[19,6,1,1],[9,7,1,1],[14,7,1,1]]],["#3b2d26",[[5,6,1,1],[18,6,1,1],[2,7,1,1],[5,7,1,1],[7,7,1,1],[16,7,1,1],[18,7,1,1],[21,7,1,1],[7,8,1,1],[16,8,1,1],[2,9,1,1],[4,9,1,1],[9,9,1,1],[14,9,1,1],[19,9,1,1],[21,9,1,1],[7,10,3,1],[14,10,3,1],[1,11,1,1],[4,11,1,1],[10,11,1,1],[13,11,1,1],[19,11,1,1],[22,11,1,1],[7,12,1,1],[16,12,1,1],[3,13,1,1],[20,13,1,1],[1,14,1,1],[7,14,1,1],[16,14,1,1],[22,14,1,1],[1,16,1,1],[4,16,1,1],[8,16,1,1],[15,16,1,1],[19,16,1,1],[22,16,1,1],[2,18,1,1],[4,18,1,1],[8,18,1,1],[15,18,1,1],[19,18,1,1],[21,18,1,1],[2,20,1,1],[5,20,1,1],[8,20,1,1],[15,20,1,1],[18,20,1,1],[21,20,1,1],[2,22,1,1],[6,22,1,1],[8,22,1,1],[15,22,1,1],[17,22,1,1],[21,22,1,1]]],["#22d3ee",[[6,6,1,1],[9,6,1,1],[14,6,1,1],[17,6,1,1],[11,12,2,1]]],["#00f0ff",[[8,6,1,1],[10,6,1,1],[13,6,1,1],[15,6,1,1],[10,12,1,1],[13,12,1,1],[12,13,1,1],[11,15,1,1]]],["#241a14",[[7,9,1,1],[16,9,1,1],[7,11,1,1],[16,11,1,1],[1,13,1,1],[7,13,1,1],[16,13,1,1],[22,13,1,1],[1,15,1,1],[3,15,1,1],[8,15,1,1],[16,15,1,1],[20,15,1,1],[22,15,1,1],[1,17,1,1],[4,17,1,1],[8,17,1,1],[15,17,1,1],[19,17,1,1],[22,17,1,1],[2,19,1,1],[5,19,1,1],[8,19,1,1],[15,19,1,1],[18,19,1,1],[21,19,1,1],[2,21,1,1],[5,21,1,1],[8,21,1,1],[15,21,1,1],[18,21,1,1],[21,21,1,1],[1,23,1,1],[5,23,1,1],[7,23,1,1],[16,23,1,1],[18,23,1,1],[22,23,1,1]]],["#0284c7",[[10,13,1,1],[13,13,1,1],[11,14,2,1]]],["#ffffff",[[11,13,1,1]]],["#0f172a",[[10,14,1,1],[13,14,1,1]]],["#0891b2",[[11,16,2,1]]]];
const APOSTATE_SPANS_WALK1 = [["#c084fc",[[11,0,2,1]]],["#e879f9",[[7,1,1,1],[16,1,1,1]]],["#382e29",[[9,1,1,1],[15,1,1,1],[7,2,1,1],[17,2,1,1],[6,3,1,1],[10,3,1,1],[13,3,1,1],[17,3,1,1],[5,4,1,1],[9,4,1,1],[14,4,1,1],[18,4,1,1],[6,5,1,1],[8,5,1,1],[13,5,1,1],[15,5,1,1]]],["#504040",[[10,1,1,1],[14,1,1,1],[8,2,1,1],[16,2,1,1],[7,3,1,1],[16,3,1,1],[6,4,1,1],[8,4,1,1],[10,4,1,1],[13,4,1,1],[15,4,1,1],[17,4,1,1]]],["#6e594d",[[11,1,1,1],[13,1,1,1],[9,2,1,1],[11,2,1,1],[13,2,1,1],[15,2,1,1],[8,3,1,1],[14,3,1,1],[7,4,1,1],[11,4,2,1],[16,4,1,1],[10,7,1,1],[13,7,1,1]]],["#856a5d",[[12,1,1,1],[10,2,1,1],[12,2,1,1],[14,2,1,1],[9,3,1,1],[11,3,2,1],[15,3,1,1],[10,9,4,1]]],["#38bdf8",[[5,2,1,1]]],["#241e1a",[[6,2,1,1],[18,2,1,1],[4,4,1,1],[19,4,1,1],[5,5,1,1],[7,5,1,1],[14,5,1,1],[16,5,1,1]]],["#1a1512",[[4,3,1,1],[19,3,1,1],[20,4,1,1],[9,5,1,1],[12,5,1,1],[17,5,1,1],[9,11,1,1],[14,11,1,1]]],["#2e2520",[[5,3,1,1],[18,3,1,1],[8,7,1,1],[15,7,1,1]]],["#9a6e58",[[2,4,1,1],[22,4,1,1],[2,6,1,1],[19,6,1,1],[22,6,1,1],[6,7,1,1],[1,8,1,1],[4,8,1,1],[11,8,2,1],[20,8,1,1],[22,8,1,1],[0,10,1,1],[3,10,1,1],[20,10,1,1],[23,10,1,1],[3,12,1,1],[21,12,1,1],[23,12,1,1],[2,14,1,1]]],["#3b322a",[[3,4,1,1],[21,4,1,1],[2,5,1,1],[22,5,1,1]]],["#5a4035",[[3,5,1,1],[21,5,1,1],[3,6,1,1],[21,6,1,1],[11,7,2,1],[17,7,1,1],[2,8,1,1],[5,8,1,1],[10,8,1,1],[13,8,1,1],[19,8,1,1],[21,8,1,1],[6,9,1,1],[8,9,1,1],[15,9,1,1],[17,9,1,1],[1,10,1,1],[4,10,1,1],[10,10,1,1],[13,10,1,1],[19,10,1,1],[22,10,1,1],[6,11,1,1],[17,11,1,1],[4,12,1,1],[20,12,1,1],[22,12,1,1],[0,13,1,1],[6,13,1,1],[17,13,1,1],[3,14,1,1],[1,15,1,1],[6,15,1,1],[17,15,1,1],[20,15,1,1],[23,15,1,1],[1,17,1,1],[3,17,1,1],[6,17,1,1],[17,17,1,1],[19,17,1,1],[23,17,1,1],[1,19,1,1],[3,19,1,1],[6,19,1,1],[17,19,1,1],[19,19,1,1],[23,19,1,1],[2,21,1,1],[4,21,1,1],[6,21,1,1],[17,21,2,1],[23,21,1,1]]],["#7a5848",[[4,5,1,1],[19,5,1,1],[1,7,1,1],[4,7,1,1],[18,7,2,1],[22,7,1,1],[6,8,4,1],[14,8,2,1],[17,8,1,1],[1,9,1,1],[3,9,1,1],[20,9,1,1],[23,9,1,1],[6,10,1,1],[11,10,2,1],[17,10,1,1],[0,11,1,1],[3,11,1,1],[20,11,1,1],[23,11,1,1],[0,12,1,1],[6,12,1,1],[17,12,1,1],[2,13,1,1],[21,13,1,1],[23,13,1,1],[1,14,1,1],[6,14,1,1],[17,14,1,1],[21,14,1,1],[23,14,1,1],[2,15,1,1],[1,16,2,1],[6,16,1,1],[17,16,1,1],[20,16,1,1],[23,16,1,1],[1,18,1,1],[3,18,1,1],[6,18,1,1],[17,18,1,1],[19,18,1,1],[23,18,1,1],[2,20,2,1],[6,20,1,1],[17,20,2,1],[23,20,1,1],[2,22,1,1],[4,22,1,1],[6,22,1,1],[17,22,1,1],[23,22,1,1]]],["#06b6d4",[[10,5,2,1],[7,6,1,1],[11,6,2,1],[16,6,1,1],[11,11,2,1],[12,15,1,1]]],["#4a3d35",[[4,6,1,1],[9,7,1,1],[14,7,1,1]]],["#3b2d26",[[5,6,1,1],[18,6,1,1],[2,7,1,1],[5,7,1,1],[7,7,1,1],[16,7,1,1],[21,7,1,1],[16,8,1,1],[2,9,1,1],[4,9,1,1],[9,9,1,1],[14,9,1,1],[19,9,1,1],[22,9,1,1],[7,10,3,1],[14,10,3,1],[1,11,1,1],[4,11,1,1],[10,11,1,1],[13,11,1,1],[19,11,1,1],[22,11,1,1],[1,12,1,1],[7,12,1,1],[16,12,1,1],[3,13,1,1],[20,13,1,1],[22,13,1,1],[7,14,1,1],[16,14,1,1],[20,14,1,1],[22,14,1,1],[3,15,1,1],[3,16,1,1],[7,16,1,1],[16,16,1,1],[19,16,1,1],[22,16,1,1],[2,18,1,1],[4,18,1,1],[7,18,1,1],[16,18,1,1],[18,18,1,1],[22,18,1,1],[4,20,1,1],[7,20,1,1],[16,20,1,1],[22,20,1,1],[3,22,1,1],[5,22,1,1],[7,22,1,1],[16,22,1,1],[22,22,1,1]]],["#22d3ee",[[6,6,1,1],[9,6,1,1],[14,6,1,1],[17,6,1,1],[11,12,2,1]]],["#00f0ff",[[8,6,1,1],[10,6,1,1],[13,6,1,1],[15,6,1,1],[10,12,1,1],[13,12,1,1],[12,13,1,1],[11,15,1,1]]],["#241a14",[[7,9,1,1],[16,9,1,1],[7,11,1,1],[16,11,1,1],[1,13,1,1],[7,13,1,1],[16,13,1,1],[7,15,1,1],[16,15,1,1],[19,15,1,1],[22,15,1,1],[2,17,1,1],[4,17,1,1],[7,17,1,1],[16,17,1,1],[18,17,1,1],[22,17,1,1],[2,19,1,1],[4,19,1,1],[7,19,1,1],[16,19,1,1],[18,19,1,1],[22,19,1,1],[3,21,1,1],[5,21,1,1],[7,21,1,1],[16,21,1,1],[22,21,1,1],[2,23,1,1],[4,23,1,1],[6,23,1,1],[17,23,1,1],[23,23,1,1]]],["#0284c7",[[10,13,1,1],[13,13,1,1],[11,14,2,1]]],["#ffffff",[[11,13,1,1]]],["#0f172a",[[10,14,1,1],[13,14,1,1]]],["#0891b2",[[11,16,2,1]]]];
const APOSTATE_SPANS_WALK2 = [["#c084fc",[[11,0,2,1]]],["#e879f9",[[7,1,1,1],[16,1,1,1]]],["#382e29",[[9,1,1,1],[15,1,1,1],[7,2,1,1],[17,2,1,1],[6,3,1,1],[10,3,1,1],[13,3,1,1],[17,3,1,1],[5,4,1,1],[9,4,1,1],[14,4,1,1],[18,4,1,1],[6,5,1,1],[8,5,1,1],[13,5,1,1],[15,5,1,1]]],["#504040",[[10,1,1,1],[14,1,1,1],[8,2,1,1],[16,2,1,1],[7,3,1,1],[16,3,1,1],[6,4,1,1],[8,4,1,1],[10,4,1,1],[13,4,1,1],[15,4,1,1],[17,4,1,1]]],["#6e594d",[[11,1,1,1],[13,1,1,1],[9,2,1,1],[11,2,1,1],[13,2,1,1],[15,2,1,1],[8,3,1,1],[14,3,1,1],[7,4,1,1],[11,4,2,1],[16,4,1,1],[10,7,1,1],[13,7,1,1]]],["#856a5d",[[12,1,1,1],[10,2,1,1],[12,2,1,1],[14,2,1,1],[9,3,1,1],[11,3,2,1],[15,3,1,1],[10,9,4,1]]],["#38bdf8",[[5,2,1,1]]],["#241e1a",[[6,2,1,1],[18,2,1,1],[4,4,1,1],[19,4,1,1],[5,5,1,1],[7,5,1,1],[14,5,1,1],[16,5,1,1]]],["#1a1512",[[4,3,1,1],[19,3,1,1],[9,5,1,1],[12,5,1,1],[17,5,1,1],[9,11,1,1],[14,11,1,1]]],["#2e2520",[[5,3,1,1],[18,3,1,1],[8,7,1,1]]],["#3b322a",[[20,3,1,1],[21,4,1,1],[2,5,1,1]]],["#9a6e58",[[21,3,1,1],[2,4,1,1],[2,6,2,1],[19,6,1,1],[21,6,1,1],[6,7,1,1],[1,8,1,1],[3,8,1,1],[11,8,2,1],[20,8,1,1],[22,8,1,1],[1,10,2,1],[20,10,1,1],[22,10,1,1],[0,12,1,1]]],["#7a5848",[[3,4,1,1],[19,5,1,1],[21,5,1,1],[1,7,1,1],[3,7,1,1],[5,7,1,1],[17,7,3,1],[22,7,1,1],[6,8,4,1],[14,8,1,1],[16,8,1,1],[1,9,2,1],[20,9,1,1],[22,9,1,1],[6,10,1,1],[11,10,2,1],[16,10,1,1],[0,11,1,1],[2,11,1,1],[20,11,1,1],[2,12,1,1],[6,12,1,1],[16,12,1,1],[22,12,1,1],[0,14,1,1],[2,14,1,1],[6,14,1,1],[15,14,1,1],[22,14,1,1],[0,16,1,1],[3,16,1,1],[7,16,1,1],[15,16,1,1],[20,16,1,1],[22,16,1,1],[1,18,1,1],[7,18,1,1],[20,18,1,1],[22,18,1,1],[1,20,1,1],[7,20,1,1],[19,20,1,1],[1,22,1,1],[7,22,1,1],[18,22,1,1]]],["#5a4035",[[20,4,1,1],[4,6,1,1],[16,6,1,1],[20,6,1,1],[11,7,2,1],[16,7,1,1],[2,8,1,1],[4,8,1,1],[10,8,1,1],[13,8,1,1],[17,8,1,1],[19,8,1,1],[21,8,1,1],[6,9,1,1],[8,9,1,1],[16,9,1,1],[3,10,1,1],[10,10,1,1],[13,10,1,1],[19,10,1,1],[21,10,1,1],[6,11,1,1],[16,11,1,1],[22,11,1,1],[1,12,1,1],[20,12,1,1],[0,13,1,1],[2,13,1,1],[6,13,1,1],[15,13,1,1],[22,13,1,1],[20,14,1,1],[0,15,1,1],[3,15,1,1],[7,15,1,1],[15,15,1,1],[22,15,1,1],[0,17,1,1],[3,17,1,1],[7,17,1,1],[15,17,1,1],[20,17,1,1],[22,17,1,1],[1,19,1,1],[7,19,1,1],[19,19,1,1],[1,21,1,1],[7,21,1,1],[19,21,1,1]]],["#4a3d35",[[3,5,1,1],[9,7,1,1],[14,7,1,1]]],["#3b2d26",[[4,5,1,1],[20,5,1,1],[15,6,1,1],[18,6,1,1],[2,7,1,1],[4,7,1,1],[7,7,1,1],[21,7,1,1],[15,8,1,1],[3,9,1,1],[9,9,1,1],[14,9,1,1],[19,9,1,1],[21,9,1,1],[7,10,3,1],[14,10,2,1],[1,11,1,1],[3,11,1,1],[10,11,1,1],[13,11,1,1],[19,11,1,1],[3,12,1,1],[7,12,1,1],[15,12,1,1],[21,12,1,1],[20,13,1,1],[1,14,1,1],[3,14,1,1],[7,14,1,1],[14,14,1,1],[21,14,1,1],[1,16,1,1],[4,16,1,1],[8,16,1,1],[14,16,1,1],[19,16,1,1],[21,16,1,1],[2,18,1,1],[8,18,1,1],[19,18,1,1],[21,18,1,1],[2,20,1,1],[8,20,1,1],[18,20,1,1],[2,22,1,1],[8,22,1,1],[17,22,1,1]]],["#06b6d4",[[10,5,2,1],[5,6,1,1],[7,6,1,1],[11,6,2,1],[11,11,2,1],[12,15,1,1]]],["#22d3ee",[[6,6,1,1],[9,6,1,1],[14,6,1,1],[17,6,1,1],[11,12,2,1]]],["#00f0ff",[[8,6,1,1],[10,6,1,1],[13,6,1,1],[10,12,1,1],[13,12,1,1],[12,13,1,1],[11,15,1,1]]],["#241a14",[[15,7,1,1],[7,9,1,1],[15,9,1,1],[7,11,1,1],[15,11,1,1],[21,11,1,1],[1,13,1,1],[3,13,1,1],[7,13,1,1],[14,13,1,1],[21,13,1,1],[1,15,1,1],[4,15,1,1],[8,15,1,1],[14,15,1,1],[20,15,2,1],[1,17,1,1],[4,17,1,1],[8,17,1,1],[14,17,1,1],[19,17,1,1],[21,17,1,1],[3,18,1,1],[15,18,1,1],[2,19,1,1],[8,19,1,1],[18,19,1,1],[22,19,1,1],[2,21,1,1],[8,21,1,1],[18,21,1,1],[1,23,1,1],[7,23,1,1],[18,23,1,1]]],["#0284c7",[[10,13,1,1],[13,13,1,1],[11,14,2,1]]],["#ffffff",[[11,13,1,1]]],["#0f172a",[[10,14,1,1],[13,14,1,1]]],["#0891b2",[[11,16,2,1]]]];
const APOSTATE_SPANS_WALK3 = [["#c084fc",[[11,0,2,1]]],["#e879f9",[[7,1,1,1],[16,1,1,1]]],["#382e29",[[9,1,1,1],[15,1,1,1],[7,2,1,1],[17,2,1,1],[6,3,1,1],[10,3,1,1],[13,3,1,1],[17,3,1,1],[5,4,1,1],[9,4,1,1],[14,4,1,1],[18,4,1,1],[6,5,1,1],[8,5,1,1],[13,5,1,1],[15,5,1,1]]],["#504040",[[10,1,1,1],[14,1,1,1],[8,2,1,1],[16,2,1,1],[7,3,1,1],[16,3,1,1],[6,4,1,1],[8,4,1,1],[10,4,1,1],[13,4,1,1],[15,4,1,1],[17,4,1,1]]],["#6e594d",[[11,1,1,1],[13,1,1,1],[9,2,1,1],[11,2,1,1],[13,2,1,1],[15,2,1,1],[8,3,1,1],[14,3,1,1],[7,4,1,1],[11,4,2,1],[16,4,1,1],[10,7,1,1],[13,7,1,1]]],["#856a5d",[[12,1,1,1],[10,2,1,1],[12,2,1,1],[14,2,1,1],[9,3,1,1],[11,3,2,1],[15,3,1,1],[10,9,4,1]]],["#38bdf8",[[5,2,1,1]]],["#241e1a",[[6,2,1,1],[18,2,1,1],[4,4,1,1],[19,4,1,1],[5,5,1,1],[7,5,1,1],[14,5,1,1],[16,5,1,1]]],["#1a1512",[[4,3,1,1],[19,3,1,1],[3,4,1,1],[9,5,1,1],[12,5,1,1],[17,5,1,1],[9,11,1,1],[14,11,1,1]]],["#2e2520",[[5,3,1,1],[18,3,1,1],[8,7,1,1],[15,7,1,1]]],["#9a6e58",[[1,4,1,1],[21,4,1,1],[1,6,1,1],[4,6,1,1],[21,6,1,1],[17,7,1,1],[1,8,1,1],[3,8,1,1],[11,8,2,1],[19,8,1,1],[22,8,1,1],[0,10,1,1],[3,10,1,1],[20,10,1,1],[23,10,1,1],[0,12,1,1],[2,12,1,1],[20,12,1,1],[21,14,1,1]]],["#3b322a",[[2,4,1,1],[20,4,1,1],[1,5,1,1],[21,5,1,1]]],["#5a4035",[[2,5,1,1],[20,5,1,1],[2,6,1,1],[20,6,1,1],[6,7,1,1],[11,7,2,1],[2,8,1,1],[4,8,1,1],[10,8,1,1],[13,8,1,1],[18,8,1,1],[21,8,1,1],[6,9,1,1],[8,9,1,1],[15,9,1,1],[17,9,1,1],[1,10,1,1],[4,10,1,1],[10,10,1,1],[13,10,1,1],[19,10,1,1],[22,10,1,1],[7,11,1,1],[17,11,1,1],[1,12,1,1],[3,12,1,1],[19,12,1,1],[7,13,1,1],[17,13,1,1],[23,13,1,1],[20,14,1,1],[0,15,1,1],[3,15,1,1],[7,15,1,1],[17,15,1,1],[22,15,1,1],[0,17,1,1],[4,17,1,1],[7,17,1,1],[16,17,1,1],[21,17,2,1],[0,19,1,1],[4,19,1,1],[8,19,1,1],[16,19,1,1],[21,19,2,1],[0,21,1,1],[5,21,1,1],[8,21,1,1],[16,21,1,1],[20,21,2,1]]],["#151210",[[3,5,1,1]]],["#7a5848",[[4,5,1,1],[19,5,1,1],[1,7,1,1],[4,7,2,1],[19,7,1,1],[22,7,1,1],[6,8,1,1],[8,8,2,1],[14,8,4,1],[1,9,1,1],[3,9,1,1],[20,9,1,1],[22,9,1,1],[6,10,1,1],[11,10,2,1],[17,10,1,1],[0,11,1,1],[3,11,1,1],[20,11,1,1],[23,11,1,1],[7,12,1,1],[17,12,1,1],[23,12,1,1],[0,13,1,1],[2,13,1,1],[21,13,1,1],[0,14,1,1],[2,14,1,1],[7,14,1,1],[17,14,1,1],[23,14,1,1],[21,15,1,1],[0,16,1,1],[3,16,1,1],[7,16,1,1],[16,16,1,1],[21,16,2,1],[0,18,1,1],[4,18,1,1],[7,18,1,1],[16,18,1,1],[21,18,2,1],[0,20,1,1],[5,20,1,1],[8,20,1,1],[16,20,1,1],[20,20,1,1],[22,20,1,1],[0,22,1,1],[6,22,1,1],[8,22,1,1],[16,22,1,1],[20,22,2,1]]],["#06b6d4",[[10,5,2,1],[7,6,1,1],[11,6,2,1],[16,6,1,1],[11,11,2,1],[12,15,1,1]]],["#3b2d26",[[5,6,1,1],[18,6,1,1],[2,7,1,1],[7,7,1,1],[16,7,1,1],[18,7,1,1],[21,7,1,1],[7,8,1,1],[2,9,1,1],[4,9,1,1],[9,9,1,1],[14,9,1,1],[19,9,1,1],[21,9,1,1],[7,10,3,1],[14,10,3,1],[1,11,1,1],[4,11,1,1],[10,11,1,1],[13,11,1,1],[19,11,1,1],[22,11,1,1],[8,12,1,1],[16,12,1,1],[22,12,1,1],[1,13,1,1],[3,13,1,1],[20,13,1,1],[1,14,1,1],[3,14,1,1],[8,14,1,1],[16,14,1,1],[22,14,1,1],[20,15,1,1],[1,16,1,1],[4,16,1,1],[8,16,1,1],[15,16,1,1],[20,16,1,1],[1,18,1,1],[5,18,1,1],[8,18,1,1],[15,18,1,1],[20,18,1,1],[1,20,1,1],[6,20,1,1],[9,20,1,1],[15,20,1,1],[19,20,1,1],[21,20,1,1],[1,22,1,1],[7,22,1,1],[9,22,1,1],[15,22,1,1],[19,22,1,1]]],["#22d3ee",[[6,6,1,1],[9,6,1,1],[14,6,1,1],[17,6,1,1],[11,12,2,1]]],["#00f0ff",[[8,6,1,1],[10,6,1,1],[13,6,1,1],[15,6,1,1],[10,12,1,1],[13,12,1,1],[12,13,1,1],[11,15,1,1]]],["#4a3d35",[[19,6,1,1],[9,7,1,1],[14,7,1,1]]],["#241a14",[[7,9,1,1],[16,9,1,1],[8,11,1,1],[16,11,1,1],[8,13,1,1],[16,13,1,1],[22,13,1,1],[1,15,1,1],[4,15,1,1],[8,15,1,1],[16,15,1,1],[1,17,1,1],[5,17,1,1],[8,17,1,1],[15,17,1,1],[20,17,1,1],[1,19,1,1],[5,19,1,1],[9,19,1,1],[15,19,1,1],[20,19,1,1],[1,21,1,1],[6,21,1,1],[9,21,1,1],[15,21,1,1],[19,21,1,1],[0,23,1,1],[6,23,1,1],[8,23,1,1],[16,23,1,1],[20,23,2,1]]],["#0284c7",[[10,13,1,1],[13,13,1,1],[11,14,2,1]]],["#ffffff",[[11,13,1,1]]],["#0f172a",[[10,14,1,1],[13,14,1,1]]],["#0891b2",[[11,16,2,1]]]];
const APOSTATE_SPANS_WALK4 = [["#c084fc",[[11,0,2,1]]],["#e879f9",[[7,1,1,1],[16,1,1,1]]],["#382e29",[[9,1,1,1],[15,1,1,1],[7,2,1,1],[17,2,1,1],[6,3,1,1],[10,3,1,1],[13,3,1,1],[17,3,1,1],[5,4,1,1],[9,4,1,1],[14,4,1,1],[18,4,1,1],[6,5,1,1],[8,5,1,1],[13,5,1,1],[15,5,1,1]]],["#504040",[[10,1,1,1],[14,1,1,1],[8,2,1,1],[16,2,1,1],[7,3,1,1],[16,3,1,1],[6,4,1,1],[8,4,1,1],[10,4,1,1],[13,4,1,1],[15,4,1,1],[17,4,1,1]]],["#6e594d",[[11,1,1,1],[13,1,1,1],[9,2,1,1],[11,2,1,1],[13,2,1,1],[15,2,1,1],[8,3,1,1],[14,3,1,1],[7,4,1,1],[11,4,2,1],[16,4,1,1],[10,7,1,1],[13,7,1,1]]],["#856a5d",[[12,1,1,1],[10,2,1,1],[12,2,1,1],[14,2,1,1],[9,3,1,1],[11,3,2,1],[15,3,1,1],[10,9,4,1]]],["#38bdf8",[[5,2,1,1]]],["#241e1a",[[6,2,1,1],[18,2,1,1],[4,4,1,1],[19,4,1,1],[5,5,1,1],[7,5,1,1],[14,5,1,1],[16,5,1,1]]],["#9a6e58",[[2,3,1,1],[21,4,1,1],[2,6,1,1],[4,6,1,1],[20,6,2,1],[17,7,1,1],[1,8,1,1],[4,8,1,1],[11,8,2,1],[21,8,2,1],[1,10,1,1],[3,10,1,1],[21,10,2,1],[3,12,1,1],[23,12,1,1]]],["#3b322a",[[3,3,1,1],[2,4,1,1],[21,5,1,1]]],["#1a1512",[[4,3,1,1],[19,3,1,1],[9,5,1,1],[12,5,1,1],[17,5,1,1],[9,11,1,1],[14,11,1,1]]],["#2e2520",[[5,3,1,1],[18,3,1,1],[15,7,1,1]]],["#5a4035",[[3,4,1,1],[3,6,1,1],[7,6,1,1],[19,6,1,1],[7,7,1,1],[11,7,2,1],[2,8,1,1],[5,8,2,1],[10,8,1,1],[13,8,1,1],[20,8,1,1],[7,9,1,1],[15,9,1,1],[17,9,1,1],[2,10,1,1],[4,10,1,1],[10,10,1,1],[13,10,1,1],[20,10,1,1],[1,11,1,1],[7,11,1,1],[17,11,1,1],[4,12,1,1],[22,12,1,1],[1,13,1,1],[8,13,1,1],[17,13,1,1],[21,13,1,1],[23,13,1,1],[3,14,1,1],[1,15,1,1],[8,15,1,1],[17,15,1,1],[20,15,1,1],[23,15,1,1],[1,17,1,1],[3,17,1,1],[8,17,1,1],[16,17,1,1],[20,17,1,1],[23,17,1,1],[4,19,1,1],[16,19,1,1],[22,19,1,1],[4,21,1,1],[16,21,1,1],[22,21,1,1]]],["#7a5848",[[20,4,1,1],[2,5,1,1],[4,5,1,1],[2,7,1,1],[4,7,3,1],[18,7,1,1],[20,7,1,1],[22,7,1,1],[7,8,1,1],[9,8,1,1],[14,8,4,1],[1,9,1,1],[3,9,1,1],[21,9,2,1],[7,10,1,1],[11,10,2,1],[17,10,1,1],[3,11,1,1],[21,11,1,1],[23,11,1,1],[1,12,1,1],[8,12,1,1],[17,12,1,1],[21,12,1,1],[1,14,1,1],[8,14,1,1],[17,14,1,1],[21,14,1,1],[23,14,1,1],[1,16,1,1],[3,16,1,1],[8,16,1,1],[16,16,1,1],[20,16,1,1],[23,16,1,1],[1,18,1,1],[3,18,1,1],[16,18,1,1],[22,18,1,1],[4,20,1,1],[16,20,1,1],[22,20,1,1],[5,22,1,1],[16,22,1,1],[22,22,1,1]]],["#3b2d26",[[3,5,1,1],[19,5,1,1],[5,6,1,1],[8,6,1,1],[3,7,1,1],[16,7,1,1],[19,7,1,1],[21,7,1,1],[8,8,1,1],[2,9,1,1],[4,9,1,1],[9,9,1,1],[14,9,1,1],[20,9,1,1],[8,10,2,1],[14,10,3,1],[4,11,1,1],[10,11,1,1],[13,11,1,1],[20,11,1,1],[22,11,1,1],[2,12,1,1],[9,12,1,1],[16,12,1,1],[20,12,1,1],[3,13,1,1],[2,14,1,1],[9,14,1,1],[16,14,1,1],[20,14,1,1],[22,14,1,1],[2,16,1,1],[4,16,1,1],[9,16,1,1],[15,16,1,1],[19,16,1,1],[22,16,1,1],[2,18,1,1],[4,18,1,1],[15,18,1,1],[21,18,1,1],[5,20,1,1],[15,20,1,1],[21,20,1,1],[6,22,1,1],[15,22,1,1],[21,22,1,1]]],["#06b6d4",[[10,5,2,1],[11,6,2,1],[16,6,1,1],[18,6,1,1],[11,11,2,1],[12,15,1,1]]],["#4a3d35",[[20,5,1,1],[9,7,1,1],[14,7,1,1]]],["#22d3ee",[[6,6,1,1],[9,6,1,1],[14,6,1,1],[17,6,1,1],[11,12,2,1]]],["#00f0ff",[[10,6,1,1],[13,6,1,1],[15,6,1,1],[10,12,1,1],[13,12,1,1],[12,13,1,1],[11,15,1,1]]],["#241a14",[[8,7,1,1],[8,9,1,1],[16,9,1,1],[2,11,1,1],[8,11,1,1],[16,11,1,1],[2,13,1,1],[9,13,1,1],[16,13,1,1],[20,13,1,1],[22,13,1,1],[2,15,2,1],[9,15,1,1],[16,15,1,1],[19,15,1,1],[22,15,1,1],[2,17,1,1],[4,17,1,1],[9,17,1,1],[15,17,1,1],[19,17,1,1],[22,17,1,1],[8,18,1,1],[20,18,1,1],[1,19,1,1],[5,19,1,1],[15,19,1,1],[21,19,1,1],[5,21,1,1],[15,21,1,1],[21,21,1,1],[5,23,1,1],[16,23,1,1],[22,23,1,1]]],["#0284c7",[[10,13,1,1],[13,13,1,1],[11,14,2,1]]],["#ffffff",[[11,13,1,1]]],["#0f172a",[[10,14,1,1],[13,14,1,1]]],["#0891b2",[[11,16,2,1]]]];
const DEFECTIVE_SPANS_DRIFT = [["#facc15",[[4,0,1,1],[7,0,1,1],[3,1,1,1],[9,2,1,1],[11,4,1,1],[11,13,1,1]]],["#ca8a04",[[4,1,1,1],[6,1,1,1],[8,3,1,1],[10,5,1,1],[8,8,1,1],[11,8,1,1],[7,9,1,1],[10,9,1,1],[8,10,2,1],[4,18,1,1]]],["#84cc16",[[7,1,1,1],[3,2,1,1],[6,2,1,1],[2,3,1,1],[9,3,1,1],[11,5,1,1],[9,6,1,1]]],["#a3e635",[[2,2,1,1],[10,6,1,1]]],["#65a30d",[[5,2,1,1],[4,3,1,1],[3,4,1,1],[8,4,1,1],[8,6,1,1]]],["#4d7c0f",[[5,3,1,1],[5,4,1,1],[4,5,1,1],[7,5,1,1],[7,6,1,1]]],["#365314",[[6,4,1,1],[6,5,1,1],[5,6,1,1],[8,16,1,1],[6,17,1,1],[3,19,1,1]]],["#c026d3",[[12,5,1,1],[14,5,1,1],[14,8,1,1]]],["#fae8ff",[[13,5,1,1]]],["#d946ef",[[11,6,1,1],[14,6,1,1]]],["#ffffff",[[12,6,2,1],[13,7,1,1]]],["#a21caf",[[11,7,1,1],[15,7,1,1],[13,8,1,1]]],["#f0abfc",[[12,7,1,1],[14,7,1,1],[16,16,1,1]]],["#eab308",[[9,8,1,1],[9,9,1,1]]],["#fde047",[[10,8,1,1],[8,9,1,1]]],["#701a75",[[12,8,1,1]]],["#3b82f6",[[15,8,1,1],[13,9,1,1],[16,9,1,1],[12,10,1,1],[12,11,1,1]]],["#60a5fa",[[16,8,1,1],[14,9,1,1],[13,10,2,1]]],["#2563eb",[[11,9,1,1],[11,10,1,1],[15,10,1,1],[11,11,1,1],[13,11,1,1]]],["#06b6d4",[[12,9,1,1]]],["#93c5fd",[[15,9,1,1]]],["#1d4ed8",[[10,10,1,1],[10,11,1,1],[14,11,1,1],[13,12,1,1],[11,14,2,1]]],["#1e40af",[[9,11,1,1],[13,13,1,1],[10,14,1,1],[11,15,1,1]]],["#1e3a8a",[[9,12,1,1],[9,13,1,1],[13,14,1,1],[10,15,1,1],[12,15,1,1]]],["#0f172a",[[10,12,3,1],[10,13,1,1],[12,13,1,1],[9,14,1,1]]],["#4338ca",[[14,14,1,1],[13,15,1,1]]],["#6366f1",[[15,14,1,1],[16,15,1,1],[14,16,1,1]]],["#991b1b",[[7,15,1,1],[5,16,1,1]]],["#7f1d1d",[[8,15,1,1],[6,16,1,1]]],["#818cf8",[[14,15,1,1],[17,16,1,1]]],["#a855f7",[[15,15,1,1]]],["#b45309",[[7,16,1,1]]],["#c084fc",[[15,16,1,1]]],["#450a0a",[[4,17,1,1],[3,18,1,1]]],["#78350f",[[5,17,1,1]]],["#7c3aed",[[15,17,1,1]]],["#e879f9",[[16,17,1,1]]]];
const DEFECTIVE_SPANS_ATTACK = [["#ffffff",[[4,0,1,1],[19,0,1,1],[3,1,1,1],[8,1,1,1],[15,1,1,1],[20,1,1,1],[11,2,2,1],[11,4,2,1],[7,5,1,1],[16,5,1,1],[11,6,2,1],[10,7,4,1],[4,8,1,1],[9,8,6,1],[19,8,1,1],[10,9,4,1],[11,10,2,1],[11,11,2,1],[11,18,2,1]]],["#facc15",[[5,0,1,1],[18,0,1,1],[4,2,1,1],[19,2,1,1],[7,3,1,1],[16,3,1,1],[11,14,2,1]]],["#fde047",[[4,1,1,1],[19,1,1,1],[7,2,1,1],[16,2,1,1]]],["#ca8a04",[[5,1,1,1],[18,1,1,1]]],["#a3e635",[[2,2,1,1],[21,2,1,1],[6,3,1,1],[17,3,1,1]]],["#84cc16",[[3,2,1,1],[8,2,1,1],[15,2,1,1],[20,2,1,1],[5,3,1,1],[18,3,1,1]]],["#65a30d",[[3,3,1,1],[20,3,1,1]]],["#fae8ff",[[11,3,2,1],[8,5,1,1],[11,5,2,1],[15,5,1,1],[5,8,1,1],[18,8,1,1]]],["#f0abfc",[[9,6,1,1],[14,6,1,1],[8,7,1,1],[15,7,1,1],[6,8,1,1],[17,8,1,1],[11,17,2,1]]],["#c026d3",[[10,6,1,1],[13,6,1,1],[10,10,1,1],[13,10,1,1],[10,16,1,1],[13,16,1,1]]],["#e879f9",[[9,7,1,1],[14,7,1,1],[7,8,1,1],[16,8,1,1],[9,9,1,1],[14,9,1,1],[11,16,2,1]]],["#d946ef",[[8,8,1,1],[15,8,1,1]]],["#a21caf",[[8,9,1,1],[15,9,1,1]]],["#701a75",[[9,10,1,1],[14,10,1,1],[11,15,2,1]]],["#1e40af",[[6,11,1,1],[17,11,1,1],[7,12,1,1],[16,12,1,1],[8,13,1,1],[15,13,1,1],[9,14,1,1],[14,14,1,1]]],["#06b6d4",[[7,11,1,1],[16,11,1,1]]],["#2563eb",[[8,11,1,1],[15,11,1,1],[8,12,1,1],[15,12,1,1]]],["#60a5fa",[[9,11,1,1],[14,11,1,1],[10,12,1,1],[13,12,1,1]]],["#93c5fd",[[10,11,1,1],[13,11,1,1],[11,12,2,1]]],["#1e3a8a",[[6,12,1,1],[17,12,1,1],[7,13,1,1],[16,13,1,1],[8,14,1,1],[15,14,1,1],[9,15,1,1],[14,15,1,1]]],["#3b82f6",[[9,12,1,1],[14,12,1,1]]],["#1d4ed8",[[9,13,1,1],[14,13,1,1]]],["#0f172a",[[10,13,4,1],[10,14,1,1],[13,14,1,1]]],["#4338ca",[[10,15,1,1],[13,15,1,1],[9,16,1,1],[14,16,1,1]]],["#991b1b",[[6,16,1,1],[17,16,1,1]]],["#7f1d1d",[[7,16,1,1],[16,16,1,1],[5,17,1,1],[18,17,1,1]]],["#b45309",[[6,17,1,1],[17,17,1,1]]],["#a855f7",[[10,17,1,1],[13,17,1,1]]],["#450a0a",[[5,18,1,1],[18,18,1,1]]]];

const _catwalkSpriteCache = new Map();
let _catwalkCacheIdCounter = 0;

function getCatwalkSpanId(spans) {
  if (!spans._cacheId) {
    spans._cacheId = ++_catwalkCacheIdCounter;
  }
  return spans._cacheId;
}

function getBakedCatwalkSprite(spans, facing) {
  if (typeof document === 'undefined' || !document.createElement) return null;
  const key = getCatwalkSpanId(spans) + '_' + (facing === -1 ? '-1' : '1');
  let cached = _catwalkSpriteCache.get(key);
  if (cached) return cached;

  const cvs = document.createElement('canvas');
  cvs.width = 26;
  cvs.height = 26;
  const cctx = cvs.getContext('2d');
  for (let i = 0; i < spans.length; i++) {
    const [col, rects] = spans[i];
    cctx.fillStyle = col;
    for (let j = 0; j < rects.length; j++) {
      const [gx, gy, gw, gh] = rects[j];
      const effGX = facing === -1 ? (24 - gx - gw) : gx;
      cctx.fillRect(effGX, gy, gw, gh);
    }
  }
  _catwalkSpriteCache.set(key, cvs);
  return cvs;
}

function drawCatwalkSprite(ctx, baseX, footY, spans, facing = 1, scale = 1.0) {
  const drawStartY = Math.round(footY - 23 * scale);
  const drawStartX = Math.round(baseX - 12 * scale);
  if (scale === 1.0) {
    const baked = getBakedCatwalkSprite(spans, facing);
    if (baked) {
      ctx.drawImage(baked, drawStartX, drawStartY);
      return;
    }
  }
  for (let i = 0; i < spans.length; i++) {
    const [col, rects] = spans[i];
    ctx.fillStyle = col;
    for (let j = 0; j < rects.length; j++) {
      const [gx, gy, gw, gh] = rects[j];
      const effGX = facing === -1 ? (24 - gx - gw) : gx;
      ctx.fillRect(
        drawStartX + effGX * scale,
        drawStartY + gy * scale,
        gw * scale,
        gh * scale
      );
    }
  }
}

function drawCatwalkTentacle(ctx, x0, y0, x1, y1, tension = 0, t = 0) {
  const dist = Math.hypot(x1 - x0, y1 - y0);
  if (dist < 2) return;
  const steps = Math.max(12, Math.round(dist / 3));
  const dx = (x1 - x0) / steps, dy = (y1 - y0) / steps;
  const perpX = -(y1 - y0) / dist, perpY = (x1 - x0) / dist;
  const amp = Math.max(1.2, (4.5 - tension * 3.0));
  const freq = 12 + tension * 20;

  ctx.strokeStyle = '#581c87'; ctx.lineWidth = 3.2; ctx.beginPath(); ctx.moveTo(x0, y0);
  for (let s = 1; s <= steps; s++) {
    const u = s / steps;
    const wave = Math.sin(u * freq - t * 24) * amp * Math.sin(u * Math.PI);
    ctx.lineTo(x0 + dx * s + perpX * wave, y0 + dy * s + perpY * wave);
  }
  ctx.stroke();

  ctx.strokeStyle = '#e879f9'; ctx.lineWidth = 1.8; ctx.beginPath(); ctx.moveTo(x0, y0);
  for (let s = 1; s <= steps; s++) {
    const u = s / steps;
    const wave = Math.sin(u * freq - t * 24) * amp * Math.sin(u * Math.PI);
    ctx.lineTo(x0 + dx * s + perpX * wave, y0 + dy * s + perpY * wave);
  }
  ctx.stroke();

  ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 0.8; ctx.beginPath(); ctx.moveTo(x0, y0);
  for (let s = 1; s <= steps; s++) {
    const u = s / steps;
    const wave = Math.sin(u * freq - t * 24) * amp * Math.sin(u * Math.PI);
    ctx.lineTo(x0 + dx * s + perpX * wave, y0 + dy * s + perpY * wave);
  }
  ctx.stroke();
}

function getUpperGirderY(x) {
  const trolleyBump = (x >= 350 && x <= 420) ? Math.sin((x - 350) / 70 * Math.PI) * 11 : 0;
  return 82 - trolleyBump;
}



const _bayGlowCanvasCache = new Map();

function getBayGlowCanvas(rgbKey) {
  let canvas = _bayGlowCanvasCache.get(rgbKey);
  if (!canvas) {
    if (typeof document === 'undefined') return null;
    canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const gctx = canvas.getContext('2d');
    const g = gctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, `rgb(${rgbKey})`);
    g.addColorStop(1, `rgba(${rgbKey}, 0)`);
    gctx.fillStyle = g;
    gctx.fillRect(0, 0, 64, 64);
    _bayGlowCanvasCache.set(rgbKey, canvas);
  }
  return canvas;
}

let _cachedFogPuffCanvas = null;
function getFogPuffCanvas() {
  if (_cachedFogPuffCanvas) return _cachedFogPuffCanvas;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, 'rgba(224, 242, 254, 1)');
  g.addColorStop(0.5, 'rgba(186, 230, 253, 0.4)');
  g.addColorStop(1, 'rgba(186, 230, 253, 0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(64, 64, 64, 0, Math.PI * 2);
  ctx.fill();
  _cachedFogPuffCanvas = canvas;
  return _cachedFogPuffCanvas;
}

const _bokehSphereLutMap = new Map();
function getBokehSphereLUT(tier, baseRgb, rimRgb) {
  if (typeof document === 'undefined') return null;
  const key = `${tier}_${baseRgb}_${rimRgb}`;
  let canvas = _bokehSphereLutMap.get(key);
  if (canvas) return canvas;

  canvas = document.createElement('canvas');
  const size = 160;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const cx = size * 0.5;
  const cy = size * 0.5;
  const r = 40;
  const haloR = r * (tier === 'macro' ? 1.55 : (tier === 'mid' ? 1.35 : 1.2));

  // 1. Halo
  const haloGrad = ctx.createRadialGradient(cx, cy, r * 0.2, cx, cy, haloR);
  haloGrad.addColorStop(0, `rgba(${baseRgb}, 0.38)`);
  haloGrad.addColorStop(0.55, `rgba(${rimRgb}, 0.16)`);
  haloGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = haloGrad;
  ctx.beginPath();
  ctx.arc(cx, cy, haloR, 0, Math.PI * 2);
  ctx.fill();

  // 2. Main
  const mainGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  mainGrad.addColorStop(0.0, 'rgba(255, 255, 255, 0.65)');
  mainGrad.addColorStop(0.25, `rgba(${baseRgb}, 0.52)`);
  mainGrad.addColorStop(0.60, `rgba(${baseRgb}, 0.32)`);
  mainGrad.addColorStop(0.84, `rgba(${rimRgb}, 0.68)`);
  mainGrad.addColorStop(0.95, `rgba(${rimRgb}, 0.35)`);
  mainGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = mainGrad;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  // 3. Core
  const coreR = r * (tier === 'macro' ? 0.36 : (tier === 'mid' ? 0.44 : 0.65));
  const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
  coreGrad.addColorStop(0.0, 'rgba(255, 255, 255, 0.85)');
  coreGrad.addColorStop(0.55, `rgba(${baseRgb}, 0.45)`);
  coreGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = coreGrad;
  ctx.beginPath();
  ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
  ctx.fill();

  // 4. Chromatic Aberration
  if (tier !== 'sparkle') {
    const caDist = tier === 'macro' ? 2.4 : 1.2;
    const caX = cx + caDist;
    const caY = cy;
    const caGrad = ctx.createRadialGradient(caX, caY, r * 0.5, caX, caY, r * 1.06);
    caGrad.addColorStop(0.0, 'rgba(0, 0, 0, 0)');
    caGrad.addColorStop(0.72, 'rgba(0, 0, 0, 0)');
    caGrad.addColorStop(0.90, `rgba(${rimRgb}, 0.45)`);
    caGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = caGrad;
    ctx.beginPath();
    ctx.arc(caX, caY, r * 1.06, 0, Math.PI * 2);
    ctx.fill();
  }

  _bokehSphereLutMap.set(key, canvas);
  return canvas;
}

let _cachedTealGrad = null;
let _cachedWarmGrad = null;
let _cachedCockGlow = null;
let _cachedPilotLamp = null;
let _cachedMechLamp = null;
let _cachedMoonShaft = null;
let _cachedHoloConeGrad = null;
let _cachedSpotPoolGrad = null;
let _cachedRoverBodyGrad = null;

function getGlowDetails(color) {
  if (typeof color !== 'string') return null;
  if (color.charCodeAt(0) === 35) { // '#'
    let r = 0, g = 0, b = 0;
    if (color.length === 7) {
      r = parseInt(color.slice(1, 3), 16);
      g = parseInt(color.slice(3, 5), 16);
      b = parseInt(color.slice(5, 7), 16);
    }
    return { rgbKey: `${r},${g},${b}`, alpha: 1 };
  }
  const start = color.indexOf('(');
  const end = color.lastIndexOf(')');
  if (start !== -1 && end !== -1) {
    const parts = color.slice(start + 1, end).split(',');
    if (parts.length >= 3) {
      const rgbKey = `${parts[0].trim()},${parts[1].trim()},${parts[2].trim()}`;
      const alpha = parts.length > 3 ? parseFloat(parts[3]) : 1;
      return { rgbKey, alpha: isNaN(alpha) ? 1 : alpha };
    }
  }
  return null;
}

function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
  return (
    (window.matchMedia && (window.matchMedia('(max-width: 900px)').matches || window.matchMedia('(pointer: coarse)').matches)) ||
    /Mobi|Android|iPhone|iPad|iPod/i.test(ua)
  );
}

export class BayBackground {
  constructor(options = {}) {
    const isMobile = options.isMobile !== undefined ? options.isMobile : isMobileDevice();
    const cheapPath = options.cheapPath !== undefined ? options.cheapPath : (options.quality === 'low');

    this.options = Object.assign({
      assetPath: 'assets/the-bay/',
      enableParallax: true,
      nightShift: false,
      redAlert: false,
      flirEnabled: !cheapPath,
      strobeEnabled: !cheapPath,
      animationsEnabled: !cheapPath,
      craneEnabled: !cheapPath,
      godRaysEnabled: !cheapPath,
      bokehEnabled: !cheapPath && !isMobile,
      cinemaGradingEnabled: true,
      holoEnabled: !cheapPath,
      fogEnabled: !cheapPath && !isMobile,
      floorReflectEnabled: false, // user requested no wet floor
      rimLightEnabled: !cheapPath,
      thermalHazeEnabled: !cheapPath && !isMobile,
      bloomEnabled: false,
      breathingEnabled: !cheapPath,
      isMobile,
      cheapPath,
    }, options);

    this.container = null;
    this.viewportEl = null;
    this.bgCanvas = null;
    this.bgCtx = null;
    this.fgCanvas = null;
    this.canvas = null; // compatibility alias
    this.ctx = null;

    this.planes = [];
    this.isRunning = false;
    this.rafId = null;
    this.lastTimestamp = 0;
    // Target interval of the live-FX loop; 60 fps by default (identical to the
    // previously hard-coded 13.6 ms threshold / 1/60 drift correction).
    this._frameDt = 1 / 60;

    // Simulation state object matching sandbox
    this.fx = {
      bgCtx: null,
      ctx: null,
      time: 0,
      pointerX: 0,
      pointerY: 0,
      targetPointerX: 0,
      targetPointerY: 0,
      scrollNorm: 0,
      targetScrollNorm: 0,
      flirAngle: 0,
      sparks: [],
      steamPuffs: [],
      thermosSteam: [],
      cryoSteamPuffs: [],
      cryoTimer: 18.0,
      dustMotes: [],
      groundFogPuffs: [],
      drone: {
        x: 620, y: 130, targetX: 620, targetY: 130,
        mode: 'patrol', timer: 3.0, weldTimer: 0,
        tilt: 0, armExtension: 0, thrusterTilt: 0, strobeTimer: 0,
        exhaustParticles: [],
      },
      crane: { x: -120, speed: 16, dir: 1, cargoAngle: 0 },
      rover: { x: BASE_WIDTH + 80, speed: -22, wheelAngle: 0, beaconAngle: 0, strobeTimer: 0 },
      spotlight: { angle: 0 },
      tech: { x: 345, y: 318, facing: -1, state: 'patrol_left', timer: 3.5, walkPhase: 0, lightAngle: -0.05 },
      marines: { active: true, x: 100, speed: 28, walkPhase: 0, cooldownTimer: 0, squadSize: 4 },
      sensor: { pulse: 0.8, scanActiveTimer: 0, scanSweep: 0 },
      bay05Crane: { x: 845, dir: 1, speed: 6.5, cableAngle: 0 },
      piranhaAvionics: { elevonAngle: 0, gunPitch: 0, testCycleTimer: 0, autonomousAngle: 0 },
      sentrySalute: { armRaised: 0 },
      teslaWalker: { x: 720, baseY: 530, facing: -1, headAngle: -0.35, headTilt: 0, state: 'look_left', timer: 3.5, walkPhase: 0, sparkTimer: 0 },
      hyperTesla: { x: 885, baseY: 530, facing: -1, stingerAngle: 0, clawFlex: 0, sparkTimer: 0 },
      devilPatrol: {
        x: 460, baseY: 524, facing: 1, state: 'idle_fidget', timer: 3.0,
        walkPhase: 0, walkWeight: 0, headTilt: 0, headAngle: 0, gunAngle: -0.05, stumbleTilt: 0,
        hopY: 0, stompY: 0, stompCount: 0, fireX: 0, fireActive: false, fireIntensity: 0,
        panicTimer: 0, muzzleFlame: 0, flamePuffs: [], embers: [], scorches: [], smokeWisps: []
      },
      catwalkEncounter: {
        state: 'cooldown',
        timer: 18.0,
        apostate: {
          x: 1060, baseY: 82, facing: -1, walkPhase: 0, speed: 22,
          recoilX: 0, jitterX: 0, lookAngle: 0, eyePulse: 1.0,
          pauseTimer: 0, struggleShift: 0, jumpTimer: 0, _lowerPauseDone: false
        },
        defective: {
          active: false, x: -40, baseY: 82, facing: 1, walkPhase: 0, speed: 32,
          tentacleReach: 0, anchorTension: 0
        },
        spores: [],
        clawSparks: [],
        tetherPulses: []
      },

      wireframeYaw: 0,
      rotorAngle: 0,
      parallaxLayers: null,
      bokehDiscs: [],
      godRays: [],
    };

    this.initParticles();
    this.bindWindowEvents();
  }

  initParticles() {
    const fx = this.fx;

    // 45 Staubpartikel
    for (let i = 0; i < 45; i++) {
      fx.dustMotes.push({
        x: 80 + Math.random() * 864,
        y: 30 + Math.random() * 500,
        vx: (Math.random() - 0.5) * 0.28,
        vy: 0.12 + Math.random() * 0.38,
        size: Math.random() > 0.55 ? 2 : 1.2,
        alpha: 0.2 + Math.random() * 0.55,
      });
    }

    // 24 Bodennebel-Partikel
    for (let i = 0; i < 24; i++) {
      fx.groundFogPuffs.push({
        x: Math.random() * BASE_WIDTH,
        y: 450 + Math.random() * 100,
        vx: 0.25 + Math.random() * 0.45,
        radius: 50 + Math.random() * 45,
        alpha: 0.08 + Math.random() * 0.14,
        seed: Math.random() * 100,
      });
    }

    // 3-Tier Kinoreifes Vordergrund-Bokeh
    const BOKEH_TYPES = ['amber', 'cyan', 'warm_white'];
    for (let i = 0; i < 4; i++) {
      fx.bokehDiscs.push({
        x: Math.random() * BASE_WIDTH,
        y: Math.random() * BASE_HEIGHT,
        radius: 38 + Math.random() * 26,
        vx: (Math.random() - 0.5) * 1.8,
        vy: -(0.7 + Math.random() * 1.2),
        type: BOKEH_TYPES[i % BOKEH_TYPES.length],
        phase: Math.random() * Math.PI * 2,
        baseAlpha: 0.07 + Math.random() * 0.07,
        tier: 'macro',
        sides: 6,
        aspect: 1.16,
      });
    }
    for (let i = 0; i < 16; i++) {
      fx.bokehDiscs.push({
        x: Math.random() * BASE_WIDTH,
        y: Math.random() * BASE_HEIGHT,
        radius: 11 + Math.random() * 16,
        vx: (Math.random() - 0.48) * 3.8,
        vy: -(2.0 + Math.random() * 3.2),
        type: BOKEH_TYPES[(i + 1) % BOKEH_TYPES.length],
        phase: Math.random() * Math.PI * 2,
        baseAlpha: 0.12 + Math.random() * 0.16,
        tier: 'mid',
        sides: 6,
        aspect: 1.12,
      });
    }
    for (let i = 0; i < 10; i++) {
      fx.bokehDiscs.push({
        x: Math.random() * BASE_WIDTH,
        y: Math.random() * BASE_HEIGHT,
        radius: 4 + Math.random() * 5,
        vx: (Math.random() - 0.5) * 5.0,
        vy: -(3.0 + Math.random() * 4.0),
        type: BOKEH_TYPES[(i + 2) % BOKEH_TYPES.length],
        phase: Math.random() * Math.PI * 2,
        baseAlpha: 0.22 + Math.random() * 0.25,
        tier: 'sparkle',
        sides: 4,
        aspect: 1.0,
      });
    }

    // 4 Volumetrische God-Ray-Lichtkegel
    fx.godRays = [
      { topX: 130, topW: 34, botW: 95, slant: 165, phase: 0.0, speed: 0.8 },
      { topX: 320, topW: 48, botW: 130, slant: 180, phase: 1.8, speed: 0.65 },
      { topX: 560, topW: 42, botW: 120, slant: 190, phase: 3.4, speed: 0.9 },
      { topX: 790, topW: 55, botW: 155, slant: 205, phase: 5.1, speed: 0.75 },
    ];
  }

  bindWindowEvents() {
    if (typeof window === 'undefined') return;
    this._onPointerMove = (e) => {
      const w = window.innerWidth || 1920;
      const h = window.innerHeight || 1080;
      this.fx.targetPointerX = (e.clientX / w) - 0.5;
      this.fx.targetPointerY = (e.clientY / h) - 0.5;
    };
    window.addEventListener?.('pointermove', this._onPointerMove, { passive: true });

    // Gyroscope / DeviceOrientation on Mobile
    this._onDeviceOrientation = (e) => {
      if (e && e.gamma !== null && e.beta !== null) {
        const tiltX = Math.max(-1, Math.min(1, e.gamma / 25));
        const tiltY = Math.max(-1, Math.min(1, (e.beta - 45) / 25));
        this.fx.targetPointerX = tiltX * 0.5;
        this.fx.targetPointerY = tiltY * 0.5;
      }
    };
    window.addEventListener?.('deviceorientation', this._onDeviceOrientation, { passive: true });

    this._onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.hidden) {
        if (this.isRunning) this._wasRunningBeforeHide = true;
        this.pause();
      } else if (this._wasRunningBeforeHide) {
        this._wasRunningBeforeHide = false;
        this.resume();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener?.('visibilitychange', this._onVisibilityChange);
    }
  }

  mount(container) {
    if (!container || typeof container.prepend !== 'function') return;
    if (typeof document === 'undefined' || typeof document.createElement !== 'function') return;
    this.container = container;

    // Viewport Container
    const vp = document.createElement('div');
    vp.className = 'bay-deck-viewport';
    this.viewportEl = vp;

    // 1. BG Layer (Tiefe: 5)
    const bgImg = document.createElement('img');
    bgImg.className = 'bay-plane bay-plane-bg';
    bgImg.dataset.parallax = 5;
    bgImg.alt = '';
    bgImg.draggable = false;
    bgImg.src = `${this.options.assetPath}bay_layer_bg.webp`;
    bgImg.onerror = () => {
      bgImg.onerror = null;
      bgImg.src = `${this.options.assetPath}bay_layer_bg.png`;
    };
    vp.appendChild(bgImg);
    this.planes.push({ el: bgImg, depth: 5 });

    const isHalfBuffer = this.options.isMobile || this.options.cheapPath;
    const canvasW = isHalfBuffer ? BASE_WIDTH : CANVAS_WIDTH;
    const canvasH = isHalfBuffer ? BASE_HEIGHT : CANVAS_HEIGHT;

    // 2. Background Dynamic FX Canvas (Tiefe: 8)
    const cBg = document.createElement('canvas');
    cBg.className = 'bay-plane bay-plane-fx-bg';
    cBg.dataset.parallax = 8;
    cBg.width = canvasW;
    cBg.height = canvasH;
    vp.appendChild(cBg);
    this.bgCanvas = cBg;
    this.bgCtx = cBg.getContext ? cBg.getContext('2d') : null;
    this.planes.push({ el: cBg, depth: 8 });

    // 3. Lights & Atmosphere Layer (Tiefe: 9)
    const lightsImg = document.createElement('img');
    lightsImg.className = 'bay-plane bay-plane-lights';
    lightsImg.dataset.parallax = 9;
    lightsImg.alt = '';
    lightsImg.draggable = false;
    lightsImg.src = `${this.options.assetPath}bay_layer_lights.webp`;
    lightsImg.onerror = () => {
      lightsImg.onerror = null;
      lightsImg.src = `${this.options.assetPath}bay_layer_lights.png`;
    };
    vp.appendChild(lightsImg);
    this.planes.push({ el: lightsImg, depth: 9 });

    // 4. Monitors & Catwalk Layer (Tiefe: 13)
    const monitorsImg = document.createElement('img');
    monitorsImg.className = 'bay-plane bay-plane-monitors';
    monitorsImg.dataset.parallax = 13;
    monitorsImg.alt = '';
    monitorsImg.draggable = false;
    monitorsImg.src = `${this.options.assetPath}bay_layer_monitors.webp`;
    monitorsImg.onerror = () => {
      monitorsImg.onerror = null;
      monitorsImg.src = `${this.options.assetPath}bay_layer_monitors.png`;
    };
    vp.appendChild(monitorsImg);
    this.planes.push({ el: monitorsImg, depth: 13 });

    // 5. Hero Layer (A9 Piranha Dock) (Tiefe: 18)
    const heroImg = document.createElement('img');
    heroImg.className = 'bay-plane bay-plane-hero';
    heroImg.dataset.parallax = 18;
    heroImg.alt = '';
    heroImg.draggable = false;
    heroImg.src = `${this.options.assetPath}bay_layer_hero.webp`;
    heroImg.onerror = () => {
      heroImg.onerror = null;
      heroImg.src = `${this.options.assetPath}bay_layer_hero.png`;
    };
    vp.appendChild(heroImg);
    this.planes.push({ el: heroImg, depth: 18 });

    // 6. Foreground Dynamic FX Canvas (Tiefe: 18)
    const cFg = document.createElement('canvas');
    cFg.className = 'bay-plane bay-plane-fx-fg';
    cFg.dataset.parallax = 18;
    cFg.width = canvasW;
    cFg.height = canvasH;
    vp.appendChild(cFg);
    this.fgCanvas = cFg;
    this.canvas = cFg; // legacy alias
    this.ctx = cFg.getContext ? cFg.getContext('2d') : null;
    this.planes.push({ el: cFg, depth: 18 });

    // 7. Foreground Frame Layer (Tiefe: 26)
    const fgImg = document.createElement('img');
    fgImg.className = 'bay-plane bay-plane-fg';
    fgImg.dataset.parallax = 26;
    fgImg.alt = '';
    fgImg.draggable = false;
    fgImg.src = `${this.options.assetPath}bay_layer_fg.webp`;
    fgImg.onerror = () => {
      fgImg.onerror = null;
      fgImg.src = `${this.options.assetPath}bay_layer_fg.png`;
    };
    vp.appendChild(fgImg);
    this.planes.push({ el: fgImg, depth: 26 });

    this.container.prepend(vp);

    // Link contexts into fx state
    this.fx.bgCtx = this.bgCtx;
    this.fx.ctx = this.ctx;
    this.fx.parallaxLayers = this.planes;
  }

  resume() {
    if (this.isRunning) return;
    if (typeof window !== 'undefined' && window.tcgGame?.isOnline) {
      // Safety guard: If an online match is active, do not run the living Hangar loop
      return;
    }
    this.isRunning = true;
    this.lastTimestamp = performance.now();
    this._lastRenderTime = performance.now();

    // Cheap path on mobile / low tier: do an initial draw once, then skip the continuous 60/120fps canvas loop
    if (this.options.cheapPath) {
      this.update(0.016);
      this.render(0.016);
      this.loop = (now) => {
        if (!this.isRunning) return;
        this.rafId = requestAnimationFrame(this.loop);
        if (typeof document !== 'undefined' && document.hidden) return;

        // On cheap path, only update parallax transforms when pointer or scroll is actively moving
        const fx = this.fx;
        const pointerMoving = Math.abs(fx.targetPointerX - fx.pointerX) > 0.001 || Math.abs(fx.targetPointerY - fx.pointerY) > 0.001;
        const scrollMoving = Math.abs(fx.targetScrollNorm - fx.scrollNorm) > 0.001;
        if (pointerMoving || scrollMoving) {
          const dt = Math.min((now - this.lastTimestamp) / 1000, 0.1);
          this.lastTimestamp = now;
          this.update(dt);
        }
      };
      this.rafId = requestAnimationFrame(this.loop);
      return;
    }

    this.loop = (now) => {
      if (!this.isRunning) return;
      this.rafId = requestAnimationFrame(this.loop);
      if (typeof document !== 'undefined' && document.hidden) return;
      // Cap at ~60 FPS on ProMotion / 120Hz displays (13.6ms jitter threshold)
      const renderDt = (now - this._lastRenderTime) / 1000;
      // 0.0136 s is 0.816 x the 60 fps interval; the same ratio applies to any cap.
      if (renderDt >= this._frameDt * 0.816) {
        if (renderDt > 0.1) {
          this._lastRenderTime = now;
        } else {
          this._lastRenderTime = now - ((renderDt % this._frameDt) * 1000);
        }
        const dt = Math.min((now - this.lastTimestamp) / 1000, 0.1);
        this.lastTimestamp = now;

        this.update(dt);
        this.render(dt);
      }
    };
    this.rafId = requestAnimationFrame(this.loop);
  }

  pause() {
    this.isRunning = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  // Caps the live-FX loop (canvas redraw + parallax easing). Clamped to 10..60 fps;
  // 60 restores the default. Only the loop cadence changes, not what is drawn.
  setFrameRate(fps) {
    const f = Math.min(60, Math.max(10, Number(fps) || 60));
    this._frameDt = 1 / f;
  }

  // Switches the hangar between the full living render and the "cheap path" (drawn once, then
  // only the parallax follows pointer/scroll) at runtime. The full-render option values are
  // remembered from the constructor and restored on the way back. Returns whether anything
  // changed. Used by the Archive on phones, see docs/ARCHIVE_MODE_TOGGLE_PERFORMANCE_PLAN_2026-09-20.md.
  setCheapPath(on) {
    const want = !!on;
    const o = this.options;
    if (!!o.cheapPath === want) return false;
    const keys = ['flirEnabled', 'strobeEnabled', 'animationsEnabled', 'craneEnabled', 'godRaysEnabled', 'holoEnabled', 'rimLightEnabled', 'breathingEnabled', 'bokehEnabled', 'fogEnabled', 'thermalHazeEnabled'];
    if (!this._fullFx) {
      if (o.cheapPath) return false; // built cheap: there is no full-render state to go back to
      this._fullFx = {};
      for (const k of keys) this._fullFx[k] = o[k];
    }
    for (const k of keys) o[k] = want ? false : this._fullFx[k];
    o.cheapPath = want;
    const wasRunning = this.isRunning;
    this.pause();
    if (wasRunning) this.resume();
    return true;
  }

  setNightShift(enabled) {
    this.options.nightShift = !!enabled;
  }

  setRedAlert(enabled) {
    this.options.redAlert = !!enabled;
  }

  // Mobile Swipe Parallax: smoothly pans the multiplane background as the player
  // swipes through the deck selection grid (progress: 0.0 at deck 0 to 1.0 at last deck).
  setScrollProgress(progress) {
    if (typeof progress !== 'number' || isNaN(progress)) return;
    const clamped = Math.max(0, Math.min(1, progress));
    this.fx.targetScrollNorm = clamped - 0.5;
  }

  update(dt) {
    const fx = this.fx;
    const state = this.options;
    let breathX = 0, breathY = 0;
    if (state.breathingEnabled) {
      breathX = Math.sin(fx.time * 0.35) * 0.010;
      breathY = Math.cos(fx.time * 0.22) * 0.007;
    }
    const combinedTargetX = fx.targetPointerX + breathX;
    const combinedTargetY = fx.targetPointerY + breathY;

    fx.pointerX += (combinedTargetX - fx.pointerX) * 0.12;
    fx.pointerY += (combinedTargetY - fx.pointerY) * 0.12;
    fx.scrollNorm += (fx.targetScrollNorm - fx.scrollNorm) * 0.15;

    if (this.options.enableParallax && this.planes && this.planes.length > 0) {
      for (let i = 0; i < this.planes.length; i++) {
        const item = this.planes[i];
        const parallaxX = -fx.pointerX * item.depth;
        // Multiplane mobile scroll pan: deeper layers travel less, foreground layers travel more
        const scrollPanX = -fx.scrollNorm * (80 + item.depth * 4.5);
        const tx = (parallaxX + scrollPanX).toFixed(2);
        const ty = (-fx.pointerY * item.depth * 0.65).toFixed(2);
        const nextTransform = `translate3d(${tx}px, ${ty}px, 0)`;
        if (item._lastTransform !== nextTransform) {
          item._lastTransform = nextTransform;
          item.el.style.transform = nextTransform;
        }
      }
    }
  }

  render(dt) {
    if (!this.ctx) return;
    this.fx.bgCtx = this.bgCtx;
    this.fx.ctx = this.ctx;
    this._runLiveFX(dt);
  }

  destroy() {
    this.pause();
    if (typeof window !== 'undefined') {
      window.removeEventListener?.('pointermove', this._onPointerMove);
      window.removeEventListener?.('deviceorientation', this._onDeviceOrientation);
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener?.('visibilitychange', this._onVisibilityChange);
    }
    if (this.viewportEl && this.viewportEl.parentNode) {
      this.viewportEl.parentNode.removeChild(this.viewportEl);
    }
    // Explicitly zero canvas backing stores so iOS WebKit releases GPU buffers immediately
    if (this.canvas) {
      this.canvas.width = 0;
      this.canvas.height = 0;
    }
    if (this.fgCanvas) {
      this.fgCanvas.width = 0;
      this.fgCanvas.height = 0;
    }
    if (this.bgCanvas) {
      this.bgCanvas.width = 0;
      this.bgCanvas.height = 0;
    }
    if (Array.isArray(this.planes)) {
      for (const p of this.planes) {
        if (p?.el) {
          if (p.el.tagName === 'IMG') {
            p.el.src = '';
          } else if (p.el.tagName === 'CANVAS') {
            p.el.width = 0;
            p.el.height = 0;
          }
        }
      }
    }
    this.planes = [];
    this.canvas = null;
    this.fgCanvas = null;
    this.bgCanvas = null;
    this.ctx = null;
    this.bgCtx = null;
    this.viewportEl = null;
    this.container = null;
  }

  // Bind the complete 30-effect engine from sandbox
  _runLiveFX(dt) {
    const fx = this.fx;
    const state = this.options;
    const els = {};
    const isHalfBuffer = state.isMobile || state.cheapPath;
    const renderScale = isHalfBuffer ? 1 : SCALE;
    const WIDTH = isHalfBuffer ? BASE_WIDTH : CANVAS_WIDTH;
    const HEIGHT = isHalfBuffer ? BASE_HEIGHT : CANVAS_HEIGHT;

    // Helper functions and routines from bay.js
  function spawnSparks(originX, originY, count = (state.isMobile ? 22 : 95)) {
    for (let i = 0; i < count; i++) {
      const ang = Math.PI * 0.12 + Math.random() * Math.PI * 0.76;
      const spd = 1.8 + Math.random() * 7.2;
      fx.sparks.push({
        x: originX,
        y: originY,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd,
        life: 1.0,
        decay: 0.32 + Math.random() * 0.42,
        size: 1.5 + Math.random() * 2,
      });
    }
  }

  function spawnSteamBurst() {
    const count = state.isMobile ? 10 : 38;
    const originX = 84, originY = 172;
    for (let i = 0; i < count; i++) {
      fx.steamPuffs.push({
        x: originX + (Math.random() - 0.5) * 8,
        y: originY + (Math.random() - 0.5) * 8,
        vx: 1.2 + Math.random() * 3.2,
        vy: -1.2 - Math.random() * 2.4,
        radius: 6 + Math.random() * 6,
        maxRadius: 38 + Math.random() * 32,
        alpha: 0.88,
        decay: 0.28 + Math.random() * 0.24,
      });
    }
  }

  function triggerSensorScan() {
    fx.sensor.scanActiveTimer = 3.6;
    fx.sensor.scanSweep = 0;
  }

  function p(ctx, x, y, col) {
    ctx.fillStyle = col;
    ctx.fillRect(Math.floor(x), Math.floor(y), 1, 1);
  }

  function rect(ctx, x, y, w, h, col, alpha = 1) {
    ctx.save();
    ctx.globalAlpha *= alpha;
    ctx.fillStyle = col;
    ctx.fillRect(Math.floor(x), Math.floor(y), Math.ceil(w), Math.ceil(h));
    ctx.restore();
  }

  function line(ctx, x1, y1, x2, y2, col, width = 1, alpha = 1) {
    ctx.save();
    ctx.globalAlpha *= alpha;
    ctx.strokeStyle = col;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.restore();
  }

  function drawBeveledBox(ctx, x, y, w, h, baseCol, lightCol, darkCol, bevel = 1) {
    rect(ctx, x, y, w, h, baseCol);
    line(ctx, x, y, x + w, y, lightCol, bevel);
    line(ctx, x, y, x, y + h, lightCol, bevel);
    line(ctx, x, y + h, x + w, y + h, darkCol, bevel);
    line(ctx, x + w, y, x + w, y + h, darkCol, bevel);
  }

  function hazardStripes(ctx, x, y, w, h, step = 8) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    rect(ctx, x, y, w, h, '#141203');
    ctx.strokeStyle = '#eab308';
    ctx.lineWidth = step / 2;
    for (let lx = x - h * 2; lx < x + w + h * 2; lx += step) {
      ctx.beginPath();
      ctx.moveTo(lx, y + h);
      ctx.lineTo(lx + h, y);
      ctx.stroke();
    }
    rect(ctx, x, y, w, 1, 'rgba(255, 255, 255, 0.35)');
    rect(ctx, x, y + h - 1, w, 1, 'rgba(0, 0, 0, 0.6)');
    ctx.restore();
  }

  function drawCylinder(ctx, x, y, w, h, isVertical = false, isChrome = false) {
    const g = isVertical
      ? ctx.createLinearGradient(x, 0, x + w, 0)
      : ctx.createLinearGradient(0, y, 0, y + h);

    if (isChrome) {
      g.addColorStop(0, '#334155');
      g.addColorStop(0.2, '#94a3b8');
      g.addColorStop(0.45, '#ffffff');
      g.addColorStop(0.7, '#94a3b8');
      g.addColorStop(1, '#334155');
    } else {
      g.addColorStop(0, '#121822');
      g.addColorStop(0.25, '#4b5e78');
      g.addColorStop(0.5, '#94a7c1');
      g.addColorStop(0.8, '#243042');
      g.addColorStop(1, '#121822');
    }
    ctx.fillStyle = g;
    ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
  }

  function glow(ctx, x, y, radius, color, maxAlpha = 1) {
    if (radius <= 0) return;
    const details = getGlowDetails(color);
    if (details) {
      const glowCanvas = getBayGlowCanvas(details.rgbKey);
      if (glowCanvas) {
        ctx.save();
        ctx.globalAlpha *= (maxAlpha * details.alpha);
        ctx.drawImage(glowCanvas, x - radius, y - radius, radius * 2, radius * 2);
        ctx.restore();
        return;
      }
    }
    ctx.save();
    ctx.globalAlpha *= maxAlpha;
    const g = ctx.createRadialGradient(x, y, 0, x, y, radius);
    g.addColorStop(0, color);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // --- 1. AUTOMATISIERTER DECKEN-KRAN MIT GOD-RAY SCHATTENWURF -------------
  function renderGantryCrane(ctx, dt) {
    const crane = fx.crane;
    crane.x += crane.speed * crane.dir * dt;

    if (crane.dir > 0 && crane.x > BASE_WIDTH + 140) {
      crane.x = -140;
    } else if (crane.dir < 0 && crane.x < -140) {
      crane.x = BASE_WIDTH + 140;
    }

    crane.cargoAngle = Math.sin(fx.time * 2.2) * 0.08;

    const ty = 24;
    const tx = crane.x;

    // Laufkatzen-Gehäuse auf der Schiene
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(Math.round(tx - 36), ty - 8, 72, 16);
    ctx.fillStyle = '#334155';
    ctx.fillRect(Math.round(tx - 32), ty - 6, 64, 12);
    ctx.fillStyle = '#eab308';
    ctx.fillRect(Math.round(tx - 28), ty + 6, 56, 3); // Hazard Strip

    // Blinkende Kran-Sicherheitsleuchte
    const craneBlink = Math.sin(fx.time * 8) > 0;
    ctx.fillStyle = craneBlink ? '#f59e0b' : '#78350f';
    ctx.fillRect(Math.round(tx - 3), ty - 12, 6, 4);

    // 4 hängende Führungs-/Winden-Stahlseile von der Deckenkatze
    const cableLen = 46;
    const swingX = Math.sin(crane.cargoAngle) * cableLen;
    const hoistX = tx + swingX;
    const hoistY = ty + 8 + cableLen;

    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(tx - 20, ty + 8); ctx.lineTo(hoistX - 18, hoistY);
    ctx.moveTo(tx - 8, ty + 8);  ctx.lineTo(hoistX - 8, hoistY);
    ctx.moveTo(tx + 8, ty + 8);  ctx.lineTo(hoistX + 8, hoistY);
    ctx.moveTo(tx + 20, ty + 8); ctx.lineTo(hoistX + 18, hoistY);
    ctx.stroke();

    // Laufkatzen-Winde / Flaschenzug-Kranwerk (Trolley Hoist Block)
    ctx.save();
    ctx.translate(hoistX, hoistY);
    ctx.rotate(crane.cargoAngle);

    // Winden-Chassis & Schwerlast-Träger
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-24, 0, 48, 18);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(-22, 2, 44, 14);

    // Industrielle Seiltrommel (Winch Drum) in der Mitte
    ctx.fillStyle = '#334155';
    ctx.fillRect(-12, 4, 24, 10);
    // Gerillte Stahlseilwicklungen auf der Trommel
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 1;
    for (let wx = -10; wx <= 10; wx += 3) {
      ctx.beginPath();
      ctx.moveTo(wx, 4);
      ctx.lineTo(wx, 14);
      ctx.stroke();
    }

    // Flansch-Kappen & Schmiernippel
    ctx.fillStyle = '#475569';
    ctx.fillRect(-23, 5, 3, 8);
    ctx.fillRect(20, 5, 3, 8);
    p(ctx, -22, 9, '#cbd5e1');
    p(ctx, 21, 9, '#cbd5e1');

    // Gelb-schwarze Hazard-Warnstreifen an der Unterkante
    ctx.fillStyle = '#eab308';
    ctx.fillRect(-20, 14, 40, 3);
    ctx.fillStyle = '#090f17';
    for (let hx = -18; hx < 20; hx += 8) {
      ctx.fillRect(hx, 14, 4, 3);
    }

    // Status-LED an der Winde (Grün = Bereit / Last aktiv)
    p(ctx, -18, 3, '#22c55e');
    glow(ctx, -18, 3, 4, 'rgba(34, 197, 94, 0.6)', 0.5);

    // Sekundäres Hubseil hinab zum Kranhaken (mit leichter Eigenpendelung)
    const hookSwingAngle = crane.cargoAngle * 1.35;
    const hookCableLen = 38;
    const hookLocalX = Math.sin(hookSwingAngle) * hookCableLen;
    const hookLocalY = 17 + hookCableLen;

    // Doppelt verdrilltes Schwerlast-Hubseil
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(-2, 17);
    ctx.lineTo(hookLocalX - 2, hookLocalY);
    ctx.moveTo(2, 17);
    ctx.lineTo(hookLocalX + 2, hookLocalY);
    ctx.stroke();

    // SCHWERLAST-KRANHAKEN & FLASCHENZUG-ROLLE (Forged Crane Hook Assembly)
    ctx.save();
    ctx.translate(hookLocalX, hookLocalY);
    ctx.rotate(hookSwingAngle * 0.4);

    // Flaschenzug-Kopf (Sheave Block)
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(-8, 0, 16, 12);
    ctx.fillStyle = '#334155';
    ctx.fillRect(-6, 2, 12, 8);
    // Zentrale Seilrollen-Achse
    ctx.beginPath();
    ctx.arc(0, 6, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = '#64748b';
    ctx.fill();
    p(ctx, 0, 6, '#f8fafc');

    // Haken-Drehzapfen (Swivel Collar)
    ctx.fillStyle = '#475569';
    ctx.fillRect(-4, 12, 8, 4);

    // Geschmiedeter Schwerlast-Kranhaken (Schenkel, Bogen & Spitze)
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(0, 16);
    ctx.lineTo(0, 23);
    // Hakenbogen nach links herum
    ctx.bezierCurveTo(-14, 25, -16, 38, -3, 39);
    ctx.bezierCurveTo(8, 40, 12, 33, 9, 25);
    ctx.stroke();

    // Haken-Kern / Innenkante (Stahl-Schattierung)
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(0, 16);
    ctx.lineTo(0, 22);
    ctx.bezierCurveTo(-12, 24, -14, 36, -3, 37);
    ctx.bezierCurveTo(6, 38, 10, 32, 8, 26);
    ctx.stroke();

    // Sicherheitsfalle (Safety Latch, federbelastet)
    ctx.strokeStyle = '#eab308';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(-1, 23);
    ctx.lineTo(7, 27);
    ctx.stroke();

    // Highlight-Glint an der geschmiedeten Hakenspitze
    p(ctx, 9, 25, '#ffffff');

    ctx.restore(); // Hook
    ctx.restore(); // Winch

    // Subtiler, physikalisch stimmiger Decken- & Bodenschatten
    // 1. Weicher Kontaktschatten direkt unter der Schienen-Laufkatze
    ctx.fillStyle = 'rgba(2, 6, 12, 0.35)';
    ctx.fillRect(Math.round(tx - 38), ty + 8, 76, 3.5);

    // 2. Weicher Bodenschatten des hängenden Flaschenzugs und Hakens
    if (hoistX > -20 && hoistX < BASE_WIDTH + 20) {
      ctx.save();
      ctx.fillStyle = 'rgba(2, 6, 12, 0.16)';
      ctx.beginPath();
      ctx.ellipse(hoistX, 520, 18, 5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  // --- 2. FERNER BODEN-ROVER (Automated Munitions Tug // 40T) ---------------
  function renderGroundRover(ctx, dt) {
    const rover = fx.rover;
    rover.x += rover.speed * dt;
    rover.wheelAngle = (rover.wheelAngle + rover.speed * dt * 0.16) % (Math.PI * 2);
    rover.beaconAngle = (rover.beaconAngle + dt * 8.5) % (Math.PI * 2);

    if (rover.x < -120) {
      rover.x = BASE_WIDTH + 120;
    }

    const ry = 330;
    const rx = rover.x;

    // A. Front-Scheinwerferkegel auf dem fernen Hangar-Boden (leuchtet nach links)
    const headGrad = ctx.createLinearGradient(rx - 22, ry + 7, rx - 95, ry + 12);
    headGrad.addColorStop(0, 'rgba(254, 240, 138, 0.55)');
    headGrad.addColorStop(0.3, 'rgba(254, 240, 138, 0.28)');
    headGrad.addColorStop(1, 'rgba(254, 240, 138, 0)');
    ctx.fillStyle = headGrad;
    ctx.beginPath();
    ctx.moveTo(rx - 22, ry + 4);
    ctx.lineTo(rx - 105, ry - 4);
    ctx.lineTo(rx - 105, ry + 18);
    ctx.closePath();
    ctx.fill();

    // B. Bodenschatten des Rovers
    ctx.fillStyle = 'rgba(2, 6, 18, 0.45)';
    ctx.beginPath();
    ctx.ellipse(rx, ry + 16, 26, 4.5, 0, 0, Math.PI * 2);
    ctx.fill();

    // C. Federung & Achs-Aufhängung
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 2;
    [-15, 15].forEach(ax => {
      ctx.beginPath();
      ctx.moveTo(rx + ax, ry + 6);
      ctx.lineTo(rx + ax, ry + 12);
      ctx.stroke();

      // Gelbe Stoßdämpferfeder
      ctx.strokeStyle = '#ca8a04';
      ctx.lineWidth = 1.5;
      for (let sy = ry + 7; sy < ry + 12; sy += 2) {
        ctx.beginPath();
        ctx.moveTo(rx + ax - 2, sy);
        ctx.lineTo(rx + ax + 2, sy + 1);
        ctx.stroke();
      }
      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 2;
    });

    // D. 4 Schwere Industrie-Stollenreifen mit rotierenden Felgen
    [-16, 16].forEach(wx => {
      ctx.save();
      ctx.translate(rx + wx, ry + 12);

      // Gummireifen
      ctx.fillStyle = '#020617';
      ctx.beginPath();
      ctx.arc(0, 0, 6.5, 0, Math.PI * 2);
      ctx.fill();

      // Profil-Lauffläche (Tread Grooves)
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(0, 0, 5.2, 0, Math.PI * 2);
      ctx.stroke();

      // Rotierende Felge & Bolzenkranz
      ctx.rotate(rover.wheelAngle);
      ctx.fillStyle = '#475569';
      ctx.beginPath();
      ctx.arc(0, 0, 3.8, 0, Math.PI * 2);
      ctx.fill();

      // 4 Radbolzen
      ctx.fillStyle = '#f1f5f9';
      for (let b = 0; b < 4; b++) {
        const bAng = b * (Math.PI / 2);
        ctx.fillRect(Math.cos(bAng) * 2.2 - 0.5, Math.sin(bAng) * 2.2 - 0.5, 1.2, 1.2);
      }
      p(ctx, 0, 0, '#0f172a');
      ctx.restore();
    });

    // E. Schweres Chassis & Ladefläche (Warngelb mit Industrie-Patina)
    // Unterbau
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(Math.round(rx - 24), ry + 3, 48, 8);

    // Gelbe Karosserie-Wanne
    if (!_cachedRoverBodyGrad) {
      _cachedRoverBodyGrad = ctx.createLinearGradient(0, ry - 4, 0, ry + 8);
      _cachedRoverBodyGrad.addColorStop(0, '#fde047');
      _cachedRoverBodyGrad.addColorStop(0.3, '#eab308');
      _cachedRoverBodyGrad.addColorStop(0.85, '#ca8a04');
      _cachedRoverBodyGrad.addColorStop(1, '#854d0e');
    }
    ctx.fillStyle = _cachedRoverBodyGrad;
    ctx.beginPath();
    ctx.moveTo(rx - 24, ry + 8);
    ctx.lineTo(rx - 22, ry - 3);
    ctx.lineTo(rx - 10, ry - 4);
    ctx.lineTo(rx + 24, ry - 4);
    ctx.lineTo(rx + 24, ry + 8);
    ctx.closePath();
    ctx.fill();

    // Kantenschlag & Bevel-Highlight
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(rx - 22, ry - 3);
    ctx.lineTo(rx - 10, ry - 4);
    ctx.lineTo(rx + 24, ry - 4);
    ctx.stroke();

    // Hazard-Chevrons entlang des Schwellers
    ctx.save();
    ctx.beginPath();
    ctx.rect(rx - 20, ry + 5, 42, 3);
    ctx.clip();
    ctx.fillStyle = '#0f172a';
    for (let hx = rx - 24; hx < rx + 26; hx += 6) {
      ctx.beginPath();
      ctx.moveTo(hx, ry + 8);
      ctx.lineTo(hx + 3, ry + 5);
      ctx.lineTo(hx + 5, ry + 5);
      ctx.lineTo(hx + 2, ry + 8);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();

    // Front-Kühlergrill & Seilwinde
    ctx.fillStyle = '#020617';
    ctx.fillRect(Math.round(rx - 24), ry + 1, 3, 6);
    ctx.fillStyle = '#64748b';
    ctx.fillRect(Math.round(rx - 25), ry + 4, 2, 3); // Windenmaul

    // Scheinwerfer-Glas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(Math.round(rx - 23), ry - 1, 2, 3);
    p(ctx, rx - 23, ry, '#fef08a');

    // Rote Rückleuchte am Heck
    ctx.fillStyle = '#ef4444';
    ctx.fillRect(Math.round(rx + 23), ry + 1, 2, 3);
    p(ctx, rx + 24, ry + 2, '#fca5a5');

    // Stencil auf der Fahrertür / Seitenwand
    ctx.font = '700 4px monospace';
    ctx.fillStyle = '#0f172a';
    ctx.fillText('40T', rx - 16, ry + 3);

    // F. Ladefläche & Geladene Militär-Munitionskisten (Cargo)
    // Riffelblech-Kante
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(Math.round(rx - 8), ry - 5, 30, 2);

    // Kiste 1: Olivgrüne schwere Raketenkiste
    ctx.fillStyle = '#166534';
    ctx.fillRect(Math.round(rx - 6), ry - 12, 17, 7);
    ctx.fillStyle = '#14532d';
    ctx.fillRect(Math.round(rx - 6), ry - 12, 17, 1);
    ctx.fillStyle = '#ca8a04';
    ctx.fillRect(Math.round(rx - 4), ry - 10, 13, 2); // Munitions-Markierung
    // Kiste 2: Gestapelte kleinere Kiste
    ctx.fillStyle = '#334155';
    ctx.fillRect(Math.round(rx + 12), ry - 10, 11, 5);
    p(ctx, rx + 13, ry - 9, '#94a3b8');
    p(ctx, rx + 21, ry - 9, '#94a3b8');

    // Schwarze Spanngurte über die Ladung
    ctx.fillStyle = '#020617';
    ctx.fillRect(Math.round(rx - 2), ry - 12, 1.5, 8);
    ctx.fillRect(Math.round(rx + 6), ry - 12, 1.5, 8);
    ctx.fillRect(Math.round(rx + 16), ry - 10, 1.5, 6);

    // G. 360° Drehende Gelbe Rundumleuchte (Rotating Beacon)
    const beaconX = rx - 10, beaconY = ry - 6;
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(Math.round(beaconX - 2), beaconY, 5, 2); // Sockel

    // Glasdom
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(Math.round(beaconX - 2), beaconY - 4, 5, 4);

    // Rotierender Spiegelglanz
    const sweep = Math.cos(rover.beaconAngle);
    const sweepW = Math.abs(sweep) * 3;
    ctx.fillStyle = sweep > 0 ? '#ffffff' : '#fbbf24';
    ctx.fillRect(Math.round(beaconX - sweepW * 0.5), beaconY - 3, Math.max(1, sweepW), 3);

    // Weiche Licht-Aura der Rundumleuchte
    if (sweep > 0.1) {
      ctx.save();
      const bGlow = ctx.createRadialGradient(beaconX, beaconY - 2, 0, beaconX, beaconY - 2, 22);
      bGlow.addColorStop(0, `rgba(251, 191, 36, ${0.45 * sweep})`);
      bGlow.addColorStop(0.5, `rgba(245, 158, 11, ${0.18 * sweep})`);
      bGlow.addColorStop(1, 'rgba(245, 158, 11, 0)');
      ctx.fillStyle = bGlow;
      ctx.beginPath();
      ctx.arc(beaconX, beaconY - 2, 22, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  // --- 3. WABERNDER BODENNEBEL (Cryo-Haze & Ground Fog) ---------------------
  function renderGroundFog(ctx, dt) {
    ctx.save();
    const fogCanvas = getFogPuffCanvas();
    for (const fog of fx.groundFogPuffs) {
      fog.x += fog.vx * 30 * dt;
      if (fog.x - fog.radius > BASE_WIDTH) {
        fog.x = -fog.radius;
        fog.y = 440 + Math.random() * 110;
      }

      const waveY = fog.y + Math.sin(fx.time * 1.5 + fog.seed) * 5;
      const alphaMod = fog.alpha * (0.7 + Math.sin(fx.time * 2 + fog.seed * 0.5) * 0.3);

      if (fogCanvas && ctx.drawImage) {
        ctx.globalAlpha = alphaMod;
        ctx.drawImage(fogCanvas, fog.x - fog.radius, waveY - fog.radius, fog.radius * 2, fog.radius * 2);
      } else {
        const fGrad = ctx.createRadialGradient(fog.x, waveY, 0, fog.x, waveY, fog.radius);
        fGrad.addColorStop(0, `rgba(224, 242, 254, ${alphaMod})`);
        fGrad.addColorStop(0.5, `rgba(186, 230, 253, ${alphaMod * 0.4})`);
        fGrad.addColorStop(1, 'rgba(186, 230, 253, 0)');
        ctx.fillStyle = fGrad;
        ctx.beginPath();
        ctx.arc(fog.x, waveY, fog.radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }

  // --- 4. SCHWENKENDER HANGAR-SICHERHEITSSCHEINWERFER ----------------------
  function renderSweepingSpotlight(ctx, dt) {
    fx.spotlight.angle += dt * 0.4;
    const originX = 360, originY = 24;

    const targetX = 460 + Math.sin(fx.spotlight.angle) * 220;
    const targetY = 220 + Math.cos(fx.spotlight.angle * 1.6) * 60;

    ctx.save();
    const spotGrad = ctx.createRadialGradient(originX, originY, 10, targetX, targetY, 140);
    spotGrad.addColorStop(0, 'rgba(254, 243, 199, 0.22)');
    spotGrad.addColorStop(0.6, 'rgba(254, 243, 199, 0.06)');
    spotGrad.addColorStop(1, 'rgba(254, 243, 199, 0)');
    ctx.fillStyle = spotGrad;

    ctx.beginPath();
    ctx.moveTo(originX - 10, originY);
    ctx.lineTo(targetX - 55, targetY - 35);
    ctx.lineTo(targetX + 55, targetY + 35);
    ctx.closePath();
    ctx.fill();

    // Wand-Lichtfleck
    if (!_cachedSpotPoolGrad) {
      _cachedSpotPoolGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 45);
      _cachedSpotPoolGrad.addColorStop(0, 'rgba(254, 243, 199, 0.28)');
      _cachedSpotPoolGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    }
    ctx.save();
    ctx.translate(targetX, targetY);
    ctx.fillStyle = _cachedSpotPoolGrad;
    ctx.beginPath();
    ctx.ellipse(0, 0, 50, 28, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.restore();
  }

  // --- 5. 3D-HOLOGRAPHISCHER GLOBUS AUF DEM CATWALK -------------------------
  function renderHoloGlobe(ctx) {
    const hx = 180, hy = 214;
    const r = 14;

    ctx.save();
    // Basis-Lichtkegel
    if (!_cachedHoloConeGrad) {
      _cachedHoloConeGrad = ctx.createRadialGradient(hx, 232, 2, hx, hy, 28);
      _cachedHoloConeGrad.addColorStop(0, 'rgba(0, 240, 255, 0.4)');
      _cachedHoloConeGrad.addColorStop(1, 'rgba(0, 240, 255, 0)');
    }
    ctx.fillStyle = _cachedHoloConeGrad;
    ctx.beginPath();
    ctx.moveTo(hx - 12, 232);
    ctx.lineTo(hx + 12, 232);
    ctx.lineTo(hx + r + 4, hy);
    ctx.lineTo(hx - r - 4, hy);
    ctx.closePath();
    ctx.fill();

    // Rotierende 3D Wireframe-Kugel
    const rot = fx.time * 1.5;
    ctx.strokeStyle = state.redAlert ? '#ef4444' : '#00f0ff';
    ctx.lineWidth = 1;

    // Äußerer Kugelumriss
    ctx.beginPath();
    ctx.arc(hx, hy, r, 0, Math.PI * 2);
    ctx.stroke();

    // Längenkreis-Rotation (Longitude Ellipsen)
    const cosR = Math.cos(rot);
    ctx.beginPath();
    ctx.ellipse(hx, hy, Math.max(1, Math.abs(cosR) * r), r, 0, 0, Math.PI * 2);
    ctx.stroke();

    const cosR2 = Math.cos(rot + Math.PI / 3);
    ctx.beginPath();
    ctx.ellipse(hx, hy, Math.max(1, Math.abs(cosR2) * r), r, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Breitenkreise (Latitude Lines)
    ctx.beginPath();
    ctx.ellipse(hx, hy - 5, r * 0.85, 3, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(hx, hy + 5, r * 0.85, 3, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Orbitierender taktischer Satelliten-Blip
    const orbX = hx + Math.cos(rot * 2) * (r + 5);
    const orbY = hy + Math.sin(rot * 2) * 6;
    ctx.fillStyle = '#fbbf24';
    ctx.fillRect(orbX - 1.5, orbY - 1.5, 3, 3);

    // Schwebende Koordinaten
    ctx.font = '6px monospace';
    ctx.fillStyle = state.redAlert ? '#ef4444' : '#38bdf8';
    ctx.fillText('SEC: 04', hx - 12, hy - r - 3);

    ctx.restore();
  }

  // --- 6a. TECHNIKER 1 AUF DEM CATWALK (VORDERGRUND-KONSOLE) ---------------
  function renderCatwalkTechnician(ctx) {
    ctx.save();
    const t1X = 130, t1Y = 240;
    // Beine & Körper
    ctx.fillStyle = '#09111a';
    ctx.fillRect(t1X - 3, t1Y - 26, 6, 16);
    ctx.fillRect(t1X - 4, t1Y - 10, 3, 10);
    ctx.fillRect(t1X + 1, t1Y - 10, 3, 10);
    // Kopf & Schutzhelm
    ctx.beginPath();
    ctx.arc(t1X, t1Y - 30, 4, 0, Math.PI * 2);
    ctx.fill();
    p(ctx, t1X + 1, t1Y - 30, '#38bdf8'); // Helm-Visier
    // Leuchtendes Datapad in den Händen
    ctx.fillStyle = '#38bdf8';
    ctx.fillRect(t1X + 3, t1Y - 20, 5, 3);
    glow(ctx, t1X + 5, t1Y - 19, 8, 'rgba(56, 189, 248, 0.4)', 0.6);
    ctx.restore();
  }

  // --- 6a2. SHADE-INFILTRATOR AUF DEM CATWALK (INGAME-CHARAKTER // DROPSHIP-VORBEREITUNG) ---
  function renderCatwalkShade(ctx, dt) {
    ctx.save();
    const sX = 64, sY = 240; // Position am Catwalk-Geländer
    const t = fx.time;

    // 1. ZIELVEKTOR & ANGLE-BERECHNUNG
    // Wandern des Laserzielpunkts am Hangar-Boden
    const sweepSine = Math.sin(t * 0.8);
    const sweepCos = Math.cos(t * 1.15);
    const targetX = 330 + sweepSine * 65 + sweepCos * 20;
    const targetY = 485 + Math.sin(t * 1.4) * 14;

    // Pivot-Punkt des Gewehrs & Schulter (am Geländer aufgestützt)
    const pivotX = sX + 6;
    const pivotY = sY - 18;

    // Exakter Zielwinkel von der Gewehraufhängung zum Boden-Zielpunkt
    const aimAngle = Math.atan2(targetY - pivotY, targetX - pivotX);

    // 2. UNTERKÖRPER & STEALTH-GREAVES (Beine aus Shade_Legs Stand)
    // Tatterd Shadow Cape Flatter-Effekt (wehender Umhangsaum im Hangar-Luftzug)
    const capeFlutter = Math.sin(t * 2.8) * 1.5;

    // Hinterer zerschlissener Umhangsaum (-X)
    ctx.fillStyle = '#020617';
    ctx.fillRect(sX - 9 + capeFlutter * 0.3, sY - 12, 4, 12);
    ctx.fillStyle = '#090d16';
    ctx.fillRect(sX - 7 + capeFlutter * 0.5, sY - 10, 3, 10);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(sX - 5 + capeFlutter * 0.6, sY - 8, 2, 7);

    // Beine & taktische Stealth-Panzerung
    // Linkes Bein (am Boden abgestützt)
    ctx.fillStyle = '#020617';
    ctx.fillRect(sX - 3, sY - 13, 4, 13);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(sX - 2, sY - 11, 2.5, 9);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(sX - 2, sY - 8, 2, 5);

    // Rechtes Bein (leicht vorgebeugt am Geländerpfosten)
    ctx.fillStyle = '#020617';
    ctx.fillRect(sX + 2, sY - 13, 4.5, 13);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(sX + 3, sY - 11, 2.8, 9);
    ctx.fillStyle = '#334155';
    ctx.fillRect(sX + 3, sY - 8, 2, 5);

    // Gürtel, Schnallen & Holster
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(sX - 3, sY - 15, 10, 3);
    p(ctx, sX + 1, sY - 14, '#64748b'); // Gürtelschnalle

    // 3. OBERKÖRPER, HOODED COWL & LEUCHTENDES VISIER
    // Sanfte Atem- und Zielkorrekturbewegung
    const breathY = Math.sin(t * 1.6) * 0.4;

    // Tattered Cape über den Schultern
    ctx.fillStyle = '#020617';
    ctx.fillRect(sX - 7, sY - 26 + breathY, 8, 12);
    ctx.fillStyle = '#090d16';
    ctx.fillRect(sX - 5, sY - 25 + breathY, 6, 10);

    // Torso-Panzerung mit Team-Emblem-Farbakzent (Western Cyan)
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(sX - 2, sY - 25 + breathY, 8, 11);
    ctx.fillStyle = '#00f0ff'; // Team-Badge
    ctx.fillRect(sX, sY - 23 + breathY, 2, 4);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(sX + 2, sY - 23 + breathY, 3, 8);

    // Kapuze (Hooded Cowl) & Kopf – neigt sich synchron zum Zielwinkel
    const headTilt = aimAngle * 0.45;
    ctx.save();
    ctx.translate(sX + 2, sY - 27 + breathY);
    ctx.rotate(headTilt);

    // Kapuzen-Silhouette
    ctx.fillStyle = '#020617';
    ctx.beginPath();
    ctx.arc(0, 0, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-3, -2, 6, 6);

    // Crimson / Scarlet Visier-Optik (Ingame-Signatur des Shade)
    ctx.fillStyle = '#ef4444';
    ctx.fillRect(1, -1, 3, 2);
    ctx.fillStyle = '#ff0033';
    p(ctx, 3, -1, '#ffffff');
    glow(ctx, 2.5, 0, 4, 'rgba(239, 68, 68, 0.65)', 0.5);

    ctx.restore(); // Head

    // 4. SUPPRESSED SNIPER RIFLE MIT ZIELFERNROHR & BIPOD
    // Rotiert exakt um den Aufstützpunkt am Geländer (`aimAngle`)
    ctx.save();
    ctx.translate(pivotX, pivotY + breathY);
    ctx.rotate(aimAngle);

    // Handschuh & Schaft (Shade Arm)
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(-7, -2, 6, 4);
    ctx.fillStyle = '#334155';
    ctx.fillRect(-5, -1, 4, 3);

    // Receiver, Magazin & Verschluss
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-2, -2, 10, 4);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, -1, 6, 3);
    ctx.fillStyle = '#090d16';
    ctx.fillRect(1, 2, 3, 4); // Magazin

    // High-Power Optic Scope (Zielfernrohr mit roter Linsenreflexion)
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, -6, 9, 3);
    ctx.fillStyle = '#334155';
    ctx.fillRect(2, -5, 5, 2);
    p(ctx, 8, -5, '#ff0033'); // Scope Frontglint

    // Schwerer Lauf & Karbon-Handguard
    ctx.fillStyle = '#334155';
    ctx.fillRect(8, -1.5, 12, 3);
    ctx.fillStyle = '#64748b';
    ctx.fillRect(12, -1, 7, 2);

    // Ausgeklapptes Zweibein (Bipod) auf das Catwalk-Geländer gestützt
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(14, 1.5);
    ctx.lineTo(13, 6);
    ctx.moveTo(16, 1.5);
    ctx.lineTo(17, 6);
    ctx.stroke();

    // Großer Schalldämpfer (Suppressor Muzzle)
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(20, -2.5, 10, 5);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(21, -2, 8, 4);

    // Mündungsspitze / Laser-Emitter-Punkt
    const localMuzzleX = 30;
    const localMuzzleY = 0;
    p(ctx, localMuzzleX, localMuzzleY, '#ef4444');

    ctx.restore(); // Rifle

    // 5. RECHNERISCH EXAKTER ZIELLASER & BODEN-ZIELPUNKT
    // Weltkoordinaten der Mündung aus Rotation berechnen (kein Versatz/Knick!)
    const worldMuzzleX = pivotX + Math.cos(aimAngle) * 30;
    const worldMuzzleY = (pivotY + breathY) + Math.sin(aimAngle) * 30;

    // Kollimierter roter Laserstrahl (exakt von Mündung zum Boden)
    const beamAlpha = 0.22 + Math.sin(t * 8) * 0.05;
    ctx.strokeStyle = `rgba(239, 68, 68, ${beamAlpha})`;
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(worldMuzzleX, worldMuzzleY);
    ctx.lineTo(targetX, targetY);
    ctx.stroke();

    // Dünnerer intensiver Kernstrahl
    ctx.strokeStyle = `rgba(255, 200, 200, ${beamAlpha * 0.8})`;
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    ctx.moveTo(worldMuzzleX, worldMuzzleY);
    ctx.lineTo(targetX, targetY);
    ctx.stroke();

    // Laser-Zielpunkt am Boden / Fracht
    const dotPulse = (Math.sin(t * 14) + 1) * 0.5;
    p(ctx, targetX, targetY, '#ff2222');
    p(ctx, targetX + 1, targetY, '#ffffff');
    glow(ctx, targetX, targetY, 4, `rgba(239, 68, 68, ${0.5 + dotPulse * 0.4})`, 0.7);

    // 6. AKTIVE OPTISCHE TARNUNG (Nanotech Camo Pulse alle 4.5s)
    const camoCycle = (t * 0.7) % 4.5;
    if (camoCycle < 0.6) {
      const camoAlpha = Math.sin((camoCycle / 0.6) * Math.PI) * 0.45;
      ctx.fillStyle = `rgba(56, 189, 248, ${camoAlpha})`;
      ctx.fillRect(sX - 9, sY - 32, 22, 34);
      ctx.strokeStyle = `rgba(168, 85, 247, ${camoAlpha * 0.85})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sX - 6, sY - 20);
      ctx.lineTo(sX + 10, sY - 20);
      ctx.stroke();
    }

    ctx.restore();
  }

  // --- 6a3. DECK OFFICER / TAKTISCHER COMMANDER (AM HOLO-TISCH) -------------
  function renderDeckOfficer(ctx, dt) {
    ctx.save();
    const offX = 198, offY = 240;
    const t = fx.time;

    // Idle-Atmung (Brustkorb-Hebung)
    const breath = Math.sin(t * 1.8) * 0.45;

    // Taktischer Blickwinkel: Beobachtet den rotierenden Holo-Globus auf dem Tisch (links von ihm bei X=180)
    const lookCycle = (t * 0.25) % (Math.PI * 2);
    const lookHolo = Math.sin(lookCycle) > -0.2;
    const headTurnX = lookHolo ? -0.8 : 0.4;

    // Bodenschatten auf den Catwalk-Gitterrosten
    ctx.fillStyle = 'rgba(2, 6, 12, 0.6)';
    ctx.beginPath();
    ctx.ellipse(offX, offY, 6, 2.5, 0, 0, Math.PI * 2);
    ctx.fill();

    // 1. Offiziershose & polierte Stiefel
    drawBeveledBox(ctx, offX - 3, offY - 8, 3, 8, '#0f172a', '#334155', '#020617', 1);
    drawBeveledBox(ctx, offX + 1, offY - 8, 3, 8, '#0f172a', '#334155', '#020617', 1);
    drawBeveledBox(ctx, offX - 3, offY - 16, 3, 9, '#1e293b', '#475569', '#090d16', 1);
    drawBeveledBox(ctx, offX + 1, offY - 16, 3, 9, '#1e293b', '#475569', '#090d16', 1);

    // 2. Dunkelblaue Dienstuniform mit goldenen Rangabzeichen & Atmung
    drawBeveledBox(ctx, offX - 4, offY - 26 + breath, 9, 11, '#0f172a', '#1e3a8a', '#020617', 1);
    p(ctx, offX - 4, offY - 25 + breath, '#fbbf24');
    p(ctx, offX + 4, offY - 25 + breath, '#fbbf24');
    line(ctx, offX - 1, offY - 24 + breath, offX - 1, offY - 17 + breath, '#f59e0b', 1);

    // Gestikulierender Arm zum Holo-Tisch
    const gesture = Math.sin(t * 1.2) * 1.5;
    line(ctx, offX - 3, offY - 22 + breath, offX - 8, offY - 18 + gesture, '#1e3a8a', 2);
    p(ctx, offX - 8, offY - 18 + gesture, '#cbd5e1'); // Weiße Offiziershandschuhe

    // 3. Kopf mit Offiziersmütze (Peaked Cap)
    const headY = offY - 29 + breath * 0.7;
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(offX + headTurnX, headY, 3.5, 0, Math.PI * 2);
    ctx.fill();
    rect(ctx, offX - 3 + headTurnX, headY - 3, 6, 2, '#1e293b');
    line(ctx, offX - 4 + headTurnX, headY - 1, offX + headTurnX, headY - 1, '#000000', 1.2);
    p(ctx, offX - 1 + headTurnX, headY - 2, '#eab308'); // Mützen-Kranz

    // Comms-Headset Diode am Ohr (grün-blinkend)
    const commsBlink = Math.sin(t * 3.5) > 0.3;
    p(ctx, offX + headTurnX + 3, headY - 1, commsBlink ? '#22c55e' : '#14532d');

    ctx.restore();
  }

  // --- 6a4. HUMAN VANGUARD MARINE SENTRY (POWER ARMOR // WACHE) -----
  function renderMarineSentry(ctx, dt) {
    ctx.save();
    const mX = 98, mY = 518;
    const t = fx.time;

    // Respiration: Schweres Heben und Senken des gepanzerten Torsos
    const breath = Math.sin(t * 1.6) * 0.55;

    // Rover-Erkennung & Ehrenbezeigung (Comradeship Salute)
    const roverNear = fx.rover && fx.rover.x > 60 && fx.rover.x < 175;
    const targetSalute = roverNear ? 1.0 : 0.0;
    fx.sentrySalute.armRaised += (targetSalute - fx.sentrySalute.armRaised) * Math.min(1.0, dt * 6.0);
    const salute = fx.sentrySalute.armRaised;

    // Sektor-Scanning oder Rover-Verfolgung
    let headTurn = 0;
    if (salute > 0.15) {
      // Verfolgt den vorbeifahrenden Munitions-Tug mit dem Blick
      const roverProg = (fx.rover.x - 60) / 115;
      headTurn = (roverProg - 0.5) * 3.2;
    } else {
      // Standard Hangar-Sicherungs-Scan alle 6 Sekunden
      const scanTime = (t * 0.3) % (Math.PI * 2);
      if (scanTime > Math.PI && scanTime < Math.PI * 1.4) {
        headTurn = Math.sin(((scanTime - Math.PI) / 0.4) * Math.PI) * -2.2;
      }
    }

    // Bodenschatten
    ctx.fillStyle = 'rgba(2, 6, 12, 0.7)';
    ctx.beginPath();
    ctx.ellipse(mX + 1, mY, 11, 4, 0, 0, Math.PI * 2);
    ctx.fill();

    // 1. Gepanzerte Stiefel & Beinschienen (Heavy Combat Greaves)
    drawBeveledBox(ctx, mX - 7, mY - 8, 6, 8, '#1e3a8a', '#3b82f6', '#0f172a', 1);
    drawBeveledBox(ctx, mX + 2, mY - 8, 6, 8, '#1e3a8a', '#3b82f6', '#0f172a', 1);
    p(ctx, mX - 5, mY - 2, '#94a3b8');
    p(ctx, mX + 4, mY - 2, '#94a3b8');

    // 2. Oberschenkel & Tiefschutz-Panzerplatte
    drawBeveledBox(ctx, mX - 6, mY - 17, 5, 10, '#1d4ed8', '#60a5fa', '#0f172a', 1);
    drawBeveledBox(ctx, mX + 2, mY - 17, 5, 10, '#1d4ed8', '#60a5fa', '#0f172a', 1);
    rect(ctx, mX - 2, mY - 15, 5, 7, '#0f172a');

    // 3. Torso / Schwere Brustpanzerung mit Atmung
    const torsoY = mY - 29 + breath;
    drawBeveledBox(ctx, mX - 8, torsoY, 17, 13, '#1e3a8a', '#3b82f6', '#09152b', 1.5);
    rect(ctx, mX - 3, torsoY + 3, 7, 3, '#eab308'); // Vanguard Gold-Insignie
    p(ctx, mX, torsoY + 4, '#ffffff');
    p(ctx, mX - 4, torsoY + 6, '#ca8a04');
    p(ctx, mX + 4, torsoY + 6, '#ca8a04');

    // 4. Massive Kugel-Schulterpanzer (Shoulder Pauldrons)
    drawBeveledBox(ctx, mX - 13, torsoY - 1, 8, 9, '#1d4ed8', '#93c5fd', '#0f172a', 1);
    drawBeveledBox(ctx, mX + 6, torsoY - 1, 8, 9, '#1d4ed8', '#93c5fd', '#0f172a', 1);
    line(ctx, mX - 11, torsoY + 2, mX - 7, torsoY + 2, '#f8fafc', 1.5);
    line(ctx, mX + 8, torsoY + 2, mX + 12, torsoY + 2, '#f8fafc', 1.5);

    // 4b. Linker Arm: Haltung am Oberschenkel vs. militärischer Vanguard-Gruß (Salute)
    if (salute > 0.04) {
      // Oberarm angewinkelt nach oben
      drawBeveledBox(ctx, mX - 14, torsoY + 1 - salute * 5, 6, 8, '#1e3a8a', '#60a5fa', '#0f172a', 1);
      // Unterarm zum Visier/Helmrand geführt
      const foreArmX = mX - 12 + salute * 5;
      const foreArmY = torsoY - 6 * salute;
      drawBeveledBox(ctx, foreArmX, foreArmY, 6, 7, '#1d4ed8', '#93c5fd', '#0f172a', 1);
      // Gold-verzierter Panzerhandschuh an der Schläfe
      rect(ctx, foreArmX + 2, foreArmY - 2, 4, 4, '#eab308');
      p(ctx, foreArmX + 3, foreArmY - 1, '#fef08a');
    } else {
      // Ruhende Standard-Wachhaltung am Oberschenkel
      drawBeveledBox(ctx, mX - 14, torsoY + 4, 5, 12, '#1e3a8a', '#3b82f6', '#0f172a', 1);
      rect(ctx, mX - 14, torsoY + 13, 5, 4, '#1e293b');
    }

    // 5. Vollhelm mit Cyan-HUD Visier & Scanning-Drehung
    const helmX = mX + headTurn;
    const helmY = torsoY - 7;
    drawBeveledBox(ctx, helmX - 4, helmY, 9, 8, '#1e293b', '#64748b', '#050a14', 1);

    // Cyan leuchtendes HUD-Visier mit lebendigem Glanzpuls
    const visorAlpha = 0.75 + Math.sin(t * 3.2) * 0.25;
    ctx.fillStyle = `rgba(0, 240, 255, ${visorAlpha})`;
    ctx.fillRect(helmX - 3, helmY + 3, 7, 2);
    p(ctx, helmX - 1, helmY + 3, '#ffffff');
    glow(ctx, helmX, helmY + 4, 6, `rgba(0, 240, 255, ${visorAlpha * 0.7})`, 0.6);

    // 6. C-14 Impaler Gauss-Gewehr in Wachstellung (vertikal gehalten)
    const gX = mX + 11, gY = mY - 25 + breath * 0.5;
    drawBeveledBox(ctx, gX - 3, gY, 6, 26, '#0f172a', '#334155', '#020617', 1);
    line(ctx, gX, gY - 14, gX, gY, '#cbd5e1', 2);
    drawBeveledBox(ctx, gX - 2, gY - 17, 4, 4, '#1e293b', '#94a3b8', '#020617', 1);
    rect(ctx, gX - 5, gY + 12, 4, 7, '#1e293b');
    p(ctx, gX - 4, gY + 14, '#ca8a04');
    drawBeveledBox(ctx, gX - 3, gY + 6, 5, 5, '#1e3a8a', '#60a5fa', '#0f172a', 1);

    ctx.restore();
  }

  // --- 6a5. HANGAR MECHANIC / COMBAT ENGINEER (AM WERKZEUGWAGEN) ------------
  function renderHangarMechanic(ctx, dt) {
    ctx.save();
    const engX = 282, engY = 515;
    const t = fx.time;

    // Ratschen-Arbeitszyklus am Hydro-Ventil:
    // Periodisches Festziehen mit Druckmoment (2 Klicks, Pause)
    const cycle = (t * 1.5) % 3.0;
    let wrenchAngle = 0;
    let armY = 0;
    if (cycle < 0.4) {
      wrenchAngle = Math.sin((cycle / 0.4) * Math.PI) * 0.25;
      armY = Math.sin((cycle / 0.4) * Math.PI) * 2;
    } else if (cycle > 0.6 && cycle < 1.0) {
      wrenchAngle = Math.sin(((cycle - 0.6) / 0.4) * Math.PI) * 0.25;
      armY = Math.sin(((cycle - 0.6) / 0.4) * Math.PI) * 2;
    }

    // Bodenschatten
    ctx.fillStyle = 'rgba(2, 6, 12, 0.65)';
    ctx.beginPath();
    ctx.ellipse(engX, engY, 8, 3, 0, 0, Math.PI * 2);
    ctx.fill();

    // 1. Beine & Arbeitshose
    drawBeveledBox(ctx, engX - 4, engY - 8, 4, 8, '#292524', '#57534e', '#0c0a09', 1);
    drawBeveledBox(ctx, engX + 1, engY - 8, 4, 8, '#292524', '#57534e', '#0c0a09', 1);
    drawBeveledBox(ctx, engX - 4, engY - 16, 3.5, 9, '#44403c', '#78716c', '#1c1917', 1);
    drawBeveledBox(ctx, engX + 1, engY - 16, 3.5, 9, '#44403c', '#78716c', '#1c1917', 1);

    // 2. Rote Arbeitsjacke mit Reflektorstreifen
    drawBeveledBox(ctx, engX - 5, engY - 25, 9, 10, '#7f1d1d', '#b91c1c', '#450a0a', 1);
    line(ctx, engX - 4, engY - 21, engX + 3, engY - 21, '#eab308', 1.2);

    // 3. Kopf mit hochgeschobener Schweißer-Schutzbrille
    const headNod = armY * 0.4;
    ctx.fillStyle = '#1c1917';
    ctx.beginPath();
    ctx.arc(engX, engY - 28 + headNod, 3.5, 0, Math.PI * 2);
    ctx.fill();
    // Grüne Linsen der Schutzbrille mit Lichtreflex
    rect(ctx, engX - 2, engY - 30 + headNod, 4, 2, '#22c55e');
    const glint = Math.sin(t * 2.5) > 0.4;
    p(ctx, engX, engY - 30 + headNod, glint ? '#ffffff' : '#4ade80');

    // 4. Arm mit Hydro-Drehmomentschlüssel (bewegt sich beim Festziehen)
    ctx.save();
    ctx.translate(engX - 3, engY - 18);
    ctx.rotate(wrenchAngle);
    line(ctx, 0, 0, -5, 6 + armY, '#cbd5e1', 2);
    drawBeveledBox(ctx, -8, 5 + armY, 4, 5, '#1e293b', '#94a3b8', '#020617', 1);
    ctx.restore();

    ctx.restore();
  }

  // --- 6a6. A9 PIRANHA PILOT / FLIGHT OFFICER (PRE-FLIGHT WALKAROUND & COFFEE) -
  function renderA9Pilot(ctx, dt) {
    ctx.save();
    // Steht zwischen Panzer-Stellplatz und A9-Piranha-Dock, prüft Telemetrie und blickt zur Piranha-Optik
    const pX = 525, pY = 438;
    const t = fx.time;

    // Atmung & Gurtzeug-Flexing
    const breath = Math.sin(t * 2.0) * 0.45;

    // 16-Sekunden Storytelling-Zyklus:
    // Phase 1 (0.0 - 6.5s): Datapad-Telemetrie prüfen mit Tipp-Bewegung
    // Phase 2 (6.5 - 9.5s): Innehalten & Blick nach oben zur Kanzel/Hangar-Dach
    // Phase 3 (9.5 - 13.5s): Thermosbecher zur Brust/Mund heben & warmer Schluck
    // Phase 4 (13.5 - 16.0s): Becher absetzen, Ausatmen & Datapad reaktivieren
    const cycle = t % 16.0;
    const isInspecting = cycle < 6.5;
    const isLookingUp = cycle >= 6.5 && cycle < 9.5;
    const isDrinking = cycle >= 9.5 && cycle < 13.5;
    const isExhaling = cycle >= 13.5 && cycle < 16.0;

    let headLiftY = 0;
    let headTiltX = 0;
    if (isLookingUp) {
      headLiftY = -2.2;
      headTiltX = 1.4;
    } else if (isDrinking) {
      headLiftY = -1.0;
      headTiltX = -0.5;
    }

    // Datapad Tipp-Mikrobewegung
    const tapMotion = Math.sin(t * 7.5);
    const isTapping = isInspecting && (Math.sin(t * 1.8) > 0.2);

    // Bodenschatten
    ctx.fillStyle = 'rgba(2, 6, 12, 0.6)';
    ctx.beginPath();
    ctx.ellipse(pX, pY, 8, 3, 0, 0, Math.PI * 2);
    ctx.fill();

    // 1. Beine & Fliegerkombi
    drawBeveledBox(ctx, pX - 5, pY - 8, 4, 8, '#1e293b', '#475569', '#020617', 1);
    drawBeveledBox(ctx, pX + 1, pY - 8, 4, 8, '#1e293b', '#475569', '#020617', 1);
    drawBeveledBox(ctx, pX - 4, pY - 16, 3.5, 9, '#334155', '#64748b', '#0f172a', 1);
    drawBeveledBox(ctx, pX + 1, pY - 16, 3.5, 9, '#334155', '#64748b', '#0f172a', 1);

    // 2. Torso mit orangefarbenem High-G Gurtzeug
    const torsoY = pY - 26 + breath;
    drawBeveledBox(ctx, pX - 5, torsoY, 10, 11, '#1e293b', '#475569', '#0f172a', 1);
    rect(ctx, pX - 3, torsoY, 2, 10, '#f97316');
    rect(ctx, pX + 1, torsoY, 2, 10, '#f97316');
    p(ctx, pX, torsoY + 4, '#f8fafc');

    // 3. Kopf (neigt sich nach oben zur Kanzel oder trinkt am Becher)
    const headY = pY - 29 + breath * 0.7 + headLiftY;
    const headX = pX + headTiltX;
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(headX, headY, 3.8, 0, Math.PI * 2);
    ctx.fill();
    p(ctx, headX - 1, headY, '#f87171');
    p(ctx, headX + 1, headY - 1, isLookingUp ? '#fde047' : (isDrinking ? '#f8fafc' : '#38bdf8'));

    // 4. Pilotenhelm unter linkem Arm geklemmt
    drawBeveledBox(ctx, pX - 7, pY - 20 + breath * 0.8, 6, 6, '#0f172a', '#475569', '#020617', 1);
    rect(ctx, pX - 6, pY - 18 + breath * 0.8, 4, 3, '#f59e0b');
    p(ctx, pX - 5, pY - 17 + breath * 0.8, '#fef08a');

    // 5. Rechter Arm: Haltung abhängig vom Zyklus (Datapad vs. Kaffee trinken)
    if (isDrinking) {
      // Arm hebt Thermosbecher zum Mund
      const sipArmProg = Math.sin(((cycle - 9.5) / 4.0) * Math.PI);
      const mugX = pX + 2 + (1 - sipArmProg) * 3;
      const mugY = pY - 26 + (1 - sipArmProg) * 4;

      // Unterarm gebeugt
      line(ctx, pX + 4, torsoY + 2, mugX, mugY + 2, '#334155', 2.5);
      // Hand mit Kaffeebecher
      drawBeveledBox(ctx, mugX, mugY - 3, 4, 5, '#64748b', '#cbd5e1', '#1e293b', 0.8);
      rect(ctx, mugX + 1, mugY - 4, 2, 1, '#1e293b'); // Deckel/Mundstück
      p(ctx, mugX + 1, mugY - 2, '#f59e0b');         // Keramik-Streifen
    } else {
      // Datapad in der Hand mit interaktiven Telemetrie-Zeilen
      const padY = pY - 21 + (isTapping ? tapMotion * 0.5 : (isLookingUp ? 1.5 : 0));
      drawBeveledBox(ctx, pX + 4, padY, 7, 5, '#020617', '#38bdf8', '#000000', 0.8);
      rect(ctx, pX + 5, padY + 1, 5, 3, '#00f0ff');

      const dataTick = Math.floor(t * 3) % 3;
      p(ctx, pX + 6, padY + 2, dataTick === 0 ? '#22c55e' : '#f97316');
      p(ctx, pX + 8, padY + 2, dataTick === 1 ? '#22c55e' : '#38bdf8');

      const pilotPulse = (Math.sin(t * 4) + 1) * 0.5;
      glow(ctx, pX + 7, padY + 2, 8, `rgba(0, 240, 255, ${0.30 + pilotPulse * 0.3})`, 0.7);
    }

    ctx.restore();
  }

  // --- 6a6b. PILOTEN-THERMOS-DAMPF & ATEMHAUCH --------------------------------
  function renderThermosSteam(ctx, dt) {
    const t = fx.time;
    const cycle = t % 16.0;
    const isDrinking = cycle >= 9.5 && cycle < 13.5;
    const isExhaling = cycle >= 13.5 && cycle < 15.0;

    // Dampf-Ursprung: Becher auf Crate (554, 419) oder in der Hand (527, 412) oder Atem (527, 409)
    let originX = 554, originY = 419;
    if (isDrinking) {
      originX = 527;
      originY = 412;
    } else if (isExhaling) {
      originX = 527;
      originY = 409;
    }

    // Partikel periodisch erzeugen
    if (Math.random() < 0.38) {
      fx.thermosSteam.push({
        x: originX + (Math.random() - 0.5) * 1.5,
        y: originY,
        vx: (Math.random() - 0.5) * 1.2,
        vy: -3.8 - Math.random() * 2.4,
        radius: 1.0,
        maxRadius: 3.5 + Math.random() * 2.0,
        alpha: isExhaling ? 0.28 : 0.42,
        decay: 0.26 + Math.random() * 0.12,
        swirl: Math.random() * 10,
      });
    }

    // Partikel aktualisieren & zeichnen
    for (let i = fx.thermosSteam.length - 1; i >= 0; i--) {
      const p = fx.thermosSteam[i];
      p.x += Math.sin(t * 2.5 + p.swirl) * 0.35 + p.vx * dt;
      p.y += p.vy * dt;
      p.radius += (p.maxRadius - p.radius) * 0.045;
      p.alpha -= p.decay * dt;

      if (p.alpha <= 0) {
        fx.thermosSteam.splice(i, 1);
        continue;
      }

      ctx.save();
      ctx.globalAlpha = Math.max(0, p.alpha);
      const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius);
      grad.addColorStop(0, 'rgba(241, 245, 249, 0.7)');
      grad.addColorStop(0.5, 'rgba(226, 232, 240, 0.3)');
      grad.addColorStop(1, 'rgba(226, 232, 240, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  // --- 6a6b2. HEAVY EXPERIMENTAL TESLA WALKER (TW-04 COMBAT SCOUT // VORDERGRUND) -
  function renderTeslaWalker(ctx, dt) {
    ctx.save();
    const tw = fx.teslaWalker;
    if (!tw) { ctx.restore(); return; }
    const t = fx.time;

    // 1. Storytelling-Zustandsmaschine & Kinematik
    tw.timer -= dt;
    tw.sparkTimer += dt;

    switch (tw.state) {
      case 'look_left':
        tw.facing = -1;
        tw.headAngle += (-0.45 - tw.headAngle) * Math.min(1.0, dt * 3.5);
        tw.headTilt += (0.0 - tw.headTilt) * Math.min(1.0, dt * 3.5);
        if (tw.timer <= 0) {
          tw.state = 'look_center';
          tw.timer = 2.5;
        }
        break;

      case 'look_center':
        tw.headAngle += (0.0 - tw.headAngle) * Math.min(1.0, dt * 4.0);
        tw.headTilt += (-1.0 - tw.headTilt) * Math.min(1.0, dt * 3.5);
        if (tw.timer <= 0) {
          tw.state = 'walk_left';
          tw.timer = 4.5;
          tw.walkPhase = 0;
        }
        break;

      case 'walk_left':
        tw.facing = -1;
        tw.headAngle += (-0.15 - tw.headAngle) * Math.min(1.0, dt * 4.0);
        tw.headTilt += (0.5 - tw.headTilt) * Math.min(1.0, dt * 4.0);
        tw.x -= 14 * dt;
        tw.walkPhase += dt * 6.5;
        if (tw.timer <= 0 || tw.x <= 650) {
          tw.x = Math.max(650, tw.x);
          tw.state = 'inspect_floor';
          tw.timer = 3.5;
        }
        break;

      case 'inspect_floor':
        tw.facing = -1;
        tw.headAngle += (Math.sin(tw.timer * 2.5) * 0.25 - tw.headAngle) * Math.min(1.0, dt * 4.0);
        tw.headTilt += (2.5 - tw.headTilt) * Math.min(1.0, dt * 4.0);
        if (tw.timer <= 0) {
          tw.state = 'look_right';
          tw.timer = 3.0;
        }
        break;

      case 'look_right':
        tw.facing = 1;
        tw.headAngle += (0.45 - tw.headAngle) * Math.min(1.0, dt * 3.5);
        tw.headTilt += (-0.5 - tw.headTilt) * Math.min(1.0, dt * 3.5);
        if (tw.timer <= 0) {
          tw.state = 'walk_right';
          tw.timer = 4.5;
          tw.walkPhase = 0;
        }
        break;

      case 'walk_right':
        tw.facing = 1;
        tw.headAngle += (0.15 - tw.headAngle) * Math.min(1.0, dt * 4.0);
        tw.headTilt += (0.5 - tw.headTilt) * Math.min(1.0, dt * 4.0);
        tw.x += 14 * dt;
        tw.walkPhase += dt * 6.5;
        if (tw.timer <= 0 || tw.x >= 720) {
          tw.x = Math.min(720, tw.x);
          tw.state = 'look_left';
          tw.timer = 3.5;
        }
        break;
    }

    const isWalking = (tw.state === 'walk_left' || tw.state === 'walk_right');
    const x = tw.x;
    const baseY = tw.baseY;

    // Chassis Auf- und Ab-Wiegen beim Gehen
    const chassisBob = isWalking ? Math.abs(Math.sin(tw.walkPhase)) * 1.8 : Math.sin(t * 1.8) * 0.4;
    const cy = baseY - 32 + chassisBob;

    // 2. Weicher Bodenschatten & Deck-Reflexion
    ctx.fillStyle = 'rgba(2, 6, 14, 0.72)';
    ctx.beginPath();
    ctx.ellipse(x, baseY + 1, 32, 8, 0, 0, Math.PI * 2);
    ctx.fill();

    glow(ctx, x, baseY, 22, 'rgba(0, 240, 255, 0.16)', 0.5);

    // 3. 4 artikulierte Hydraulik-Gelenkbeine
    const legDefs = [
      { id: 0, hipX: -14, hipY: cy + 4, restX: -26, isRear: true,  phaseOffset: 0 },
      { id: 1, hipX:  14, hipY: cy + 4, restX:  26, isRear: true,  phaseOffset: Math.PI },
      { id: 2, hipX: -10, hipY: cy + 7, restX: -14, isRear: false, phaseOffset: Math.PI },
      { id: 3, hipX:  10, hipY: cy + 7, restX:  14, isRear: false, phaseOffset: 0 },
    ];

    function drawLeg(leg) {
      const hx = x + leg.hipX;
      const hy = leg.hipY;

      let fxTarget = x + leg.restX;
      let fyTarget = baseY;
      let inAir = false;

      if (isWalking) {
        const stepPhase = tw.walkPhase + leg.phaseOffset;
        const normPhase = ((stepPhase % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        inAir = normPhase < Math.PI;
        // Schwung nach vorn in Luft, Abdrücken nach hinten am Boden (kein Moonwalk)
        const swingProg = -Math.cos(normPhase);
        fxTarget += swingProg * 8 * tw.facing;
        if (inAir) {
          fyTarget -= Math.sin(normPhase) * 6.5;
        }
      }

      const kneeLift = inAir ? 3.5 : 0;
      const kx = (hx + fxTarget) * 0.5 + (leg.restX < 0 ? -9 : 9);
      const ky = (hy + fyTarget) * 0.5 - 9 - kneeLift + (leg.isRear ? -2 : 0);

      const armorFill = leg.isRear ? '#1e293b' : '#334155';
      const armorLight = leg.isRear ? '#334155' : '#64748b';
      const pistonCol = leg.isRear ? '#64748b' : '#cbd5e1';

      // 1. Hüftgelenk-Pivot
      drawBeveledBox(ctx, hx - 2.5, hy - 2.5, 5, 5, armorFill, armorLight, '#0f172a', 0.6);
      p(ctx, hx, hy, '#00f0ff');

      // 2. Oberschenkel-Strut mit hydraulischem Stellzylinder
      line(ctx, hx, hy, kx, ky, armorFill, 3.5);
      line(ctx, hx, hy - 1, kx, ky - 1, armorLight, 1.2);
      const pOffX = leg.restX < 0 ? 2 : -2;
      line(ctx, hx + pOffX, hy + 2, (hx + kx) * 0.5 + pOffX, (hy + ky) * 0.5 + 2, pistonCol, 1.4);

      // 3. Kniegelenk-Knöchel mit Bolzen
      drawBeveledBox(ctx, kx - 2.5, ky - 2.5, 5, 5, '#1e293b', armorLight, '#090d16', 0.6);
      p(ctx, kx, ky, '#94a3b8');

      // 4. Unterschenkel-Stempel mit Federdämpfer
      line(ctx, kx, ky, fxTarget, fyTarget, armorFill, 2.5);
      line(ctx, kx, ky, fxTarget, fyTarget, armorLight, 1.0);
      const midLX = (kx + fxTarget) * 0.5;
      const midLY = (ky + fyTarget) * 0.5;
      line(ctx, midLX - 1.5, midLY - 1, midLX + 1.5, midLY + 1, '#94a3b8', 1.5);
      line(ctx, midLX - 1.5, midLY + 2, midLX + 1.5, midLY + 4, '#94a3b8', 1.5);

      // 5. Magnet-Fußklaue mit Bodensensor
      drawBeveledBox(ctx, fxTarget - 3.5, fyTarget - 2, 7, 3, '#0f172a', armorLight, '#020617', 0.5);
      p(ctx, fxTarget - 2, fyTarget, '#64748b');
      p(ctx, fxTarget + 2, fyTarget, '#64748b');
      if (!inAir && fyTarget >= baseY - 0.5) {
        ctx.fillStyle = 'rgba(2, 6, 14, 0.85)';
        ctx.beginPath();
        ctx.ellipse(fxTarget, baseY + 0.5, 4.5, 1.8, 0, 0, Math.PI * 2);
        ctx.fill();
        p(ctx, fxTarget, fyTarget - 1, '#00f0ff');
      }
    }

    // Zeichne hintere Beine
    drawLeg(legDefs[0]);
    drawLeg(legDefs[1]);

    // 4. Chassis-Rumpf (34 x 18 px Titan/Navy-Hülle & Cyan-Reaktor)
    const hullX = x - 17;
    const hullY = cy - 9;
    const hullW = 34;
    const hullH = 18;

    drawBeveledBox(ctx, hullX, hullY, hullW, hullH, '#1e293b', '#64748b', '#090d16', 2.0);
    drawBeveledBox(ctx, hullX + 2, hullY + 2, 6, hullH - 4, '#0f172a', '#334155', '#020617', 0.8);
    drawBeveledBox(ctx, hullX + hullW - 8, hullY + 2, 6, hullH - 4, '#0f172a', '#334155', '#020617', 0.8);

    for (let rib = 0; rib < 3; rib++) {
      const ry = hullY + 4 + rib * 3.5;
      line(ctx, hullX + 3, ry, hullX + 6, ry, '#b45309', 1.2);
      p(ctx, hullX + 4, ry, '#f59e0b');
      line(ctx, hullX + hullW - 7, ry, hullX + hullW - 4, ry, '#b45309', 1.2);
      p(ctx, hullX + hullW - 6, ry, '#f59e0b');
    }

    hazardStripes(ctx, hullX + 6, hullY + hullH - 3, hullW - 12, 2.5, 2);

    const rx = x - 6;
    const ry = cy - 5;
    drawBeveledBox(ctx, rx, ry, 12, 10, '#083344', '#00f0ff', '#020617', 0.8);

    const coreGrad = ctx.createLinearGradient(rx, ry, rx + 12, ry + 10);
    const corePulse = (Math.sin(t * 5.0) + 1) * 0.5;
    coreGrad.addColorStop(0, '#ffffff');
    coreGrad.addColorStop(0.4, '#00f0ff');
    coreGrad.addColorStop(1, '#0369a1');
    ctx.fillStyle = coreGrad;
    ctx.fillRect(rx + 2, ry + 2, 8, 6);

    line(ctx, x, ry + 2, x, ry + 8, '#083344', 1);
    line(ctx, rx + 2, ry + 5, rx + 10, ry + 5, '#083344', 1);
    glow(ctx, x, cy, 14, `rgba(0, 240, 255, ${0.40 + corePulse * 0.35})`, 0.7);

    p(ctx, hullX + 10, hullY + 3, '#94a3b8');
    p(ctx, hullX + 12, hullY + 3, '#94a3b8');
    p(ctx, hullX + 14, hullY + 3, '#94a3b8');

    // Zeichne vordere Beine
    drawLeg(legDefs[2]);
    drawLeg(legDefs[3]);

    // 5. Schwenkbarer Tesla-Kopfturm & Amber-Sensorvisier
    const neckX = x;
    const neckY = cy - 10;
    drawBeveledBox(ctx, neckX - 8, neckY - 2, 16, 3, '#1e293b', '#475569', '#090d16', 0.5);

    ctx.save();
    ctx.translate(neckX, neckY);
    ctx.rotate(tw.headAngle);
    ctx.translate(0, tw.headTilt);

    drawBeveledBox(ctx, -9, -13, 18, 13, '#334155', '#64748b', '#0f172a', 1.5);
    line(ctx, -8, -6, 8, -6, '#1e293b', 1);

    drawBeveledBox(ctx, -6.5, -10, 13, 6, '#451a03', '#f59e0b', '#1c0a00', 0.6);
    rect(ctx, -5.5, -9, 11, 4, '#d97706');
    line(ctx, -5.5, -8, 5.5, -8, '#fbbf24', 0.8);
    line(ctx, -5.5, -6.5, 5.5, -6.5, '#fbbf24', 0.8);

    const sweep = (Math.sin(t * 3.2) + 1) * 0.5;
    p(ctx, -4 + sweep * 8, -8, '#ffffff');
    p(ctx, -4 + sweep * 8, -6.5, '#fef08a');
    glow(ctx, 0, -7.5, 7, 'rgba(245, 158, 11, 0.45)', 0.6);

    if (tw.state === 'inspect_floor') {
      const beamAlpha = 0.55 + Math.sin(t * 14) * 0.25;
      ctx.save();
      ctx.strokeStyle = `rgba(245, 158, 11, ${beamAlpha})`;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(0, -7);
      const floorTargetY = baseY - (neckY + tw.headTilt);
      ctx.lineTo(Math.sin(t * 4) * 4, floorTargetY);
      ctx.stroke();
      ctx.restore();

      glow(ctx, neckX + Math.sin(t * 4) * 4, baseY, 6, 'rgba(245, 158, 11, 0.75)', 0.8);
      p(ctx, neckX + Math.sin(t * 4) * 4, baseY, '#ffffff');
    }

    // 6. Tesla-Kuppel, Energiekugel & Blitz-Elektroden
    drawBeveledBox(ctx, -5, -17, 10, 4, '#78350f', '#f59e0b', '#451a03', 0.6);
    line(ctx, -4, -15, 4, -15, '#fbbf24', 1);

    const emitterY = -21;
    const sphereGrad = ctx.createRadialGradient(0, emitterY, 1, 0, emitterY, 5);
    sphereGrad.addColorStop(0, '#ffffff');
    sphereGrad.addColorStop(0.4, '#00f0ff');
    sphereGrad.addColorStop(1, '#0284c7');
    ctx.fillStyle = sphereGrad;
    ctx.beginPath();
    ctx.arc(0, emitterY, 4.5, 0, Math.PI * 2);
    ctx.fill();

    const emitterPulse = (Math.sin(t * 6) + 1) * 0.5;
    glow(ctx, 0, emitterY, 11, `rgba(0, 240, 255, ${0.55 + emitterPulse * 0.35})`, 0.8);

    const p1x = -4, p1y = -19, tip1x = -7.5, tip1y = -29;
    const p2x =  4, p2y = -19, tip2x =  7.5, tip2y = -29;

    line(ctx, p1x, p1y, tip1x, tip1y, '#64748b', 1.8);
    line(ctx, p1x + 0.5, p1y, tip1x + 0.5, tip1y, '#94a3b8', 0.8);
    drawBeveledBox(ctx, tip1x - 1.5, tip1y - 1.5, 3, 3, '#00f0ff', '#ffffff', '#0284c7', 0.5);

    line(ctx, p2x, p2y, tip2x, tip2y, '#64748b', 1.8);
    line(ctx, p2x - 0.5, p2y, tip2x - 0.5, tip2y, '#94a3b8', 0.8);
    drawBeveledBox(ctx, tip2x - 1.5, tip2y - 1.5, 3, 3, '#00f0ff', '#ffffff', '#0284c7', 0.5);

    const isSparking = (tw.state === 'look_center') || (Math.sin(t * 2.5) > 0.7);
    if (isSparking) {
      const sparkAlpha = 0.7 + Math.random() * 0.3;
      ctx.strokeStyle = `rgba(255, 255, 255, ${sparkAlpha})`;
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(tip1x, tip1y);
      const midSX = (Math.random() - 0.5) * 4;
      const midSY = -31 + (Math.random() - 0.5) * 3;
      ctx.lineTo(midSX, midSY);
      ctx.lineTo(tip2x, tip2y);
      ctx.stroke();

      glow(ctx, 0, -30, 9, 'rgba(0, 240, 255, 0.85)', 0.9);
      p(ctx, midSX, midSY, '#ffffff');
    }

    ctx.restore();
    ctx.restore();
  }

  // --- 6a6b3. HEAVY EXPERIMENTAL HYPER TESLA WALKER (APEX COMBAT SCORPION // VORDERGRUND) -
  // Vollvektor-Implementierung im Oberflächenstil des Tesla Walkers:
  // - Keine Pixelblöcke mehr: Bevel-Lichtkanten, Sloped Composite Armor, Zylinder, Dämpferfedern
  // - Imposante Apex-Größe (ca. 140px Spannweite, 125px Gesamthöhe inkl. aufgerichtetem Skorpionschwanz)
  // - Lebendige Hydraulik-Federung: 8 artikulierte Beine federn das Chassis dynamisch ein und aus
  // - Organisches Vor- und Zurückwiegen des 5-segmentigen Skorpionschwanzes
  // - Periodische elektrische Spulenladung mit Mikro-Lichtbögen (ausschließlich lokal um die Spule)
  function renderHyperTesla(ctx, dt) {
    ctx.save();
    const ht = fx.hyperTesla;
    if (!ht) { ctx.restore(); return; }
    const t = fx.time;

    ht.sparkTimer += dt;
    const x = ht.x;
    const baseY = ht.baseY;

    // 1. HYDRAULISCHES EIN- UND AUSFEDERN (SUSPENSION HEAVE & CHASSIS PITCH)
    const heaveCycle = Math.sin(t * 1.5);
    const heave = heaveCycle * 3.0;        // Chassis federt +/- 3.0px vertikal
    const cy = baseY - 46 + heave;          // Dynamische Chassis-Mitte
    const tilt = Math.sin(t * 0.75) * 0.02; // Subtile Rumpf-Nickbewegung

    // 2. MASSIVER MULTI-TIER BODENSCHATTEN & DECK-REFLEXION
    ctx.fillStyle = 'rgba(2, 6, 14, 0.84)';
    ctx.beginPath();
    ctx.ellipse(x, baseY + 1, 80, 17, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(2, 6, 14, 0.50)';
    ctx.beginPath();
    ctx.ellipse(x - 12, baseY + 1, 96, 22, 0, 0, Math.PI * 2);
    ctx.fill();

    // Cyan-Bodenreflexion des Reaktor-Kerns
    const rPulse = (Math.sin(t * 3.0) + 1) * 0.5;
    glow(ctx, x - 14, baseY + 1, 42, `rgba(0, 240, 255, ${0.14 + rPulse * 0.08})`, 0.6);

    // 3. 8 ARTIKULIERTE HYDRAULIK-BEINE (4 HINTERE + 4 VORDERE)
    // Gestaffelte Standpunkte auf dem Deck (baseY = 530); Knie spreizen beim Einfedern
    const legs = [
      // Hintere Beine (Z = -1, dunklere Schattierung, hinter dem Rumpf gerendert)
      { id: 0, hipX:  28, hipYOff: -4, restX:  60, isFar: true },
      { id: 1, hipX:  10, hipYOff: -2, restX:  32, isFar: true },
      { id: 2, hipX: -10, hipYOff: -2, restX: -20, isFar: true },
      { id: 3, hipX: -28, hipYOff: -4, restX: -56, isFar: true },
      // Vordere Beine (Z = +1, voll detailliert, vor dem Rumpf gerendert)
      { id: 4, hipX:  32, hipYOff:  6, restX:  68, isFar: false },
      { id: 5, hipX:  12, hipYOff:  8, restX:  38, isFar: false },
      { id: 6, hipX: -12, hipYOff:  8, restX: -26, isFar: false },
      { id: 7, hipX: -32, hipYOff:  6, restX: -68, isFar: false },
    ];

    function drawVectorLeg(leg) {
      // Hüft-Pivot folgt der Chassis-Suspension
      const hx = x + leg.hipX * Math.cos(tilt) - leg.hipYOff * Math.sin(tilt);
      const hy = cy + leg.hipX * Math.sin(tilt) + leg.hipYOff * Math.cos(tilt);

      // Fuß bleibt starr auf dem Deck fixiert
      const fxTarget = x + leg.restX;
      const fyTarget = baseY;

      // Kinematische Kniefederung: Bei Absenkung spreizt das Knie seitlich ab
      const mx = (hx + fxTarget) * 0.5;
      const kneeOut = (leg.restX > 0 ? 15 : -15) * (leg.isFar ? 0.85 : 1.0);
      const flexFactor = 1.0 + (heave / 3.0) * 0.20;
      const kx = mx + kneeOut * flexFactor;
      const ky = cy - 16 + (leg.isFar ? -5 : 2) - (heave * 0.4);

      const armorFill = leg.isFar ? '#131c2a' : '#1e293b';
      const armorMid  = leg.isFar ? '#1e293b' : '#334155';
      const armorLight= leg.isFar ? '#334155' : '#64748b';
      const pistonCol = leg.isFar ? '#475569' : '#cbd5e1';

      // A. Hüft-Drehlager
      drawBeveledBox(ctx, hx - 4, hy - 4, 8, 8, armorFill, armorLight, '#090d16', 0.8);
      p(ctx, hx, hy, leg.isFar ? '#475569' : '#00f0ff');

      // B. Oberschenkel-Träger (Femur)
      line(ctx, hx, hy, kx, ky, armorFill, 4.5);
      line(ctx, hx, hy - 1, kx, ky - 1, armorLight, 1.5);
      // Hydraulik-Zylinder parallel zum Femur
      const pOffX = leg.restX > 0 ? -2.5 : 2.5;
      line(ctx, hx + pOffX, hy + 2, (hx + kx) * 0.5 + pOffX, (hy + ky) * 0.5 + 2, pistonCol, 2.0);

      // C. Knie-Gelenkknoten mit Bolzen
      drawBeveledBox(ctx, kx - 4, ky - 4, 8, 8, armorMid, armorLight, '#050a12', 0.8);
      p(ctx, kx, ky, leg.isFar ? '#334155' : '#94a3b8');
      p(ctx, kx, ky - 1, leg.isFar ? '#475569' : '#cbd5e1');

      // D. Unterschenkel (Tibia) mit Federdämpfer
      line(ctx, kx, ky, fxTarget, fyTarget, armorFill, 3.5);
      line(ctx, kx, ky, fxTarget, fyTarget, armorLight, 1.2);

      // Stoßdämpfer-Rippen am Unterschenkel
      const midLX = (kx + fxTarget) * 0.5;
      const midLY = (ky + fyTarget) * 0.5;
      line(ctx, midLX - 2.5, midLY - 1.5, midLX + 2.5, midLY + 1.5, armorLight, 2.0);
      line(ctx, midLX - 2.5, midLY + 2.0, midLX + 2.5, midLY + 5.0, armorLight, 2.0);

      // E. Fußklaue & Bodenschatten
      drawBeveledBox(ctx, fxTarget - 5, fyTarget - 3, 10, 4, '#090d16', armorLight, '#020617', 0.6);
      line(ctx, fxTarget - 4.5, fyTarget, fxTarget - 7, fyTarget + 1.5, '#475569', 2.0);
      line(ctx, fxTarget + 4.5, fyTarget, fxTarget + 7, fyTarget + 1.5, '#475569', 2.0);

      // Fußkontaktschatten auf dem Deck
      ctx.fillStyle = 'rgba(2, 6, 14, 0.92)';
      ctx.beginPath();
      ctx.ellipse(fxTarget, baseY + 0.5, 6.5, 2.2, 0, 0, Math.PI * 2);
      ctx.fill();

      if (!leg.isFar) {
        p(ctx, fxTarget, fyTarget - 1.5, '#00f0ff');
      }
    }

    // Zeichne 4 hintere Beine
    for (let i = 0; i < 4; i++) {
      drawVectorLeg(legs[i]);
    }

    // 4. CHASSIS-RUMPF (SLOPED COMPOSITE ARMOR, COCKPIT & VENTILATION)
    ctx.save();
    ctx.translate(x, cy);
    ctx.rotate(tilt);

    // A. Reaktor-Wanne an der Unterseite
    drawBeveledBox(ctx, -28, 3, 56, 14, '#090d16', '#334155', '#020617', 1.4);
    // Reaktor-Sichtfenster
    const coreGlow = (Math.sin(t * 4.0) + 1) * 0.5;
    rect(ctx, -14, 6, 28, 6, '#0284c7');
    rect(ctx, -12, 7, 24, 4, '#00f0ff');
    line(ctx, -14, 9, 14, 9, '#ffffff', 1.2);
    glow(ctx, 0, 9, 16, `rgba(0, 240, 255, ${0.45 + coreGlow * 0.35})`, 0.75);

    // B. Zentraler Panzer-Rumpf (Sloped Composite Armor)
    drawBeveledBox(ctx, -40, -14, 76, 20, '#1e293b', '#64748b', '#090d16', 2.2);
    // Obere geschrägte Carapace-Platte
    drawBeveledBox(ctx, -32, -24, 60, 12, '#334155', '#94a3b8', '#0f172a', 1.6);

    // C. Kühlrippen / Belüftungslamellen auf dem Motordeck
    for (let rib = 0; rib < 5; rib++) {
      const rx = 4 + rib * 5;
      line(ctx, rx, -22, rx, -14, '#b45309', 1.5);
      p(ctx, rx, -18, '#f59e0b');
    }

    // D. Flanken-Hazard-Streifen
    hazardStripes(ctx, -22, -5, 34, 4.5, 3);

    // E. Front-Pincer-Scheren (schwere Greifzangen vorn links)
    const pincerFlex = Math.sin(t * 2.2) * 1.8;
    // Obere Zange
    line(ctx, -40, -7, -54, -12 + pincerFlex, '#334155', 3.5);
    line(ctx, -54, -12 + pincerFlex, -62, -8 + pincerFlex, '#cbd5e1', 2.4);
    line(ctx, -54, -13 + pincerFlex, -62, -9 + pincerFlex, '#ffffff', 1.2);
    // Untere Zange
    line(ctx, -40, 3, -53, 7 - pincerFlex, '#334155', 3.5);
    line(ctx, -53, 7 - pincerFlex, -61, 4 - pincerFlex, '#cbd5e1', 2.4);
    line(ctx, -53, 8 - pincerFlex, -61, 5 - pincerFlex, '#ffffff', 1.2);

    // F. Cockpit / Sensor-Zitadelle
    drawBeveledBox(ctx, -30, -32, 22, 10, '#1e293b', '#64748b', '#090d16', 1.2);
    // Polarisiertes Visierglas
    rect(ctx, -28, -30, 18, 5, '#0284c7');
    line(ctx, -28, -29, -10, -29, '#00f0ff', 1.4);
    line(ctx, -28, -28, -16, -28, '#ffffff', 0.9);
    p(ctx, -19, -28, '#caa152'); // Pilotensilhouette

    // G. Primäres Cyan-Sensorauge (blickt ruhig nach links)
    const eyeX = -31;
    const eyeY = -27;
    const eyePulse = (Math.sin(t * 3.5) + 1) * 0.5;
    glow(ctx, eyeX, eyeY, 12, `rgba(0, 240, 255, ${0.55 + eyePulse * 0.35})`, 0.85);
    p(ctx, eyeX, eyeY, '#00f0ff');
    p(ctx, eyeX - 1, eyeY, '#ffffff');

    // H. Sekundäre Telemetrie-Optik & Antennenmasten
    p(ctx, -31, -23, '#f59e0b');
    line(ctx, -20, -32, -23, -42, '#64748b', 1.4);
    p(ctx, -23, -42, '#00f0ff');
    line(ctx, -13, -32, -14, -39, '#64748b', 1.2);
    p(ctx, -14, -39, '#ef4444');

    ctx.restore(); // Ende Chassis-Transform

    // Zeichne 4 vordere Beine
    for (let i = 4; i < 8; i++) {
      drawVectorLeg(legs[i]);
    }

    // 5. ARTIKULIERTER SKORPIONSCHWANZ MIT 5 SEGMENTEN ("SCHWANZ ETWAS HIN UND HER")
    // Start an der Heck-Wand des Rumpfes
    const tailRootX = x + 24 * Math.cos(tilt) - (-10) * Math.sin(tilt);
    const tailRootY = cy + 24 * Math.sin(tilt) + (-10) * Math.cos(tilt);

    // Elegante Krümmung nach oben-hinten und dann weit nach vorn über das Chassis
    const segs = [
      { len: 18, curve: -0.32, w: 14 },
      { len: 20, curve: -0.38, w: 13 },
      { len: 22, curve: -0.42, w: 12 },
      { len: 22, curve: -0.40, w: 11 },
      { len: 24, curve: -0.15, w: 10 },
    ];

    let curX = tailRootX;
    let curY = tailRootY;
    let curAng = -Math.PI * 0.40; // Startwinkel: nach oben-hinten

    const tailPoints = [{ x: curX, y: curY, ang: curAng }];

    for (let s = 0; s < segs.length; s++) {
      // Schwingende Wellenkinematik entlang der Segmente
      const sway = Math.sin(t * 1.4 - s * 0.35) * 0.05;
      curAng += segs[s].curve + sway;
      const nextX = curX + Math.cos(curAng) * segs[s].len;
      const nextY = curY + Math.sin(curAng) * segs[s].len;
      tailPoints.push({ x: nextX, y: nextY, ang: curAng, w: segs[s].w });

      // Segment-Chassis
      ctx.save();
      const midX = (curX + nextX) * 0.5;
      const midY = (curY + nextY) * 0.5;
      ctx.translate(midX, midY);
      ctx.rotate(curAng);

      const halfLen = segs[s].len * 0.5;
      const halfW = segs[s].w * 0.5;

      drawBeveledBox(ctx, -halfLen, -halfW, segs[s].len, segs[s].w, '#1e293b', '#64748b', '#090d16', 1.2);
      p(ctx, -halfLen + 2.5, 0, '#94a3b8');
      p(ctx, halfLen - 2.5, 0, '#94a3b8');

      // Hydraulischer Verstellzylinder auf der Außenseite
      line(ctx, -halfLen + 3, -halfW - 2, halfLen - 3, -halfW - 2, '#cbd5e1', 1.6);
      p(ctx, 0, -halfW - 2, '#64748b');

      ctx.restore();

      curX = nextX;
      curY = nextY;
    }

    // 6. HOCHSPANNUNGS-TESLASPULE & WOLFRAM-STACHEL (SEGMENT 5)
    const tipSeg = tailPoints[tailPoints.length - 1];
    const coilX = (tailPoints[tailPoints.length - 2].x + tipSeg.x) * 0.5;
    const coilY = (tailPoints[tailPoints.length - 2].y + tipSeg.y) * 0.5;
    const coilAng = tipSeg.ang;

    ctx.save();
    ctx.translate(coilX, coilY);
    ctx.rotate(coilAng);

    // Keramik-Isolatorflansche
    drawBeveledBox(ctx, -10, -7, 5, 14, '#475569', '#cbd5e1', '#0f172a', 1.0);
    drawBeveledBox(ctx, 5, -7, 5, 14, '#475569', '#cbd5e1', '#0f172a', 1.0);

    // Mehrreihige Kupferdraht-Hochspannungsspule
    for (let ring = 0; ring < 5; ring++) {
      const rx = -5 + ring * 2.4;
      line(ctx, rx, -6, rx, 6, '#b45309', 2.0);
      line(ctx, rx, -5, rx, 5, '#f59e0b', 1.2);
    }

    // Wolfram-Stachelnadel
    line(ctx, 10, 0, 23, 0, '#cbd5e1', 2.6);
    line(ctx, 10, 0, 23, 0, '#ffffff', 1.2);
    p(ctx, 24, 0, '#ffffff');

    // Stachel-Aura
    const stingerPulse = (Math.sin(t * 6.0) + 1) * 0.5;
    glow(ctx, 24, 0, 10, `rgba(0, 240, 255, ${0.50 + stingerPulse * 0.35})`, 0.85);

    // 7. ELEKTRISCHER LADEZYKLUS ("HIN UND WIEDER EIN WENIG ELEKTRISCHE LADUNG UM DIE SPULE")
    // 4.8 Sekunden Zyklus: Vorwärmen -> Hochspannung -> Mikro-Lichtbögen um die Wicklung -> Abklingen
    const cyclePeriod = 4.8;
    const cycleT = (t % cyclePeriod);

    // Magnetisches Grundglimmen
    const baseGlowAlpha = 0.35 + Math.sin(t * 3.0) * 0.12;
    glow(ctx, 0, 0, 16, `rgba(245, 158, 11, ${baseGlowAlpha})`, 0.6);

    if (cycleT > 2.8 && cycleT < 4.2) {
      // Aktive Entladungsphase um die Spule
      const chargeProg = (cycleT - 2.8) / 1.4;
      const arcIntensity = Math.sin(chargeProg * Math.PI);

      glow(ctx, 0, 0, 20 + arcIntensity * 8, `rgba(0, 240, 255, ${0.45 + arcIntensity * 0.5})`, 0.9);

      const numArcs = 2 + Math.floor(arcIntensity * 2.5);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.lineWidth = 1.0;

      for (let a = 0; a < numArcs; a++) {
        const arcAng = (t * 12 + a * (Math.PI * 2 / numArcs)) % (Math.PI * 2);
        const r1 = 5.0 + Math.sin(t * 20 + a) * 1.5;
        const r2 = 8.0 + Math.cos(t * 22 + a) * 2.5;

        const p1x = Math.cos(arcAng) * r1;
        const p1y = Math.sin(arcAng) * r1;
        const midX = Math.cos(arcAng + 0.3) * ((r1 + r2) * 0.5) + (Math.random() - 0.5) * 2;
        const midY = Math.sin(arcAng + 0.3) * ((r1 + r2) * 0.5) + (Math.random() - 0.5) * 2;
        const p2x = Math.cos(arcAng + 0.6) * r2;
        const p2y = Math.sin(arcAng + 0.6) * r2;

        ctx.beginPath();
        ctx.moveTo(p1x, p1y);
        ctx.lineTo(midX, midY);
        ctx.lineTo(p2x, p2y);
        ctx.stroke();

        p(ctx, midX, midY, '#00f0ff');
        p(ctx, p2x, p2y, '#ffffff');
      }

      // Mikro-Lichtbogen zwischen Spulenring und Nadelansatz
      if (Math.sin(t * 28) > 0.4) {
        line(ctx, 5, -5, 11, -1, '#ffffff', 1.0);
        p(ctx, 8, -3, '#00f0ff');
      }
    }

    ctx.restore(); // Ende Spulen-Transform

    ctx.restore(); // Ende Haupt-Save
  }

  // --- 6a6b4. HIGH-DETAIL DEVIL PATROL (CLUMSY & PLAYFUL PYRO EXPERT) --------
  // Vollvektor-Implementierung im visuellen Oberflächenstil des Tesla Walkers:
  // - Basiert auf dem exakten Ingame-Sprite (1_devilpatrol.png / unitSprites.js)
  // - Skaliert und verfeinert: Sloped Composite Armor, Dual-Promethium-Tanks mit Manometern & Hazard-Stripes,
  //   flexibler Kraftstoffschlauch, flammendes HUD-Visier, Perdition-Flammenwerfer mit Anlassfarben & Pilotflamme.
  // - Verspielte Persönlichkeit: Watschelt neugierig umher, erzeugt kleine spielerische Flammenpuffs & Funkenringe.
  // - Tollpatschig: Stolpert gelegentlich über Deckfugen, fängt sich ab und blickt sich ertappt um.
  // - Seltenes Missgeschick: Setzt versehentlich den Boden in Brand, gerät in hektische Panik,
  //   tritt das Feuer mit 4 kräftigen Stiefeltritten energisch aus, prüft seine Sohle und atmet erleichtert auf.
  function triggerDevilPatrolFire() {
    const dp = fx.devilPatrol;
    if (!dp) return;
    if (dp.state === 'accidental_fire') return;
    dp.state = 'accidental_fire';
    dp.timer = 7.5;
    dp.fireX = dp.x + 18 * dp.facing;
    dp.fireActive = false;
    dp.fireIntensity = 0;
    dp.panicTimer = 0;
    dp.stompCount = 0;
    dp.stompY = 0;
  }

  function drawFireballContour(ctx, r, t, seed, scale = 1.0) {
    const w1 = Math.sin(t * 16 + seed) * (r * 0.16);
    const w2 = Math.cos(t * 22 + seed * 1.7) * (r * 0.18);
    const w3 = Math.sin(t * 26 - seed * 2.1) * (r * 0.14);

    ctx.beginPath();
    ctx.moveTo(r * 1.25 * scale, 0);
    ctx.quadraticCurveTo(r * 1.1 * scale, -r * 0.7 * scale + w1, r * 0.6 * scale, -r * 0.95 * scale);
    ctx.quadraticCurveTo(0, -r * 1.1 * scale + w2, -r * 0.4 * scale, -r * 0.85 * scale);
    ctx.quadraticCurveTo(-r * 0.8 * scale, -r * 0.9 * scale, -r * 1.45 * scale, -r * 0.55 * scale + w3);
    ctx.quadraticCurveTo(-r * 0.85 * scale, -r * 0.25 * scale, -r * 1.0 * scale, -r * 0.1 * scale);
    ctx.quadraticCurveTo(-r * 1.3 * scale, 0, -r * 1.75 * scale, w1 * 0.8);
    ctx.quadraticCurveTo(-r * 1.3 * scale, r * 0.1 * scale, -r * 0.85 * scale, r * 0.25 * scale);
    ctx.quadraticCurveTo(-r * 0.8 * scale, r * 0.9 * scale, -r * 1.45 * scale, r * 0.55 * scale - w3);
    ctx.quadraticCurveTo(0, r * 1.1 * scale - w2, r * 0.6 * scale, r * 0.95 * scale);
    ctx.quadraticCurveTo(r * 1.1 * scale, r * 0.7 * scale - w1, r * 1.25 * scale, 0);
    ctx.closePath();
  }

  function renderFireball(ctx, x, y, r, angle, t, seed, lifeRatio, alpha = 1.0) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.globalAlpha *= alpha;

    // Schicht 1: Dunkelroter Verbrennungsrand
    ctx.fillStyle = '#991b1b';
    drawFireballContour(ctx, r, t, seed, 1.0);
    ctx.fill();

    // Schicht 2: Feuriges Dunkelorange
    ctx.fillStyle = '#ea580c';
    drawFireballContour(ctx, r, t, seed + 1.0, 0.84);
    ctx.fill();

    // Schicht 3: Strahlendes Orange
    ctx.fillStyle = '#f97316';
    drawFireballContour(ctx, r, t, seed + 2.0, 0.68);
    ctx.fill();

    // Schicht 4: Heißes Flammengelb (in früher Lebensphase)
    if (lifeRatio < 0.8) {
      ctx.fillStyle = '#facc15';
      drawFireballContour(ctx, r, t, seed + 3.0, 0.48 * (1.0 - lifeRatio * 0.35));
      ctx.fill();
    }

    // Schicht 5: Glühender Weiß-Gelb-Plasmakern & Funke
    if (lifeRatio < 0.5) {
      ctx.fillStyle = '#fef08a';
      drawFireballContour(ctx, r, t, seed + 4.0, 0.28 * (1.0 - lifeRatio * 1.1));
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(Math.round(r * 0.2), -1, 3, 3);
    }

    ctx.restore();
  }

  function getDevilPatrolMuzzle(dp, t, forwardOffset = 0) {
    const bob = Math.sin(t * 2.8) * 1.3;
    const localBx = 25 + forwardOffset;
    const localBy = -2.5;

    const cosG = Math.cos(dp.gunAngle || 0);
    const sinG = Math.sin(dp.gunAngle || 0);
    const gx = 3;
    const gy = -26 + bob;
    const bodyX1 = gx + localBx * cosG - localBy * sinG;
    const bodyY1 = gy + localBx * sinG + localBy * cosG;

    const cosS = Math.cos(dp.stumbleTilt || 0);
    const sinS = Math.sin(dp.stumbleTilt || 0);
    const bodyX2 = bodyX1 * cosS - bodyY1 * sinS;
    const bodyY2 = bodyX1 * sinS + bodyY1 * cosS;

    const facing = dp.facing || 1;
    const worldX = dp.x + bodyX2 * facing;
    const worldY = dp.baseY + (dp.hopY || 0) + bodyY2;

    const totalAngle = (dp.gunAngle || 0) + (dp.stumbleTilt || 0);
    const dirX = facing * Math.cos(totalAngle);
    const dirY = Math.sin(totalAngle);

    return { x: worldX, y: worldY, dirX, dirY, angle: Math.atan2(dirY, dirX) };
  }

  let _devilTanksCache = null;

  function getBakedDevilTanks() {
    if (_devilTanksCache) return _devilTanksCache;
    if (typeof document === 'undefined' || !document.createElement) return null;
    const cvs = document.createElement('canvas');
    cvs.width = 20;
    cvs.height = 30;
    const cctx = cvs.getContext('2d');
    cctx.save();
    cctx.translate(20, 45);
    drawCylinder(cctx, -19, -40, 8, 22, true);
    drawCylinder(cctx, -13, -42, 8, 24, true);
    drawBeveledBox(cctx, -19, -42, 8, 3, '#78350f', '#fbbf24', '#451a03', 0.6);
    drawBeveledBox(cctx, -13, -44, 8, 3, '#78350f', '#fbbf24', '#451a03', 0.6);
    hazardStripes(cctx, -19, -30, 14, 3, 3);
    p(cctx, -16, -39, '#f97316');
    p(cctx, -10, -41, '#22c55e');
    cctx.restore();
    _devilTanksCache = cvs;
    return cvs;
  }

  function renderDevilPatrol(ctx, dt) {
    const dp = fx.devilPatrol;
    if (!dp) return;
    const t = fx.time;

    // --- 1. VERHALTENS-ZUSTANDSAUTOMAT & ANIMATIONSDYNAMIK ---
    dp.timer -= dt;
    if (dp.panicTimer > 0) dp.panicTimer = Math.max(0, dp.panicTimer - dt);

    if (dp.state === 'curious_waddle') {
      dp.walkWeight = Math.min(1.0, (dp.walkWeight || 0) + dt * 6.0);
    } else {
      dp.walkWeight = Math.max(0, (dp.walkWeight || 0) - dt * 5.0);
    }

    switch (dp.state) {
      case 'idle_fidget': {
        dp.hopY += (0 - dp.hopY) * Math.min(1.0, dt * 6.0);
        dp.stumbleTilt += (0 - dp.stumbleTilt) * Math.min(1.0, dt * 5.0);
        dp.headTilt += (Math.sin(t * 2.0) * 0.12 - dp.headTilt) * Math.min(1.0, dt * 4.0);
        dp.headAngle += (0 - dp.headAngle) * Math.min(1.0, dt * 4.0);
        dp.gunAngle += (-0.05 + Math.sin(t * 1.5) * 0.04 - dp.gunAngle) * Math.min(1.0, dt * 4.0);

        // Gelegentliche Mikro-Funken direkt aus der Zündflamme der Mündung
        if (Math.random() < 0.25) {
          const muz = getDevilPatrolMuzzle(dp, t, 2);
          dp.embers.push({
            x: muz.x + (Math.random() - 0.5) * 3,
            y: muz.y + (Math.random() - 0.5) * 3,
            vx: muz.dirX * (5 + Math.random() * 6),
            vy: muz.dirY * (5 + Math.random() * 6) - 8 - Math.random() * 8,
            alpha: 0.9,
            life: 0,
            maxLife: 0.7 + Math.random() * 0.5
          });
        }

        if (dp.timer <= 0) {
          const r = Math.random();
          if (r < 0.40) {
            dp.state = 'playful_puff';
            dp.timer = 2.4;
          } else if (r < 0.75) {
            dp.state = 'curious_waddle';
            dp.timer = 3.5 + Math.random() * 2.0;
          } else if (r < 0.93) {
            dp.state = 'clumsy_stumble';
            dp.timer = 2.2;
          } else {
            triggerDevilPatrolFire();
          }
        }
        break;
      }

      case 'playful_puff': {
        const prog = (2.4 - dp.timer) / 2.4;

        if (prog < 0.2) {
          // Vorspannung: Geht in die Hocke & Flammenwerfer leicht nach oben
          dp.hopY += (2.5 - dp.hopY) * Math.min(1.0, dt * 8.0);
          dp.gunAngle += (-0.28 - dp.gunAngle) * Math.min(1.0, dt * 8.0);
          dp.headTilt += (-0.18 - dp.headTilt) * Math.min(1.0, dt * 8.0);
        } else if (prog < 0.45) {
          // Hüpfer & Flammen-Puff 1 (Kaskadierendes Feuerzungen-Bündel exakt an der Mündung)
          dp.hopY += (-3.5 - dp.hopY) * Math.min(1.0, dt * 12.0);
          if (!dp._puff1Done) {
            dp._puff1Done = true;
            dp.muzzleFlame = 1.0;
            const muz = getDevilPatrolMuzzle(dp, t, 0);
            const speed = 46;
            dp.flamePuffs.push(
              { x: muz.x + muz.dirX * 10, y: muz.y + muz.dirY * 10, vx: muz.dirX * speed * 1.15, vy: muz.dirY * speed * 1.15, r: 4.5, maxR: 15.0, life: 0, maxLife: 0.70, seed: Math.random() * 10 },
              { x: muz.x + muz.dirX * 5,  y: muz.y + muz.dirY * 5,  vx: muz.dirX * speed * 0.95, vy: muz.dirY * speed * 0.95, r: 3.5, maxR: 11.5, life: 0, maxLife: 0.58, seed: Math.random() * 10 },
              { x: muz.x,                 y: muz.y,                 vx: muz.dirX * speed * 0.75, vy: muz.dirY * speed * 0.75, r: 2.5, maxR: 8.0,  life: 0, maxLife: 0.45, seed: Math.random() * 10 }
            );
            for (let i = 0; i < 7; i++) {
              dp.embers.push({
                x: muz.x + (Math.random() - 0.5) * 4,
                y: muz.y + (Math.random() - 0.5) * 4,
                vx: muz.dirX * (22 + Math.random() * 26) + (Math.random() - 0.5) * 8,
                vy: muz.dirY * (22 + Math.random() * 26) + (Math.random() - 0.5) * 8,
                alpha: 1.0, life: 0, maxLife: 0.8 + Math.random() * 0.4
              });
            }
          }
        } else if (prog < 0.65) {
          // Zweiter kleiner Nachpuff (Doppel-Tap)
          if (!dp._puff2Done) {
            dp._puff2Done = true;
            dp.muzzleFlame = 0.85;
            const muz = getDevilPatrolMuzzle(dp, t, 0);
            const speed = 36;
            dp.flamePuffs.push({
              x: muz.x + muz.dirX * 4,
              y: muz.y + muz.dirY * 4,
              vx: muz.dirX * speed,
              vy: muz.dirY * speed,
              r: 3.0, maxR: 9.5, life: 0, maxLife: 0.52,
              seed: Math.random() * 10
            });
            for (let i = 0; i < 4; i++) {
              dp.embers.push({
                x: muz.x + (Math.random() - 0.5) * 3,
                y: muz.y + (Math.random() - 0.5) * 3,
                vx: muz.dirX * (18 + Math.random() * 16) + (Math.random() - 0.5) * 6,
                vy: muz.dirY * (18 + Math.random() * 16) + (Math.random() - 0.5) * 6,
                alpha: 1.0, life: 0, maxLife: 0.6 + Math.random() * 0.3
              });
            }
          }
        } else {
          // Zurücksetzen in Ausgangsposition & stolzes Kopfnicken
          dp.hopY += (0 - dp.hopY) * Math.min(1.0, dt * 5.0);
          dp.gunAngle += (-0.05 - dp.gunAngle) * Math.min(1.0, dt * 4.0);
          dp.headTilt += (0.1 - dp.headTilt) * Math.min(1.0, dt * 4.0);
        }

        if (dp.timer <= 0) {
          dp._puff1Done = false;
          dp._puff2Done = false;
          dp.state = 'idle_fidget';
          dp.timer = 3.0 + Math.random() * 1.5;
        }
        break;
      }

      case 'curious_waddle': {
        dp.x += dp.facing * 14 * dt;
        if (dp.x > 515) { dp.facing = -1; dp.x = 515; }
        if (dp.x < 415) { dp.facing = 1; dp.x = 415; }

        dp.walkPhase += dt * 6.5;
        dp.stumbleTilt = Math.sin(dp.walkPhase) * 0.08;
        dp.hopY = -Math.abs(Math.sin(dp.walkPhase)) * 2.2;
        dp.headTilt = Math.sin(dp.walkPhase * 0.5) * 0.1;
        dp.gunAngle = -0.05 + Math.sin(dp.walkPhase) * 0.05;

        if (dp.timer <= 0) {
          dp.state = (Math.random() < 0.35) ? 'clumsy_stumble' : 'idle_fidget';
          dp.timer = dp.state === 'clumsy_stumble' ? 2.2 : (3.0 + Math.random() * 2.0);
        }
        break;
      }

      case 'clumsy_stumble': {
        const prog = (2.2 - dp.timer) / 2.2;
        if (prog < 0.35) {
          // Stolpert nach vorn über eine Bodenfuge
          dp.stumbleTilt += (0.34 - dp.stumbleTilt) * Math.min(1.0, dt * 10.0);
          dp.gunAngle += (0.38 - dp.gunAngle) * Math.min(1.0, dt * 8.0);
          dp.hopY += (1.5 - dp.hopY) * Math.min(1.0, dt * 8.0);
          dp.x += dp.facing * 10 * dt;
        } else if (prog < 0.65) {
          // Fängt sich mit den Armen/Waffe ab
          dp.stumbleTilt += (-0.12 - dp.stumbleTilt) * Math.min(1.0, dt * 8.0);
          dp.gunAngle += (-0.15 - dp.gunAngle) * Math.min(1.0, dt * 8.0);
          dp.hopY += (-2.0 - dp.hopY) * Math.min(1.0, dt * 8.0);
        } else {
          // Ertappt: Blickt verlegen nach links und rechts
          dp.stumbleTilt += (0 - dp.stumbleTilt) * Math.min(1.0, dt * 6.0);
          dp.hopY += (0 - dp.hopY) * Math.min(1.0, dt * 6.0);
          dp.headAngle += ((prog < 0.85 ? -0.35 : 0.35) - dp.headAngle) * Math.min(1.0, dt * 5.0);
        }

        if (dp.timer <= 0) {
          dp.state = 'idle_fidget';
          dp.timer = 3.5;
        }
        break;
      }

      case 'accidental_fire': {
        const tElapsed = 7.5 - dp.timer;

        // Phase 1: Misfire (0..0.7s) - Waffe zuckt nach unten
        if (tElapsed < 0.7) {
          dp.gunAngle += (0.46 - dp.gunAngle) * Math.min(1.0, dt * 8.0);
          dp.headTilt += (0.2 - dp.headTilt) * Math.min(1.0, dt * 6.0);
          if (!dp.fireActive && tElapsed > 0.45) {
            dp.fireActive = true;
            dp.fireX = dp.x + 18 * dp.facing;
            dp.fireIntensity = 0.3;
            dp.panicTimer = 1.6;
            dp.muzzleFlame = 1.0;
            const muz = getDevilPatrolMuzzle(dp, t, 0);
            const speed = 32;
            dp.flamePuffs.push({
              x: muz.x, y: muz.y,
              vx: muz.dirX * speed,
              vy: muz.dirY * speed,
              r: 3.5, maxR: 12.0, life: 0, maxLife: 0.45,
              seed: Math.random() * 10
            });
          }
        }
        // Phase 2: Deck-Feuer bricht aus & Panik-Zappeln (0.7..1.8s)
        else if (tElapsed < 1.8) {
          dp.fireIntensity = Math.min(1.0, dp.fireIntensity + dt * 1.5);
          dp.panicTimer = 1.0;
          dp.headTilt += (-0.35 - dp.headTilt) * Math.min(1.0, dt * 10.0);
          dp.gunAngle += (-0.45 - dp.gunAngle) * Math.min(1.0, dt * 10.0);
          dp.hopY += (Math.sin(t * 36) * 1.8 - dp.hopY) * Math.min(1.0, dt * 12.0);

          if (Math.random() < 0.4) {
            dp.embers.push({
              x: dp.fireX + (Math.random() - 0.5) * 8,
              y: dp.baseY - 4,
              vx: (Math.random() - 0.5) * 16,
              vy: -20 - Math.random() * 16,
              alpha: 1.0, life: 0, maxLife: 0.6 + Math.random() * 0.4
            });
          }
        }
        // Phase 3: Energisches Austreten mit dem schweren Stiefel (1.8..4.8s)
        else if (tElapsed < 4.8) {
          dp.stumbleTilt += (0.18 - dp.stumbleTilt) * Math.min(1.0, dt * 6.0);
          dp.gunAngle += (-0.15 - dp.gunAngle) * Math.min(1.0, dt * 6.0);
          dp.headTilt += (0.35 - dp.headTilt) * Math.min(1.0, dt * 6.0);

          const stompTime = tElapsed - 1.8;
          const currentStomp = Math.floor(stompTime / 0.7) + 1;
          const stompFrac = (stompTime % 0.7) / 0.7;

          if (currentStomp !== dp.stompCount && currentStomp <= 4) {
            dp.stompCount = currentStomp;
            // Tritt-Einschlag: Feuerdichte nimmt ab
            dp.fireIntensity = Math.max(0, dp.fireIntensity - 0.28);
            // Stiefel-Funken sprühen zur Seite
            for (let s = 0; s < 7; s++) {
              dp.embers.push({
                x: dp.fireX,
                y: dp.baseY,
                vx: (Math.random() - 0.5) * 44,
                vy: -10 - Math.random() * 18,
                alpha: 1.0, life: 0, maxLife: 0.4 + Math.random() * 0.3
              });
            }
            // Zischende Rauchwolke
            dp.smokeWisps.push({
              x: dp.fireX, y: dp.baseY - 4,
              vx: (Math.random() - 0.5) * 6, vy: -12 - Math.random() * 8,
              r: 3, maxR: 9, alpha: 0.8, life: 0, maxLife: 1.0
            });
          }

          if (stompFrac < 0.5) {
            dp.stompY = -12 * Math.sin(stompFrac * Math.PI);
          } else {
            dp.stompY = 0;
          }
        }
        // Phase 4: Stiefel anheben, Sohle prüfen & Erleichtertes Aufatmen (4.8..6.5s)
        else if (tElapsed < 6.5) {
          dp.stompY = 0;
          if (dp.fireActive) {
            dp.fireActive = false;
            dp.fireIntensity = 0;
            dp.scorches.push({ x: dp.fireX, w: 18, alpha: 1.0 });
          }

          if (tElapsed < 5.6) {
            // Hebt Fuß an und blickt neugierig unter die Sohle
            dp.headTilt += (0.42 - dp.headTilt) * Math.min(1.0, dt * 6.0);
            dp.stompY = -4;
          } else {
            // Erleichterter Seufzer, Schultern sinken ab
            dp.stompY = 0;
            dp.headTilt += (-0.15 - dp.headTilt) * Math.min(1.0, dt * 5.0);
            dp.hopY += (2.8 - dp.hopY) * Math.min(1.0, dt * 6.0);
          }
        }
        // Phase 5: Neutraler Übergang (6.5..7.5s)
        else {
          dp.hopY += (0 - dp.hopY) * Math.min(1.0, dt * 4.0);
          dp.headTilt += (0 - dp.headTilt) * Math.min(1.0, dt * 4.0);
          dp.stumbleTilt += (0 - dp.stumbleTilt) * Math.min(1.0, dt * 4.0);
        }

        if (dp.timer <= 0) {
          dp.state = 'idle_fidget';
          dp.timer = 4.0;
          dp.stompCount = 0;
        }
        break;
      }
    }

    // --- 2. PHYSIK- & PARTIKEL-UPDATES ---
    // Mündungsflammen-Abklingen
    if (dp.muzzleFlame > 0) {
      dp.muzzleFlame = Math.max(0, dp.muzzleFlame - dt * 3.5);
    }

    // Flammen-Puffs (mit aerodynamischem Widerstand, thermischem Auftrieb & Rauch-Übergang)
    for (let i = dp.flamePuffs.length - 1; i >= 0; i--) {
      const pObj = dp.flamePuffs[i];
      pObj.life += dt;
      pObj.x += pObj.vx * dt;
      pObj.y += pObj.vy * dt;
      pObj.vx *= Math.max(0, 1.0 - dt * 0.9);
      pObj.vy *= Math.max(0, 1.0 - dt * 0.9);
      pObj.vy -= 4 * dt;
      const lifeRatio = pObj.life / pObj.maxLife;
      pObj.r = pObj.maxR * (0.35 + 0.65 * Math.sin(lifeRatio * Math.PI * 0.5));
      if (pObj.life >= pObj.maxLife) {
        dp.smokeWisps.push({
          x: pObj.x,
          y: pObj.y,
          vx: pObj.vx * 0.25 + (Math.random() - 0.5) * 4,
          vy: -8 - Math.random() * 8,
          r: pObj.r * 0.7,
          maxR: pObj.r * 1.5,
          alpha: 0.6,
          life: 0,
          maxLife: 0.9 + Math.random() * 0.4
        });
        dp.flamePuffs.splice(i, 1);
      }
    }

    // Funken & Embers
    for (let i = dp.embers.length - 1; i >= 0; i--) {
      const em = dp.embers[i];
      em.life += dt;
      em.x += em.vx * dt;
      em.y += em.vy * dt;
      em.vy += -6 * dt;
      em.alpha = Math.max(0, 1.0 - em.life / em.maxLife);
      if (em.life >= em.maxLife || em.alpha <= 0) dp.embers.splice(i, 1);
    }

    // Rauch-Wölkchen
    for (let i = dp.smokeWisps.length - 1; i >= 0; i--) {
      const sw = dp.smokeWisps[i];
      sw.life += dt;
      sw.x += sw.vx * dt;
      sw.y += sw.vy * dt;
      sw.r = 3.0 + (sw.maxR - 3.0) * (sw.life / sw.maxLife);
      sw.alpha = Math.max(0, 0.8 * (1.0 - sw.life / sw.maxLife));
      if (sw.life >= sw.maxLife || sw.alpha <= 0) dp.smokeWisps.splice(i, 1);
    }

    // Brandflecken kühlen über 18 Sekunden langsam ab
    for (let i = dp.scorches.length - 1; i >= 0; i--) {
      const sc = dp.scorches[i];
      sc.alpha -= dt / 18.0;
      if (sc.alpha <= 0) dp.scorches.splice(i, 1);
    }

    // --- 3. RENDERING ---
    ctx.save();
    const x = dp.x;
    const baseY = dp.baseY;

    // Multi-Tier Bodenschatten
    ctx.fillStyle = 'rgba(2, 6, 14, 0.78)';
    ctx.beginPath();
    ctx.ellipse(x, baseY + 1, 26, 7, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(2, 6, 14, 0.45)';
    ctx.beginPath();
    ctx.ellipse(x - 2 * dp.facing, baseY + 1, 32, 9, 0, 0, Math.PI * 2);
    ctx.fill();

    // Brandflecken auf dem Hangar-Deck
    if (dp.scorches && dp.scorches.length > 0) {
      dp.scorches.forEach(sc => {
        ctx.fillStyle = `rgba(8, 7, 6, ${sc.alpha * 0.9})`;
        ctx.beginPath();
        ctx.ellipse(sc.x, baseY + 0.5, sc.w, sc.w * 0.38, 0, 0, Math.PI * 2);
        ctx.fill();
        p(ctx, sc.x - 4, baseY, '#f97316');
        p(ctx, sc.x + 3, baseY + 1, '#ea580c');
        p(ctx, sc.x - 1, baseY - 1, '#facc15');
        p(ctx, sc.x + 6, baseY, '#78350f');
      });
    }

    // Brennendes Deck-Feuer
    if (dp.fireActive && dp.fireIntensity > 0.05) {
      const fxX = dp.fireX;
      const fi = dp.fireIntensity;
      const fPulse = Math.sin(t * 18);

      glow(ctx, fxX, baseY - 6, 24 * fi, 'rgba(249, 115, 22, 0.55)', 0.75);
      glow(ctx, fxX, baseY - 12, 14 * fi, 'rgba(251, 191, 36, 0.4)', 0.6);

      // Äußere lodernde Flammenzungen
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(fxX - 12 * fi, baseY);
      ctx.lineTo(fxX - 6 * fi, baseY - (14 + fPulse * 4) * fi);
      ctx.lineTo(fxX - 1 * fi, baseY - (20 + Math.sin(t * 26) * 5) * fi);
      ctx.lineTo(fxX + 6 * fi, baseY - (15 - fPulse * 3) * fi);
      ctx.lineTo(fxX + 12 * fi, baseY);
      ctx.closePath();
      ctx.fill();

      // Mittlere orange Flamme
      ctx.fillStyle = '#f97316';
      ctx.beginPath();
      ctx.moveTo(fxX - 8 * fi, baseY);
      ctx.lineTo(fxX - 3 * fi, baseY - (11 + fPulse * 3) * fi);
      ctx.lineTo(fxX + 1 * fi, baseY - (15 + Math.sin(t * 22) * 4) * fi);
      ctx.lineTo(fxX + 5 * fi, baseY - (10 - fPulse * 2) * fi);
      ctx.lineTo(fxX + 8 * fi, baseY);
      ctx.closePath();
      ctx.fill();

      // Weiß-gelber heißer Reaktionskern
      ctx.fillStyle = '#fef08a';
      ctx.beginPath();
      ctx.moveTo(fxX - 4 * fi, baseY);
      ctx.lineTo(fxX - 1 * fi, baseY - 8 * fi);
      ctx.lineTo(fxX + 2 * fi, baseY - 10 * fi);
      ctx.lineTo(fxX + 4 * fi, baseY);
      ctx.closePath();
      ctx.fill();
      p(ctx, fxX, baseY - 6 * fi, '#ffffff');
      p(ctx, fxX + 1, baseY - 7 * fi, '#ffffff');
    }

    // Figur-Transformation (Position, Sprung, Ausrichtung, Stolper-Neigung)
    ctx.save();
    ctx.translate(x, baseY + dp.hopY);
    ctx.scale(dp.facing, 1);
    ctx.rotate(dp.stumbleTilt);

    const bob = Math.sin(t * 2.8) * 1.3;

    // Schwere feuerfeste Stiefel (Heavy Heat-Shield Boots) mit dynamischer Geh-Kinematik
    const ww = dp.walkWeight || 0;
    let stumbleL = 0;
    let stumbleR = 0;
    if (dp.state === 'clumsy_stumble') {
      const sProg = (2.2 - dp.timer) / 2.2;
      if (sProg < 0.35) {
        const sFrac = Math.sin((sProg / 0.35) * Math.PI);
        stumbleL = -4.0 * sFrac;
        stumbleR = 5.0 * sFrac;
      }
    }

    const strideLeft = (-Math.cos(dp.walkPhase) * 4.2 * ww) + stumbleL;
    const liftLeft = (-Math.max(0, Math.sin(dp.walkPhase)) * 3.4 * ww);

    const strideRight = (Math.cos(dp.walkPhase) * 4.2 * ww) + stumbleR;
    const liftRight = (-Math.max(0, -Math.sin(dp.walkPhase)) * 3.4 * ww);
    const stompOffset = dp.stompY || 0;

    // Hinterer linker Stiefel (vollwertige Anatomie mit Zehenkappe, Bronze-Schließe und Profil)
    drawBeveledBox(ctx, -14 + strideLeft, -6 + liftLeft, 12, 6, '#181615', '#3c3836', '#080b12', 0.8);
    line(ctx, -14 + strideLeft, -1 + liftLeft, -2 + strideLeft, -1 + liftLeft, '#090807', 1.5);
    p(ctx, -5 + Math.round(strideLeft), -3 + Math.round(liftLeft), '#92400e'); // Bronze-Verschluss hinten
    p(ctx, -12 + Math.round(strideLeft), -3 + Math.round(liftLeft), '#475569'); // Niet hinten

    // Vorderer rechter Stiefel (animiert beim Austreten & Laufen)
    drawBeveledBox(ctx, 2 + strideRight, -6 + liftRight + stompOffset, 12, 6, '#1c1917', '#44403c', '#090d16', 0.8);
    line(ctx, 2 + strideRight, -1 + liftRight + stompOffset, 14 + strideRight, -1 + liftRight + stompOffset, '#0c0a09', 1.5);
    p(ctx, 11 + Math.round(strideRight), -3 + Math.round(liftRight + stompOffset), '#b45309'); // Bronze-Verschluss
    p(ctx, 4 + Math.round(strideRight), -3 + Math.round(liftRight + stompOffset), '#64748b');

    // Pneumatisch verstärkte Beine (vollwertig beidseitig)
    drawBeveledBox(ctx, -12 + strideLeft * 0.5, -14 + liftLeft * 0.3, 9, 9, '#23201f', '#3c3836', '#151312', 0.8);
    drawBeveledBox(ctx, 3 + strideRight * 0.5, -14 + liftRight * 0.3 + stompOffset * 0.5, 9, 9, '#292524', '#44403c', '#1c1917', 0.8);
    // Bevelled Bronze Kniekappen (vollwertig beidseitig)
    drawBeveledBox(ctx, -11 + strideLeft * 0.55, -13 + liftLeft * 0.4, 8, 4, '#6b2f0d', '#d97706', '#3b1602', 0.8);
    drawBeveledBox(ctx, 4 + strideRight * 0.55, -13 + liftRight * 0.4 + stompOffset * 0.5, 8, 4, '#78350f', '#fbbf24', '#451a03', 0.8);

    // Hüftgurt & Thermoregler
    drawBeveledBox(ctx, -13, -20 + bob, 24, 6, '#1c1917', '#44403c', '#090d16', 1.0);
    rect(ctx, -2, -19 + bob, 5, 4, '#f97316');
    rect(ctx, -1, -18 + bob, 3, 2, '#fbbf24');
    p(ctx, 0, -17 + bob, '#ffffff');

    // Promethium Rucksack-Tanks
    const bakedTanks = getBakedDevilTanks();
    if (bakedTanks) {
      ctx.drawImage(bakedTanks, -20, -45 + bob);
    } else {
      // Linker Tank
      drawCylinder(ctx, -19, -40 + bob, 8, 22, true);
      // Rechter/Zentraler Tank
      drawCylinder(ctx, -13, -42 + bob, 8, 24, true);
      // Messing-Überdruckventile & Kappen
      drawBeveledBox(ctx, -19, -42 + bob, 8, 3, '#78350f', '#fbbf24', '#451a03', 0.6);
      drawBeveledBox(ctx, -13, -44 + bob, 8, 3, '#78350f', '#fbbf24', '#451a03', 0.6);
      hazardStripes(ctx, -19, -30 + bob, 14, 3, 3);
      p(ctx, -16, -39 + bob, '#f97316'); // Druck-LED
      p(ctx, -10, -41 + bob, '#22c55e'); // Füllstand-LED
    }

    // Druckschlauch zum Flammenwerfer
    ctx.strokeStyle = '#78350f';
    ctx.lineWidth = 2.8;
    ctx.beginPath();
    ctx.moveTo(-13, -22 + bob);
    ctx.quadraticCurveTo(-6, -11 + bob, 6, -24 + bob);
    ctx.stroke();
    ctx.strokeStyle = '#ea580c';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(-13, -22 + bob);
    ctx.quadraticCurveTo(-6, -11 + bob, 6, -24 + bob);
    ctx.stroke();

    // Torso / Vanguard Hitzeschutzpanzer
    drawBeveledBox(ctx, -12, -37 + bob, 22, 18, '#1e293b', '#64748b', '#0f172a', 1.5);
    // Vanguard Team-Emblem auf der Brust
    rect(ctx, -3, -33 + bob, 8, 8, '#0284c7');
    rect(ctx, -2, -32 + bob, 6, 6, '#38bdf8');
    line(ctx, -1, -30 + bob, 3, -30 + bob, '#ffffff', 1.2);
    hazardStripes(ctx, -10, -22 + bob, 18, 3, 3);

    // Schulterpanzer (Pauldrons)
    drawBeveledBox(ctx, -15, -39 + bob, 7, 9, '#334155', '#94a3b8', '#0f172a', 1.2);
    drawBeveledBox(ctx, 7, -39 + bob, 8, 9, '#334155', '#94a3b8', '#0f172a', 1.2);
    p(ctx, -13, -37 + bob, '#b45309');
    p(ctx, 11, -37 + bob, '#b45309');

    // Kragen & Hitzeschild-Nackenschutz
    drawBeveledBox(ctx, -9, -40 + bob, 18, 5, '#78350f', '#f59e0b', '#451a03', 1.0);

    // Kopf & Feuriges Visier (Schwenkbar mit headTilt & headAngle)
    ctx.save();
    ctx.translate(0, -40 + bob);
    ctx.rotate(dp.headTilt + dp.headAngle);

    // Ballistikhelm
    drawBeveledBox(ctx, -10, -14, 20, 14, '#292524', '#78350f', '#0c0a09', 1.5);
    rect(ctx, -6, -16, 12, 3, '#1c1917');
    p(ctx, -5, -15, '#b45309'); p(ctx, 4, -15, '#b45309');

    // Stirn-Sensormodul mit Nieten
    drawBeveledBox(ctx, 0, -15, 8, 4, '#44403c', '#94a3b8', '#1c1917', 0.6);
    p(ctx, 2, -14, '#00f0ff');

    // Atemfilter-Kanister seitlich mit Lüftungsschlitzen
    drawBeveledBox(ctx, -11, -7, 4, 7, '#1c1917', '#44403c', '#090d16', 0.6);
    drawBeveledBox(ctx, 8, -7, 4, 7, '#1c1917', '#44403c', '#090d16', 0.6);
    line(ctx, -10, -5, -8, -5, '#475569', 1);
    line(ctx, -10, -3, -8, -3, '#475569', 1);
    line(ctx, 9, -5, 11, -5, '#475569', 1);
    line(ctx, 9, -3, 11, -3, '#475569', 1);

    // Feuriges Visier (Charakteristisches Flammen-Visier)
    drawBeveledBox(ctx, -3, -11, 13, 8, '#0c0a09', '#78350f', '#000000', 1.0);
    rect(ctx, -2, -10, 11, 6, '#ea580c');
    const vf = Math.sin(t * 14);
    const vf2 = Math.sin(t * 22 + 1);
    rect(ctx, -1, -10, 6 + vf * 2, 5, '#f97316');
    rect(ctx, 0, -9, 4 + vf2 * 1.5, 3, '#facc15');
    p(ctx, 2 + Math.floor(vf), -8, '#ffffff');
    line(ctx, -2, -10, 8, -10, '#ffffff', 0.9, 0.8);

    if (dp.panicTimer > 0) {
      glow(ctx, 4, -7, 16, 'rgba(254, 240, 138, 0.95)', 0.95);
    } else {
      glow(ctx, 4, -7, 8, 'rgba(249, 115, 22, 0.55)', 0.65);
    }

    ctx.restore(); // Ende Kopf

    // "Perdition" Doppelrohr-Flammenwerfer
    ctx.save();
    ctx.translate(3, -26 + bob);
    ctx.rotate(dp.gunAngle);

    // Gehäuse & Tragegriff
    drawBeveledBox(ctx, -5, -4, 12, 9, '#1e293b', '#475569', '#090d16', 1.2);
    rect(ctx, -3, -6, 7, 2.5, '#334155');
    p(ctx, -2, -2, '#22c55e');

    // Gepanzerte Handschuhe
    drawBeveledBox(ctx, -7, -3, 5, 7, '#44403c', '#78350f', '#1c1917', 1.0);
    drawBeveledBox(ctx, 3, -2, 5, 6, '#44403c', '#78350f', '#1c1917', 1.0);

    // Doppelrohr-Läufe mit Anlassfarben (Heat Bluing)
    drawBeveledBox(ctx, 7, -5, 18, 4, '#334155', '#64748b', '#0f172a', 1.0);
    drawBeveledBox(ctx, 7, -1, 18, 4, '#334155', '#64748b', '#0f172a', 1.0);
    rect(ctx, 19, -5, 4, 4, '#4338ca'); // Indigo
    rect(ctx, 19, -1, 4, 4, '#4338ca');
    rect(ctx, 23, -5, 2, 4, '#cbd5e1'); // Chrom-Spitze
    rect(ctx, 23, -1, 2, 4, '#cbd5e1');

    // Kühlrippen-Lochblech
    for (let vx = 9; vx <= 17; vx += 3) {
      p(ctx, vx, -4, '#090d16');
      p(ctx, vx, -1, '#090d16');
    }

    // Unterrohr-Leitung & Zündflamme
    line(ctx, 6, 3, 22, 3, '#b45309', 1.5);
    p(ctx, 22, 3, '#f97316');

    // Flackernde Pilotflamme
    const pfLen = 3 + Math.sin(t * 30) * 2;
    rect(ctx, 25, -3, pfLen, 2.5, '#facc15');
    p(ctx, 25 + pfLen, -2, '#f97316');
    glow(ctx, 26, -2, 7, 'rgba(245, 158, 11, 0.75)', 0.8);

    // Mündungs-Ausstoßflamme (Fiery Ejection Cone direkt an den Läufen)
    if (dp.muzzleFlame > 0.05) {
      const mf = dp.muzzleFlame;
      const coneLen = 14 * mf;
      const coneSpread = 6 * mf;
      glow(ctx, 25 + coneLen * 0.5, -3, 16 * mf, 'rgba(249, 115, 22, 0.8)', 0.85);

      // Äußerer orange-roter Kegel
      ctx.fillStyle = '#ea580c';
      ctx.beginPath();
      ctx.moveTo(25, -6);
      ctx.lineTo(25 + coneLen, -3 - coneSpread);
      ctx.lineTo(25 + coneLen * 1.3, -3);
      ctx.lineTo(25 + coneLen, -3 + coneSpread);
      ctx.lineTo(25, 0);
      ctx.closePath();
      ctx.fill();

      // Innerer gelber Flammenkern
      ctx.fillStyle = '#fef08a';
      ctx.beginPath();
      ctx.moveTo(25, -5);
      ctx.lineTo(25 + coneLen * 0.7, -3 - coneSpread * 0.5);
      ctx.lineTo(25 + coneLen * 0.9, -3);
      ctx.lineTo(25 + coneLen * 0.7, -3 + coneSpread * 0.5);
      ctx.lineTo(25, -1);
      ctx.closePath();
      ctx.fill();
      p(ctx, 25 + Math.floor(coneLen * 0.4), -3, '#ffffff');
    }

    ctx.restore(); // Ende Flammenwerfer

    ctx.restore(); // Ende Devil Patrol Figur

    // --- 4. FLAMMENPUFFS, FUNKEN & RAUCHZEICHEN ---
    if (dp.flamePuffs && dp.flamePuffs.length > 0) {
      dp.flamePuffs.forEach(fp => {
        const pAlpha = Math.max(0, 1.0 - (fp.life / fp.maxLife));
        const lifeRatio = fp.life / fp.maxLife;
        const angle = Math.atan2(fp.vy, fp.vx);

        // Volumetrischer Feuerschein
        glow(ctx, fp.x, fp.y, fp.r * 2.2, `rgba(234, 88, 12, ${pAlpha * 0.65})`, 0.85);
        if (lifeRatio < 0.6) {
          glow(ctx, fp.x, fp.y, fp.r * 1.3, `rgba(251, 191, 36, ${pAlpha * 0.5})`, 0.7);
        }

        // Organische mehrschichtige Flammenzungen-Geometrie
        renderFireball(ctx, fp.x, fp.y, fp.r, angle, t, fp.seed || 1.0, lifeRatio, pAlpha);
      });
    }

    if (dp.embers && dp.embers.length > 0) {
      dp.embers.forEach(em => {
        p(ctx, em.x, em.y, '#fef08a');
        p(ctx, em.x + 1, em.y, '#f97316');
      });
    }

    if (dp.smokeWisps && dp.smokeWisps.length > 0) {
      dp.smokeWisps.forEach(sw => {
        ctx.save();
        ctx.globalAlpha = sw.alpha || 0.5;
        ctx.fillStyle = '#64748b';
        ctx.beginPath();
        ctx.arc(sw.x, sw.y, sw.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
    }

    ctx.restore();
  }

  // --- 6a6c. VANGUARD XENOBIOLOGIE- & QUARANTÄNE-LABOR (BIO-SEC LEVEL 4) -----
  // Umfassende Forschungsstation in Bay 04 (X = 24..174):
  // 0. Rückwand-Containment & Luftschleusen-Trennwand (trennt Labor vollständig vom Hangar-Korridor X >= 176)
  // 1. Holographische Diagnose- & Analyse-Workstation (Reagenz-Rack, 3D-DNA-Hologramm, Live-Neuro-EKG)
  // 2. Leitender Vanguard Xenobiologe Dr. Vance (Hazmat-Anzug, Rebreather, HUD-Visier, Spektrometer-Laserstrahl, Telemetrie-Slate)
  // 3. Monumentale Primär-Stasiskammer "Specimen 07" (Titan-Sockel, 4 Erdungselektroden, aufsteigende Mikro-Bläschen)
  // 4. Gefangenes Sul Defective Loaf Alien (chitinöser Facettenpanzer, Goldrunen, 6 Tentakeln, Geweihhörner, Starburst-Auge, Bio-Blitze)
  // 5. Motorisierter Decken-Biometriescanner mit vertikalem Laser-Scanstrahl & Tracking-Laser
  // 6. Zweit-Kammer / Cryo-Inkubator "Specimen 07-B" mit rotierendem Sul-Sporenkern
  // 7. Bio-Containment Quarantine Sentry Marine (Sealed Combat Armor, Gasmaske, Amber-Visier, taktischer Sicherungsstrahl)
  // 8. Kältedampf-Entlüftung (periodischer Cryo-Venting-Dampf) & Boden-Kriechnebel
  function renderAlienResearchCell(ctx, dt) {
    ctx.save();
    const t = fx.time;

    // Basis-Deck-Höhe und Anker
    const deckY = 338;
    const labX0 = 24;
    const labX1 = 174;

    // -------------------------------------------------------------------------
    // 0. RÜCKWAND-CONTAINMENT, KABELLEITUNGEN & LUFTSCHLEUSEN-TRENNPFEILER
    // -------------------------------------------------------------------------
    // Dunkle Cleanroom-Schottwand
    const wallGrad = ctx.createLinearGradient(0, 248, 0, deckY);
    wallGrad.addColorStop(0, '#090e17');
    wallGrad.addColorStop(1, '#131e2b');
    ctx.fillStyle = wallGrad;
    ctx.fillRect(labX0, 248, labX1 - labX0, deckY - 248);

    // Vertikale Paneelfugen & Nieten
    for (let px = labX0 + 26; px < labX1 - 10; px += 26) {
      line(ctx, px, 248, px, deckY, '#1b2a3d', 1);
      p(ctx, px, 256, '#334155');
      p(ctx, px, 280, '#334155');
      p(ctx, px, 310, '#334155');
    }

    // Decken-Kabeltrassen
    line(ctx, labX0, 252, labX1, 252, '#334155', 2);
    line(ctx, labX0, 255, labX1, 255, '#1e293b', 1.5);

    // Verteilerdosen mit Status-LEDs
    drawBeveledBox(ctx, 48, 250, 6, 5, '#1e293b', '#475569', '#0b0f19', 0.5);
    p(ctx, 50, 252, (Math.sin(t * 4) > 0) ? '#22c55e' : '#15803d');
    drawBeveledBox(ctx, 138, 250, 6, 5, '#1e293b', '#475569', '#0b0f19', 0.5);
    p(ctx, 140, 252, (Math.sin(t * 3 + 1) > 0) ? '#00f0ff' : '#0284c7');

    // Biohazard Level 4 Warnschild an der Rückwand
    drawBeveledBox(ctx, 28, 264, 15, 9, '#ca8a04', '#fef08a', '#713f12', 0.8);
    p(ctx, 31, 267, '#020617');
    p(ctx, 33, 267, '#020617');
    p(ctx, 35, 267, '#020617');
    p(ctx, 40, 267, '#dc2626');

    // Rechter Quarantäne-Trennpfeiler (Schott-Airlock bei X = 171..174)
    drawBeveledBox(ctx, 171, 246, 4, deckY - 246, '#1e293b', '#475569', '#090d16', 1);
    hazardStripes(ctx, 171, 272, 4, 22, 2);
    // Quarantäne-Freigabelampe am Trennpfeiler
    const gatePulse = Math.sin(t * 2.5) > 0;
    p(ctx, 173, 248, gatePulse ? '#eab308' : '#713f12');
    if (gatePulse) glow(ctx, 173, 248, 4, 'rgba(234, 179, 8, 0.65)', 0.6);

    // -------------------------------------------------------------------------
    // 1. SICHERHEITS-PERIMETER, BODEN-HAZARD-MARKIERUNGEN & VERLÄNGERTER SOCKEL
    // -------------------------------------------------------------------------
    hazardStripes(ctx, labX0, deckY, labX1 - labX0, 3, 4);
    const pPulse = (Math.sin(t * 3.5) + 1) * 0.5;
    const pColor = pPulse > 0.4 ? '#ef4444' : '#7f1d1d';
    p(ctx, labX0 + 1, deckY + 1, pColor);
    p(ctx, labX1 - 2, deckY + 1, pColor);
    if (pPulse > 0.4) {
      glow(ctx, labX0 + 1, deckY + 1, 4, 'rgba(239, 68, 68, 0.65)', 0.6);
      glow(ctx, labX1 - 2, deckY + 1, 4, 'rgba(239, 68, 68, 0.65)', 0.6);
    }

    // Massiver verlängerter Plattform-Sockel / Fundament-Schott (deckt die Räder des Rovers vollständig ab)
    const baseBottomY = 356;
    const baseH = baseBottomY - deckY;
    drawBeveledBox(ctx, labX0, deckY + 3, labX1 - labX0, baseH - 3, '#1e293b', '#334155', '#090d16', 1.5);
    rect(ctx, labX0 + 2, deckY + 4, labX1 - labX0 - 4, baseH - 6, '#0f172a');

    // Vertikale Stahl-Versteifungsträger & Montageplatten
    const girderX = [36, 64, 94, 124, 154];
    for (const gx of girderX) {
      drawBeveledBox(ctx, gx - 2, deckY + 3, 4, baseH - 3, '#334155', '#64748b', '#090d16', 0.8);
      p(ctx, gx, deckY + 6, '#94a3b8');
      p(ctx, gx, deckY + 12, '#94a3b8');
    }

    // Untere Abschluss-Kantenleiste mit Hazard-Streifen
    line(ctx, labX0, baseBottomY - 1, labX1, baseBottomY - 1, '#475569', 1.2);
    hazardStripes(ctx, labX0, baseBottomY - 4, labX1 - labX0, 3, 3);

    // -------------------------------------------------------------------------
    // 2. HOLOGRAPHISCHE ANALYSE-WORKSTATION & REAGENZIEN-RACK (X = 32..54)
    // -------------------------------------------------------------------------
    const deskX = 32, deskY = deckY;
    // Laborpult-Sockel
    drawBeveledBox(ctx, deskX, deskY - 15, 22, 15, '#1e293b', '#475569', '#090d16', 1.2);
    // Duale angewinkelte Monitore
    drawBeveledBox(ctx, deskX + 2, deskY - 14, 8, 6, '#0f172a', '#38bdf8', '#020617', 0.5);
    rect(ctx, deskX + 3, deskY - 13, 6, 4, '#0369a1');
    p(ctx, deskX + 4 + Math.floor((t * 3) % 4), deskY - 11, '#38bdf8');
    // Reagenz-Rack mit 4 leuchtenden Proberöhrchen
    drawBeveledBox(ctx, deskX + 12, deskY - 7, 9, 6, '#334155', '#64748b', '#0f172a', 0.5);
    p(ctx, deskX + 13, deskY - 5, '#06b6d4');
    p(ctx, deskX + 15, deskY - 6, '#10b981');
    p(ctx, deskX + 17, deskY - 4, '#f59e0b');
    p(ctx, deskX + 19, deskY - 5, '#c026d3');

    // Holo-Projektor-Linse
    drawBeveledBox(ctx, deskX + 6, deskY - 17, 7, 2.5, '#0f172a', '#00f0ff', '#020617', 0.5);

    // Schwebendes 3D-Hologramm über dem Pult
    const holoX = deskX + 9.5, holoY = deskY - 29;
    ctx.save();
    // Hologramm-Projektionslichtkegel
    const holoCone = ctx.createLinearGradient(holoX, deskY - 17, holoX, holoY - 9);
    holoCone.addColorStop(0, 'rgba(0, 240, 255, 0.42)');
    holoCone.addColorStop(1, 'rgba(0, 240, 255, 0.04)');
    ctx.fillStyle = holoCone;
    ctx.beginPath();
    ctx.moveTo(deskX + 6, deskY - 16);
    ctx.lineTo(deskX + 13, deskY - 16);
    ctx.lineTo(holoX + 11, holoY - 9);
    ctx.lineTo(holoX - 11, holoY - 9);
    ctx.closePath();
    ctx.fill();

    // Live-Neuro/EKG-Oszilloskop-Kurve
    ctx.strokeStyle = '#00f0ff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let px = -9; px <= 9; px += 2) {
      const wave = Math.sin((px * 0.75) - t * 6.5) * 2.2;
      const spike = (Math.abs(px - ((t * 8) % 16 - 8)) < 2) ? Math.sin(t * 18) * 4.5 : 0;
      const py = holoY + wave + spike;
      if (px === -9) ctx.moveTo(holoX + px, py);
      else ctx.lineTo(holoX + px, py);
    }
    ctx.stroke();

    // Rotierende 3D-DNA-Doppelhelix mit leuchtenden Nukleotid-Knoten
    const rotPhase = t * 1.6;
    for (let step = -3; step <= 3; step++) {
      const hy = holoY - 8 + step * 2.2;
      const angle = rotPhase + step * 0.7;
      const nx1 = holoX + Math.cos(angle) * 5.5;
      const nx2 = holoX - Math.cos(angle) * 5.5;
      line(ctx, nx1, hy, nx2, hy, 'rgba(52, 211, 153, 0.4)', 0.8);
      p(ctx, nx1, hy, '#34d399');
      p(ctx, nx2, hy, '#38bdf8');
    }
    glow(ctx, holoX, holoY - 8, 9, 'rgba(52, 211, 153, 0.35)', 0.6);
    ctx.restore();

    // -------------------------------------------------------------------------
    // 3. ZEIT-STEUERUNG & NEURALER BLITZ (SUL DEFECTIVE LOAF)
    // -------------------------------------------------------------------------
    const neuralCycle = t % 4.5;
    const isNeuralFlashing = neuralCycle < 0.45;
    const neuralIntensity = isNeuralFlashing ? Math.sin((neuralCycle / 0.45) * Math.PI) : 0;
    const sciFlinch = isNeuralFlashing ? Math.sin((neuralCycle / 0.45) * Math.PI) * 1.8 : 0;

    // -------------------------------------------------------------------------
    // 4. LEITENDER VANGUARD XENOBIOLOGE DR. VANCE (X = 66, Y = deckY)
    // -------------------------------------------------------------------------
    const sciX = 66 - sciFlinch;
    const sciY = deckY;
    const sBreath = Math.sin(t * 2.2) * 0.5;

    // Forscher-Bodenschatten
    ctx.fillStyle = 'rgba(2, 6, 23, 0.55)';
    ctx.beginPath();
    ctx.ellipse(sciX, sciY + 1, 8, 3.5, 0, 0, Math.PI * 2);
    ctx.fill();

    // Beine & Magnetstiefel im Hazmat-Anzug
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(sciX - 4, sciY - 9, 3, 9);
    ctx.fillRect(sciX + 1, sciY - 9, 3, 9);
    p(ctx, sciX - 4, sciY - 1, '#d97706'); // Verstärkte Kappen
    p(ctx, sciX + 1, sciY - 1, '#d97706');
    line(ctx, sciX - 4, sciY - 5, sciX - 1, sciY - 5, '#64748b', 1); // Kniegelenke
    line(ctx, sciX + 1, sciY - 5, sciX + 4, sciY - 5, '#64748b', 1);

    // Torso (Schwerer weißer Schutzanzug mit Vanguard-Insignien & Amber-Schulterteilen)
    const sTorsoY = sciY - 20 + sBreath;
    drawBeveledBox(ctx, sciX - 5, sTorsoY, 10, 11, '#f8fafc', '#ffffff', '#94a3b8', 1.2);
    p(ctx, sciX - 1, sTorsoY + 3, '#0284c7'); // Vanguard-Logo
    // Bernsteinfarbene Schutz-Schulterpanzer
    drawBeveledBox(ctx, sciX - 6, sTorsoY - 1, 3, 4, '#d97706', '#fbbf24', '#92400e', 0.5);
    drawBeveledBox(ctx, sciX + 3, sTorsoY - 1, 3, 4, '#d97706', '#fbbf24', '#92400e', 0.5);
    // Brust-Lebenserhaltungs-Telemetry
    rect(ctx, sciX - 2, sTorsoY + 5, 4, 3, '#1e293b');
    p(ctx, sciX - 1, sTorsoY + 6, (Math.sin(t * 3.5) > 0) ? '#22c55e' : '#15803d');
    p(ctx, sciX + 1, sTorsoY + 6, '#00f0ff');

    // Lebenserhaltungs-Rebreather-Pack auf dem Rücken
    drawBeveledBox(ctx, sciX - 9, sTorsoY + 1, 4, 9, '#334155', '#64748b', '#0f172a', 0.8);
    rect(ctx, sciX - 8, sTorsoY + 2, 2, 7, '#0284c7');
    p(ctx, sciX - 7, sTorsoY + 3, '#38bdf8');
    // Flexible Atemschlauch-Verbindung zum Helm
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(sciX - 7, sTorsoY + 1);
    ctx.quadraticCurveTo(sciX - 4, sTorsoY - 3, sciX - 2, sTorsoY - 5);
    ctx.stroke();

    // Helm mit sphärischer Glaskuppel & reflektierendem HUD-Visier
    const sHeadY = sTorsoY - 9;
    drawBeveledBox(ctx, sciX - 4.5, sHeadY, 9, 9, '#e2e8f0', '#ffffff', '#64748b', 1.2);
    // Großes Panoramavisier blickt nach rechts zur Stasiskammer
    drawBeveledBox(ctx, sciX - 1, sHeadY + 2, 6, 4.5, '#0e7490', '#38bdf8', '#083344', 0.6);
    rect(ctx, sciX, sHeadY + 3, 4, 2.5, '#06b6d4');
    p(ctx, sciX + 2, sHeadY + 3, '#fef08a'); // Goldene Reflexion
    p(ctx, sciX + 3, sHeadY + 3, '#ffffff'); // Glanzpunkt

    // Linker Arm mit interaktivem Telemetrie-Datapad (reagiert auf Entladung)
    const slateFlinch = sciFlinch * 2;
    line(ctx, sciX - 3, sTorsoY + 3, sciX - 5, sTorsoY + 6 - slateFlinch, '#cbd5e1', 2);
    line(ctx, sciX - 5, sTorsoY + 6 - slateFlinch, sciX - 3, sTorsoY + 7 - slateFlinch, '#cbd5e1', 2);
    drawBeveledBox(ctx, sciX - 5, sTorsoY + 5 - slateFlinch, 6, 7, 'rgba(15, 23, 42, 0.85)', '#38bdf8', '#020617', 0.5);
    p(ctx, sciX - 4, sTorsoY + 6 - slateFlinch, '#22c55e');
    p(ctx, sciX - 2, sTorsoY + 6 - slateFlinch, '#38bdf8');
    p(ctx, sciX - 4, sTorsoY + 8 - slateFlinch, '#eab308');
    p(ctx, sciX - 2, sTorsoY + 8 - slateFlinch, '#22c55e');

    // Rechter Arm mit Hand-Spektrometer (strahlt Cyan-Abtastlaser in die Kammer)
    const tankX = 114;
    const tankW = 42;
    const tankH = 72;
    const tankTopY = deckY - tankH;
    const cylX = tankX - tankW * 0.5;

    line(ctx, sciX + 3, sTorsoY + 3, sciX + 8, sTorsoY + 5, '#cbd5e1', 2.5);
    drawBeveledBox(ctx, sciX + 8, sTorsoY + 3, 5, 3.5, '#0f172a', '#475569', '#020617', 0.6);
    line(ctx, sciX + 13, sTorsoY + 4.5, sciX + 15, sTorsoY + 4.5, '#0284c7', 1.5);
    // Aktiver Spektrometer-Laserstrahl
    ctx.save();
    const beamAlpha = 0.5 + Math.sin(t * 12) * 0.25;
    ctx.strokeStyle = `rgba(0, 240, 255, ${beamAlpha})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(sciX + 15, sTorsoY + 4.5);
    ctx.lineTo(cylX + 2, sTorsoY + 4.5);
    ctx.stroke();
    p(ctx, cylX + 2, sTorsoY + 4.5, '#ffffff');
    glow(ctx, cylX + 2, sTorsoY + 4.5, 4, 'rgba(0, 240, 255, 0.8)', 0.7);
    ctx.restore();

    // -------------------------------------------------------------------------
    // 5. MONUMENTALE PRIMÄR-STASISKAMMER "SPECIMEN 07" (X = 93..135)
    // -------------------------------------------------------------------------
    // Bodenschatten
    ctx.fillStyle = 'rgba(2, 6, 23, 0.65)';
    ctx.beginPath();
    ctx.ellipse(tankX, deckY + 1, 26, 7, 0, 0, Math.PI * 2);
    ctx.fill();

    // Massiver Titan-Sockel
    drawBeveledBox(ctx, cylX - 5, deckY - 11, tankW + 10, 11, '#1e293b', '#64748b', '#090d16', 2.0);
    hazardStripes(ctx, cylX - 3, deckY - 9, tankW + 6, 3, 3);
    // Hydraulische Arretierklauen
    drawBeveledBox(ctx, cylX - 4, deckY - 13, 5, 6, '#334155', '#94a3b8', '#0f172a', 0.8);
    drawBeveledBox(ctx, cylX + tankW - 1, deckY - 13, 5, 6, '#334155', '#94a3b8', '#0f172a', 0.8);
    // Sockel-Statusdisplay & Tasten
    drawBeveledBox(ctx, tankX - 8, deckY - 5, 16, 4, '#0f172a', '#38bdf8', '#020617', 0.5);
    p(ctx, tankX - 5, deckY - 3, '#22c55e');
    p(ctx, tankX - 2, deckY - 3, '#22c55e');
    p(ctx, tankX + 1, deckY - 3, '#38bdf8');
    p(ctx, tankX + 4, deckY - 3, '#eab308');

    // Transparenter Quarzglaszylinder mit leuchtendem Stasisfluid
    const glassY = tankTopY + 11;
    const glassH = tankH - 22;
    const fluidGrad = ctx.createLinearGradient(cylX, glassY, cylX + tankW, glassY + glassH);
    fluidGrad.addColorStop(0.0, 'rgba(4, 47, 46, 0.85)');
    fluidGrad.addColorStop(0.3, 'rgba(5, 150, 105, 0.68)');
    fluidGrad.addColorStop(0.7, 'rgba(13, 148, 136, 0.58)');
    fluidGrad.addColorStop(1.0, 'rgba(4, 120, 87, 0.90)');
    ctx.fillStyle = fluidGrad;
    ctx.fillRect(cylX + 2, glassY, tankW - 4, glassH);

    // Stasis-Fluid Raum-Glow
    const stasisPulse = 0.5 + Math.sin(t * 2.0) * 0.25;
    glow(ctx, tankX, glassY + glassH * 0.5, 32, `rgba(13, 148, 136, ${0.20 + stasisPulse * 0.18})`, 0.85);

    // 4 Interne Kupfer-Erdungselektroden
    line(ctx, cylX + 5, glassY, cylX + 5, glassY + glassH, '#b45309', 1.2);
    line(ctx, cylX + 9, glassY, cylX + 9, glassY + glassH, '#334155', 1);
    line(ctx, cylX + tankW - 9, glassY, cylX + tankW - 9, glassY + glassH, '#334155', 1);
    line(ctx, cylX + tankW - 5, glassY, cylX + tankW - 5, glassY + glassH, '#b45309', 1.2);

    // 12 Aufsteigende Mikro-Bläschen
    for (let b = 0; b < 12; b++) {
      const bSpeed = 6 + (b % 4) * 4;
      const bPhase = (t * bSpeed + b * 14.7) % glassH;
      const by = (glassY + glassH) - bPhase;
      const bx = tankX - (tankW * 0.38) + (b * 2.6) + Math.sin(t * 3.0 + b * 1.7) * 2;
      p(ctx, bx, by, '#a7f3d0');
      if (b % 3 === 0) p(ctx, bx, by - 1, 'rgba(255, 255, 255, 0.7)');
    }

    // -------------------------------------------------------------------------
    // 6. GEFANGENER ORGANISMUS: SUL DEFECTIVE LOAF ALIEN (X = 114)
    // -------------------------------------------------------------------------
    const hoverY = Math.sin(t * 1.5) * 2.8;
    const loafAngle = Math.sin(t * 0.8) * 0.04;
    const bioScale = 1.0 + Math.sin(t * 2.0) * 0.035;
    const loafX = tankX;
    const loafY = glassY + glassH * 0.52 + hoverY;

    ctx.save();
    ctx.translate(loafX, loafY);
    ctx.rotate(loafAngle);
    ctx.scale(bioScale, bioScale);

    // Getreue Wiedergabe der 24x24 Ingame-Sprite-Matrix (DefectiveLoaf_Front from unitSprites.js)
    // mit dornigen Geweihen, Starburst-Auge, Kobaltpanzer und Brotlaib-Hieroglyphe 𓏏
    const S = 1.35;
    const ox = -11.5 * S;
    const oy = -10.0 * S;
    const bakedLoaf = getBakedAlienTankLoaf();
    if (bakedLoaf) {
      ctx.drawImage(bakedLoaf, ox, oy);
    } else {
      for (let i = 0; i < DEFECTIVE_LOAF_SPANS.length; i++) {
        const [col, spans] = DEFECTIVE_LOAF_SPANS[i];
        ctx.fillStyle = col;
        for (let j = 0; j < spans.length; j++) {
          const [sx, sy, sw, sh] = spans[j];
          ctx.fillRect(ox + sx * S, oy + sy * S, sw * S + 0.05, sh * S + 0.05);
        }
      }
    }

    // Bio-elektrischer Puls-Glow um das Starburst-Kernauge (bei x=0, y=-2)
    const loafGlowAlpha = 0.35 + neuralIntensity * 0.7;
    glow(ctx, 0, -2, 14 + neuralIntensity * 12, isNeuralFlashing ? 'rgba(255, 255, 255, 0.9)' : `rgba(192, 38, 211, ${loafGlowAlpha})`, 0.85);

    // Bio-elektrische Blitze zu den 4 Tank-Elektroden während des Spikes
    if (isNeuralFlashing && neuralIntensity > 0.2) {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.2;
      // Blitz links zur inneren Kupferelektrode
      ctx.beginPath();
      ctx.moveTo(0, -2);
      ctx.lineTo(-7 + Math.sin(t * 70) * 3, -4);
      ctx.lineTo(-14, -2);
      ctx.lineTo(cylX + 5 - loafX, 0);
      ctx.stroke();
      // Blitz rechts zur inneren Kupferelektrode
      ctx.beginPath();
      ctx.moveTo(0, -2);
      ctx.lineTo(7 + Math.cos(t * 70) * 3, -4);
      ctx.lineTo(14, -2);
      ctx.lineTo(cylX + tankW - 5 - loafX, 0);
      ctx.stroke();
      // Geweih-Blitz zur oberen linken Kappe
      ctx.strokeStyle = '#67e8f9';
      ctx.beginPath();
      ctx.moveTo(-9, -13);
      ctx.lineTo(-14, -10);
      ctx.lineTo(cylX + 9 - loafX, -8);
      ctx.stroke();
      glow(ctx, 0, -2, 26, `rgba(103, 232, 249, ${neuralIntensity * 0.85})`, 0.95);
    }

    ctx.restore();

    // Vorderes Panzerglas & Glanzkanten
    ctx.fillStyle = 'rgba(255, 255, 255, 0.24)';
    ctx.fillRect(cylX + 3, glassY, 2.5, glassH);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.fillRect(cylX + tankW - 6, glassY, 2, glassH);

    // Oberes Stasis-Manifold & Cryo-Kappe
    drawBeveledBox(ctx, cylX - 4, tankTopY, tankW + 8, 11, '#1e293b', '#64748b', '#090d16', 2.0);
    // Druck-Manometer
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(tankX - 10, tankTopY + 5, 3.5, 0, Math.PI * 2);
    ctx.fill();
    line(ctx, tankX - 10, tankTopY + 5, tankX - 9, tankTopY + 3, '#dc2626', 1);
    // Status-LEDs
    const ledCol = isNeuralFlashing ? '#ef4444' : '#10b981';
    p(ctx, tankX + 7, tankTopY + 5, ledCol);
    p(ctx, tankX + 11, tankTopY + 5, '#00f0ff');
    glow(ctx, tankX + 7, tankTopY + 5, 4, isNeuralFlashing ? 'rgba(239, 68, 68, 0.85)' : 'rgba(16, 185, 129, 0.8)', 0.7);

    // Cryo-Kühlleitungen zur Decke
    ctx.strokeStyle = '#0284c7';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(tankX + 4, tankTopY);
    ctx.lineTo(tankX + 4, tankTopY - 18);
    ctx.moveTo(tankX - 4, tankTopY);
    ctx.lineTo(tankX - 4, tankTopY - 18);
    ctx.stroke();
    p(ctx, tankX + 5, tankTopY - 6, '#bae6fd');
    p(ctx, tankX - 3, tankTopY - 12, '#bae6fd');

    // -------------------------------------------------------------------------
    // 7. MOTORISIERTER DECKEN-BIOMETRIESCANNER (X = 96..132)
    // -------------------------------------------------------------------------
    const scannerRailY = tankTopY - 14;
    line(ctx, tankX - 18, scannerRailY, tankX + 18, scannerRailY, '#334155', 2);
    const scanOffset = Math.sin(t * 0.8) * 8;
    const carriageX = tankX + scanOffset;
    drawBeveledBox(ctx, carriageX - 5, scannerRailY - 2, 10, 5, '#0f172a', '#64748b', '#020617', 0.8);
    line(ctx, carriageX, scannerRailY + 3, carriageX, scannerRailY + 7, '#475569', 1.5);
    p(ctx, carriageX, scannerRailY + 8, '#00f0ff');
    glow(ctx, carriageX, scannerRailY + 8, 3, 'rgba(0, 240, 255, 0.8)', 0.7);

    // Vertikaler Scanstreifen & Zielmarker
    const scanLaserY = glassY + 4 + ((t * 16) % (glassH - 8));
    ctx.save();
    ctx.globalAlpha = 0.7;
    line(ctx, cylX + 3, scanLaserY, cylX + tankW - 3, scanLaserY, '#00f0ff', 1);
    p(ctx, tankX + Math.sin(t * 2.5) * 4, scanLaserY, '#ef4444');
    ctx.restore();

    // -------------------------------------------------------------------------
    // 8. ZWEIT-KAMMER / CRYO-INKUBATOR "SPECIMEN 07-B" (X = 142..158)
    // -------------------------------------------------------------------------
    const incX = 150;
    const incW = 16;
    const incH = 40;
    const incTopY = deckY - incH;
    const incCylX = incX - incW * 0.5;

    // Sockel
    drawBeveledBox(ctx, incCylX - 2, deckY - 8, incW + 4, 8, '#1e293b', '#64748b', '#090d16', 1.2);
    hazardStripes(ctx, incCylX, deckY - 6, incW, 2, 2);

    // Violettes Cryo-Gel
    const incGlassY = incTopY + 7;
    const incGlassH = incH - 15;
    const incGrad = ctx.createLinearGradient(incCylX, incGlassY, incCylX + incW, incGlassY + incGlassH);
    incGrad.addColorStop(0.0, 'rgba(59, 7, 100, 0.85)');
    incGrad.addColorStop(0.5, 'rgba(126, 34, 206, 0.65)');
    incGrad.addColorStop(1.0, 'rgba(88, 28, 135, 0.85)');
    ctx.fillStyle = incGrad;
    ctx.fillRect(incCylX + 1, incGlassY, incW - 2, incGlassH);

    // Schwebender Sul-Sporenkern
    const sporeY = incGlassY + incGlassH * 0.5 + Math.sin(t * 2.0 + 1.5) * 1.5;
    ctx.save();
    ctx.translate(incX, sporeY);
    ctx.rotate(t * 0.8);
    drawBeveledBox(ctx, -3, -3, 6, 6, '#a855f7', '#f43f5e', '#581c87', 0.6);
    p(ctx, 0, 0, '#facc15');
    glow(ctx, 0, 0, 7, 'rgba(192, 38, 211, 0.5)', 0.7);
    ctx.restore();

    // Deckel & Status-LEDs
    drawBeveledBox(ctx, incCylX - 1, incTopY, incW + 2, 7, '#1e293b', '#64748b', '#090d16', 1);
    p(ctx, incX - 3, incTopY + 3, '#22c55e');
    p(ctx, incX, incTopY + 3, '#22c55e');
    p(ctx, incX + 3, incTopY + 3, '#38bdf8');

    // -------------------------------------------------------------------------
    // 9. BIO-CONTAINMENT QUARANTINE SENTRY (X = 166, Y = deckY)
    // -------------------------------------------------------------------------
    const sentryX = 166, sentryY = deckY;
    const sntBreath = Math.sin(t * 2.0 + 2) * 0.5;

    // Wachen-Bodenschatten
    ctx.fillStyle = 'rgba(2, 6, 23, 0.45)';
    ctx.beginPath();
    ctx.ellipse(sentryX, sentryY + 1, 7, 3, 0, 0, Math.PI * 2);
    ctx.fill();

    // Schwere Kampfstiefel
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(sentryX - 3, sentryY - 8, 2.5, 8);
    ctx.fillRect(sentryX + 1, sentryY - 8, 2.5, 8);
    p(ctx, sentryX - 2, sentryY - 3, '#334155');
    p(ctx, sentryX + 2, sentryY - 3, '#334155');

    // Torso mit Panzerung
    const sntTorsoY = sentryY - 17 + sntBreath;
    drawBeveledBox(ctx, sentryX - 4, sntTorsoY, 8, 9, '#1e293b', '#475569', '#090d16', 1.2);
    rect(ctx, sentryX - 3, sntTorsoY + 3, 6, 2, '#334155');
    p(ctx, sentryX - 1, sntTorsoY + 4, '#ca8a04');

    // Helm mit versiegelter Gasmaske & Amber-Visier blickt nach rechts in den Hangar
    const sntHeadY = sntTorsoY - 7;
    drawBeveledBox(ctx, sentryX - 3.5, sntHeadY, 7, 7, '#0f172a', '#475569', '#020617', 1);
    rect(ctx, sentryX - 1, sntHeadY + 2, 4, 2, '#f59e0b');
    p(ctx, sentryX + 1, sntHeadY + 2, '#fef08a');
    drawBeveledBox(ctx, sentryX - 4, sntHeadY + 3, 2, 3, '#334155', '#64748b', '#0f172a', 0.5);

    // Schwerer Flammenwerfer / Bio-Unterdrückungskarabiner mit Sicherungsstrahl
    const gunY = sntTorsoY + 4;
    drawBeveledBox(ctx, sentryX + 1, gunY, 8, 3, '#090d16', '#334155', '#020408', 0.8);
    line(ctx, sentryX + 9, gunY + 1.5, sentryX + 12, gunY + 1.5, '#475569', 1.5);
    ctx.save();
    ctx.globalAlpha = 0.25;
    line(ctx, sentryX + 12, gunY + 1.5, 175, deckY, '#38bdf8', 1);
    ctx.restore();

    // -------------------------------------------------------------------------
    // 10. KÄLTEDAMPF-ENTLÜFTUNG & BODEN-KRIECHNEBEL
    // -------------------------------------------------------------------------
    const ventCycle = t % 4.0;
    if (ventCycle < 1.3) {
      const vAlpha = Math.sin((ventCycle / 1.3) * Math.PI) * 0.40;
      ctx.save();
      ctx.globalAlpha = vAlpha;
      const vGrad = ctx.createRadialGradient(tankX - 6, tankTopY + 2, 0, tankX - 6, tankTopY + 2, 12);
      vGrad.addColorStop(0, 'rgba(224, 242, 254, 0.75)');
      vGrad.addColorStop(0.5, 'rgba(186, 230, 253, 0.35)');
      vGrad.addColorStop(1, 'rgba(186, 230, 253, 0)');
      ctx.fillStyle = vGrad;
      ctx.beginPath();
      ctx.arc(tankX - 6, tankTopY + 2 - ventCycle * 5, 5 + ventCycle * 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Subtiler Boden-Kriechnebel unter der Kammer
    ctx.save();
    ctx.globalAlpha = 0.12 + Math.sin(t * 1.4) * 0.05;
    ctx.fillStyle = '#bae6fd';
    ctx.fillRect(cylX - 4, deckY - 2, tankW + 8, 2);
    ctx.restore();

    ctx.restore();
  }

  // --- 6a7. HUMAN VANGUARD PANZER LIVE-EFFEKTE (HITZEFLIMMERN & STANDBY-BEACONS)
  function renderPanzerLiveFX(ctx, dt) {
    ctx.save();
    const px = 350, py = 432;
    const t = fx.time;

    // 1. Zarter Hitzeflimmer-Effekt (Convection Heat Haze) über dem Heck-Motordeck
    const deckX = px + 80, deckY = py - 46;
    ctx.fillStyle = 'rgba(254, 215, 170, 0.08)';
    for (let i = 0; i < 5; i++) {
      const hPhase = (t * 2.2 + i * 1.4) % 2.5;
      const hy = deckY - hPhase * 9;
      const hx = deckX + (i - 2) * 8 + Math.sin(t * 3.5 + i) * 6;
      const hr = 5 + hPhase * 4;
      ctx.beginPath();
      ctx.ellipse(hx, hy, hr * 1.5, hr * 0.7, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // 2. Taktische Peitschenantennen-Warnleuchte (Blinkender roter Warnstrobe an Antennenspitze)
    const tipX = px + 64, tipY = py - 140;
    const beaconPulse = Math.sin(t * 4.0) > 0.2;
    if (beaconPulse) {
      p(ctx, tipX, tipY, '#ef4444');
      glow(ctx, tipX, tipY, 7, 'rgba(239, 68, 68, 0.70)', 0.85);
    } else {
      p(ctx, tipX, tipY, '#7f1d1d');
    }

    // 3. FLIR / Zieloptik Standby-Schimmer (Goldbedampfte Wärmebild- & Cyan Laser-Reflexe)
    const opticGoldX = px - 40, opticCyanX = px - 33, opticY = py - 81;
    const opticGlint = (Math.sin(t * 2.8) + 1) * 0.5;
    glow(ctx, opticGoldX, opticY, 5, `rgba(245, 158, 11, ${0.2 + opticGlint * 0.35})`, 0.65);
    glow(ctx, opticCyanX, opticY, 4, `rgba(0, 240, 255, ${0.15 + (1 - opticGlint) * 0.3})`, 0.55);

    // 4. APU Starkstrom-Bodenrezeptakel pulsierende Lade-LED
    const sockX = px + 148, sockY = py - 6;
    const sockPulse = 0.5 + Math.sin(t * 5.0) * 0.3;
    glow(ctx, sockX + 1, sockY + 1.5, 7, `rgba(0, 240, 255, ${sockPulse})`, 0.8);

    ctx.restore();
  }


  // --- 6d. CATWALK ENCOUNTER: SUL APOSTATE VS. DEFECTIVE LOAF ---------------
  function triggerCatwalkEncounter() {
    const enc = fx.catwalkEncounter;
    if (!enc) return;
    enc.state = 'apostate_crawl_upper';
    enc.timer = 0;
    enc.apostate.x = BASE_WIDTH + 35; // 1059
    enc.apostate.baseY = 82;
    enc.apostate.facing = -1;
    enc.apostate.walkPhase = 0;
    enc.apostate.recoilX = 0;
    enc.apostate.jitterX = 0;
    enc.apostate.lookAngle = 0;
    enc.apostate.eyePulse = 1.0;
    enc.apostate.pauseTimer = 0;
    enc.apostate.struggleShift = 0;
    enc.apostate.jumpTimer = 0;
    enc.apostate._lowerPauseDone = false;

    enc.defective.active = false;
    enc.defective.x = -40;
    enc.defective.baseY = 82;
    enc.defective.facing = 1;
    enc.defective.walkPhase = 0;
    enc.defective.tentacleReach = 0;
    enc.defective.anchorTension = 0;

    enc.spores = [];
    enc.clawSparks = [];
    enc.tetherPulses = [];
  }

  function renderCatwalkEncounter(ctx, dt) {
    const enc = fx.catwalkEncounter;
    if (!enc) return;

    const ap = enc.apostate;
    const def = enc.defective;
    const t = fx.time;

    // ── 1. KINEMATICS & STATE TRANSITIONS ──
    switch (enc.state) {
      case 'cooldown': {
        enc.timer -= dt;
        if (enc.timer <= 0) {
          triggerCatwalkEncounter();
        }
        break;
      }

      case 'apostate_crawl_upper': {
        ap.baseY = 82;
        ap.x -= ap.speed * dt;
        ap.walkPhase += dt * 6.5;

        // At x <= 850: Jump down to the lower girder!
        if (ap.x <= 850) {
          enc.state = 'apostate_jump_down';
          ap.jumpTimer = 0.65;
        }
        break;
      }

      case 'apostate_jump_down': {
        ap.jumpTimer -= dt;
        const u = Math.min(1.0, Math.max(0, 1.0 - ap.jumpTimer / 0.65));
        ap.x = 850 - 45 * u;
        ap.baseY = 82 + 43 * u - Math.sin(u * Math.PI) * 12;
        ap.walkPhase += dt * 4.0;

        if (ap.jumpTimer <= 0) {
          ap.baseY = 125;
          enc.state = 'apostate_crawl_lower';
          // Touchdown spore motes
          for (let k = 0; k < 6; k++) {
            enc.spores.push({
              x: ap.x + (Math.random() - 0.5) * 12,
              y: 125,
              vx: (Math.random() - 0.5) * 18,
              vy: -Math.random() * 8 - 2,
              alpha: 0.9,
              color: Math.random() < 0.5 ? '#c084fc' : '#38bdf8',
              size: 1 + Math.random() * 1.5,
            });
          }
        }
        break;
      }

      case 'apostate_crawl_lower': {
        ap.baseY = 125;
        ap.x -= ap.speed * dt;
        ap.walkPhase += dt * 6.5;

        // Inspection pause beneath Flight Deck Ops around x = 650
        if (ap.x <= 650 && !ap._lowerPauseDone) {
          ap._lowerPauseDone = true;
          ap.pauseTimer = 2.5;
          enc.state = 'apostate_lower_pause';
        }

        // At x <= 470: Jump up to the upper girder!
        if (ap.x <= 470) {
          enc.state = 'apostate_jump_up';
          ap.jumpTimer = 0.70;
        }
        break;
      }

      case 'apostate_lower_pause': {
        ap.baseY = 125;
        ap.pauseTimer -= dt;
        ap.lookAngle = Math.sin((2.5 - ap.pauseTimer) * 2.8) * 0.25;
        ap.eyePulse = 0.7 + 0.3 * Math.sin(t * 4);
        if (ap.pauseTimer <= 0) {
          ap.lookAngle = 0;
          enc.state = 'apostate_crawl_lower';
        }
        break;
      }

      case 'apostate_jump_up': {
        ap.jumpTimer -= dt;
        const u = Math.min(1.0, Math.max(0, 1.0 - ap.jumpTimer / 0.70));
        ap.x = 470 - 55 * u;
        const yLin = 125 - 49 * u;
        ap.baseY = yLin - Math.sin(u * Math.PI) * 24;
        ap.walkPhase += dt * 5.0;

        if (ap.jumpTimer <= 0) {
          ap.baseY = getUpperGirderY(ap.x);
          enc.state = 'apostate_crawl_upper_left';
          // Touchdown spore motes
          for (let k = 0; k < 6; k++) {
            enc.spores.push({
              x: ap.x + (Math.random() - 0.5) * 12,
              y: ap.baseY,
              vx: (Math.random() - 0.5) * 18,
              vy: -Math.random() * 8 - 2,
              alpha: 0.9,
              color: Math.random() < 0.5 ? '#c084fc' : '#38bdf8',
              size: 1 + Math.random() * 1.5,
            });
          }
        }
        break;
      }

      case 'apostate_crawl_upper_left': {
        ap.baseY = getUpperGirderY(ap.x);
        ap.x -= ap.speed * dt;
        ap.walkPhase += dt * 6.5;

        // Defective Loaf enters from the left on the upper girder!
        if (ap.x <= 350 && !def.active) {
          def.active = true;
          def.x = -35;
          def.facing = 1;
          def.baseY = 82;
          def.walkPhase = 0;
          def.tentacleReach = 0;
          enc.state = 'approach';
        }
        break;
      }

      case 'approach': {
        ap.baseY = getUpperGirderY(ap.x);
        ap.x -= ap.speed * dt;
        ap.walkPhase += dt * 6.5;

        def.baseY = getUpperGirderY(def.x);
        def.x += def.speed * dt;
        def.walkPhase += dt * 8.0;

        // At <= 105px distance: Apostate discovers Defective Loaf!
        if (ap.x - def.x <= 105) {
          enc.state = 'startle_react';
          enc.timer = 0.75;
          ap.recoilX = 12;
        }
        break;
      }

      case 'startle_react': {
        ap.baseY = getUpperGirderY(ap.x);
        def.baseY = getUpperGirderY(def.x);
        enc.timer -= dt;
        ap.recoilX = Math.max(0, ap.recoilX - dt * 25);
        ap.eyePulse = 2.0;

        if (Math.random() < 0.4) {
          enc.spores.push({
            x: ap.x + ap.recoilX + (Math.random() - 0.5) * 10,
            y: ap.baseY - 18 + (Math.random() - 0.5) * 6,
            vx: (Math.random() - 0.5) * 16 + 8,
            vy: -Math.random() * 14 - 6,
            alpha: 1.0,
            color: Math.random() < 0.5 ? '#c084fc' : '#38bdf8',
            size: 1 + Math.random() * 1.5,
          });
        }

        if (enc.timer <= 0) {
          enc.state = 'nervous_look';
          enc.timer = 1.3;
        }
        break;
      }

      case 'nervous_look': {
        ap.baseY = getUpperGirderY(ap.x);
        def.baseY = getUpperGirderY(def.x);
        enc.timer -= dt;
        const glancePhase = (1.3 - enc.timer) * 12;
        ap.lookAngle = Math.sin(glancePhase) * 0.38;
        ap.jitterX = Math.sin(t * 40) * 0.9;
        ap.eyePulse = 1.2 + Math.sin(t * 12) * 0.5;

        if (enc.timer <= 0) {
          ap.jitterX = 0;
          ap.lookAngle = 0;
          ap.facing = 1; // Pivot right to flee!
          ap.walkPhase = 0;
          enc.state = 'flee';
          enc.timer = 0.9;
        }
        break;
      }

      case 'flee': {
        ap.baseY = getUpperGirderY(ap.x);
        def.baseY = getUpperGirderY(def.x);
        enc.timer -= dt;
        ap.x += 38 * dt;
        ap.walkPhase += dt * 14;

        if (enc.timer <= 0) {
          enc.state = 'tentacle_strike';
          enc.timer = 0.32;
          def.tentacleReach = 0;
        }
        break;
      }

      case 'tentacle_strike': {
        ap.baseY = getUpperGirderY(ap.x);
        def.baseY = getUpperGirderY(def.x);
        enc.timer -= dt;
        const strikeProg = 1 - Math.max(0, enc.timer / 0.32);
        def.tentacleReach = Math.sin(strikeProg * Math.PI * 0.5);
        ap.x += 14 * dt * (1 - strikeProg);

        if (enc.timer <= 0) {
          enc.state = 'tug_of_war';
          enc.timer = 4.2;
          def.tentacleReach = 1.0;
        }
        break;
      }

      case 'tug_of_war': {
        ap.baseY = getUpperGirderY(ap.x);
        def.baseY = getUpperGirderY(def.x);
        enc.timer -= dt;
        const prog = (4.2 - enc.timer) / 4.2;
        const struggleWave = Math.sin(prog * Math.PI * 5.5) * 8.5 * Math.sin(prog * Math.PI) + Math.cos(prog * Math.PI * 3) * 3;
        ap.struggleShift = struggleWave;
        def.anchorTension = Math.max(0, -struggleWave);

        if (Math.random() < 0.5) {
          enc.clawSparks.push({
            x: ap.x + struggleWave + (Math.random() - 0.5) * 12,
            y: ap.baseY,
            vx: (Math.random() - 0.5) * 22 + (struggleWave > 0 ? 10 : -10),
            vy: -Math.random() * 12 - 4,
            alpha: 1.0,
            color: Math.random() < 0.6 ? '#facc15' : '#ffffff',
          });
        }

        if (enc.timer <= 0) {
          enc.state = 'drag_away';
          ap.struggleShift = 0;
        }
        break;
      }

      case 'drag_away': {
        const dragSpeed = 28 * dt;
        def.x -= dragSpeed;
        def.walkPhase += dt * 9;
        def.baseY = getUpperGirderY(def.x);

        ap.x -= dragSpeed;
        ap.walkPhase += dt * 6;
        ap.baseY = getUpperGirderY(ap.x);

        if (Math.random() < 0.45) {
          enc.clawSparks.push({
            x: ap.x + (Math.random() - 0.5) * 10,
            y: ap.baseY,
            vx: Math.random() * 16 + 6,
            vy: -Math.random() * 8 - 2,
            alpha: 1.0,
            color: '#facc15',
          });
        }

        if (ap.x < -60 && def.x < -60) {
          enc.state = 'cooldown';
          enc.timer = 50 + Math.random() * 35;
          def.active = false;
        }
        break;
      }
    }

    if (enc.state === 'cooldown' && !def.active && ap.x < -60) {
      return;
    }

    // ── 2. PARTICLES UPDATE & DRAW (SPORES & CLAW SPARKS) ──
    if (enc.state !== 'cooldown' && ap.x > -40 && ap.x < BASE_WIDTH + 40) {
      if (Math.random() < 0.18) {
        enc.spores.push({
          x: ap.x + (ap.recoilX || 0) + (ap.struggleShift || 0) + (Math.random() - 0.5) * 12,
          y: ap.baseY - 19 + (Math.random() - 0.5) * 4,
          vx: (Math.random() - 0.5) * 6 - 4,
          vy: -Math.random() * 10 - 4,
          alpha: 0.9,
          color: Math.random() < 0.65 ? '#c084fc' : '#38bdf8',
          size: 1 + Math.random() * 1.5,
        });
      }
    }

    for (let i = enc.spores.length - 1; i >= 0; i--) {
      const sp = enc.spores[i];
      sp.x += sp.vx * dt;
      sp.y += sp.vy * dt;
      sp.alpha -= dt * 0.45;
      if (sp.alpha <= 0) {
        enc.spores.splice(i, 1);
        continue;
      }
      ctx.fillStyle = sp.color;
      ctx.globalAlpha = Math.max(0, sp.alpha);
      ctx.fillRect(Math.round(sp.x), Math.round(sp.y), sp.size, sp.size);
    }
    ctx.globalAlpha = 1.0;

    for (let i = enc.clawSparks.length - 1; i >= 0; i--) {
      const cs = enc.clawSparks[i];
      cs.x += cs.vx * dt;
      cs.y += cs.vy * dt;
      cs.vy += 30 * dt;
      cs.alpha -= dt * 2.4;
      if (cs.alpha <= 0) {
        enc.clawSparks.splice(i, 1);
        continue;
      }
      ctx.fillStyle = cs.color;
      ctx.globalAlpha = Math.max(0, cs.alpha);
      ctx.fillRect(Math.round(cs.x), Math.round(cs.y), 1.5, 1.5);
    }
    ctx.globalAlpha = 1.0;

    // ── 3. GIRDER CONTACT SHADOWS ──
    const apX = ap.x + (ap.recoilX || 0) + (ap.jitterX || 0) + (ap.struggleShift || 0);
    const apBob = (enc.state.startsWith('apostate_crawl') || enc.state === 'approach' || enc.state === 'flee') ? Math.sin(ap.walkPhase * 2) * 0.8 : 0;
    const apY = ap.baseY - apBob;

    if (apX > -50 && apX < BASE_WIDTH + 50) {
      ctx.fillStyle = 'rgba(2, 6, 14, 0.55)';
      ctx.beginPath();
      ctx.ellipse(apX, ap.baseY, 12, 2.5, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    if (def.active && def.x > -50 && def.x < BASE_WIDTH + 50) {
      const defBob = (enc.state === 'approach' || enc.state === 'drag_away') ? Math.sin(def.walkPhase) * 0.7 : 0;
      const defY = def.baseY - defBob;
      ctx.fillStyle = 'rgba(2, 6, 14, 0.55)';
      ctx.beginPath();
      ctx.ellipse(def.x, def.baseY, 11, 2.5, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // ── 4. DRAW DEFECTIVE LOAF ──
    let defTipX = 0, defTipY = 0;
    if (def.active && def.x > -50 && def.x < BASE_WIDTH + 50) {
      const defBob = (enc.state === 'approach' || enc.state === 'drag_away') ? Math.sin(def.walkPhase) * 0.7 : 0;
      const defY = def.baseY - defBob;
      const defSpans = (enc.state === 'approach') ? DEFECTIVE_SPANS_DRIFT : DEFECTIVE_SPANS_ATTACK;

      drawCatwalkSprite(ctx, def.x, defY, defSpans, def.facing, 1.0);

      const starAlpha = 0.45 + (enc.state === 'startle_react' || enc.state === 'tentacle_strike' ? 0.4 : 0);
      glow(ctx, def.x + def.facing * 2, defY - 15, 9, 'rgba(232, 121, 249, ' + starAlpha + ')', 0.85);

      defTipX = def.x + (def.facing === 1 ? 11 : -11);
      defTipY = defY - 8;
    }

    // ── 5. DRAW APOSTATE LOAF ──
    if (apX > -50 && apX < BASE_WIDTH + 50) {
      let apSpans;
      const isCrawlState = enc.state.startsWith('apostate_crawl') || enc.state.startsWith('apostate_jump') || enc.state === 'approach' || enc.state === 'drag_away';
      if (isCrawlState) {
        const frameIdx = Math.floor((ap.walkPhase / (Math.PI * 0.5)) % 4);
        const frames = [APOSTATE_SPANS_WALK1, APOSTATE_SPANS_WALK2, APOSTATE_SPANS_WALK3, APOSTATE_SPANS_WALK4];
        apSpans = frames[frameIdx];
      } else if (enc.state === 'flee') {
        const frameIdx = Math.floor((ap.walkPhase / (Math.PI * 0.35)) % 4);
        const frames = [APOSTATE_SPANS_WALK1, APOSTATE_SPANS_WALK2, APOSTATE_SPANS_WALK3, APOSTATE_SPANS_WALK4];
        apSpans = frames[frameIdx];
      } else {
        apSpans = APOSTATE_SPANS_IDLE;
      }

      ctx.save();
      if (ap.lookAngle) {
        ctx.translate(apX, apY - 10);
        ctx.rotate(ap.lookAngle);
        ctx.translate(-apX, -(apY - 10));
      }
      drawCatwalkSprite(ctx, apX, apY, apSpans, ap.facing, 1.0);

      glow(ctx, apX, apY - 17, 7, 'rgba(0, 240, 255, 0.32)', 0.8);
      glow(ctx, apX + ap.facing * 2, apY - 10, 5, 'rgba(0, 240, 255, ' + (0.4 * ap.eyePulse) + ')', 0.75);
      ctx.restore();
    }

    // ── 6. DRAW BIO-ELECTRIC TENTACLE & TETHER ──
    if (def.active && (enc.state === 'tentacle_strike' || enc.state === 'tug_of_war' || enc.state === 'drag_away')) {
      const targetX = apX - (ap.facing === 1 ? 8 : -8);
      const targetY = apY - 11;
      const reach = (enc.state === 'tentacle_strike') ? def.tentacleReach : 1.0;
      const endX = defTipX + (targetX - defTipX) * reach;
      const endY = defTipY + (targetY - defTipY) * reach;
      const tension = (enc.state === 'tug_of_war') ? 0.85 : 0.4;

      drawCatwalkTentacle(ctx, defTipX, defTipY, endX, endY, tension, t);
    }
  }

  // --- 6b. PATROULLIERENDER TECHNIKER IM FERNEN HANGAR (HERUMLAUFEN & INSPEKTION) -
  function renderPatrolTechnician(ctx, dt) {
    const tech = fx.tech;
    const isWalking = tech.state === 'patrol_left' || tech.state === 'patrol_right';

    if (tech.state === 'patrol_left') {
      tech.facing = -1;
      tech.x -= 15 * dt;
      tech.walkPhase += dt * 8.5;
      tech.lightAngle = Math.sin(tech.walkPhase * 0.5) * 0.08;
      if (tech.x <= 282) {
        tech.x = 282;
        tech.state = 'inspect_crate';
        tech.timer = 4.2;
      }
    } else if (tech.state === 'inspect_crate') {
      tech.timer -= dt;
      tech.lightAngle = -0.18 + Math.sin((4.2 - tech.timer) * 2.6) * 0.32;
      if (tech.timer <= 0) {
        tech.state = 'turn_right';
        tech.timer = 0.4;
      }
    } else if (tech.state === 'turn_right') {
      tech.timer -= dt;
      if (tech.timer <= 0) {
        tech.facing = 1;
        tech.state = 'patrol_right';
      }
    } else if (tech.state === 'patrol_right') {
      tech.facing = 1;
      tech.x += 15 * dt;
      tech.walkPhase += dt * 8.5;
      tech.lightAngle = Math.sin(tech.walkPhase * 0.5) * 0.08;
      if (tech.x >= 392) {
        tech.x = 392;
        tech.state = 'inspect_rack';
        tech.timer = 4.2;
      }
    } else if (tech.state === 'inspect_rack') {
      tech.timer -= dt;
      tech.lightAngle = -0.16 + Math.sin((4.2 - tech.timer) * 2.6) * 0.3;
      if (tech.timer <= 0) {
        tech.state = 'turn_left';
        tech.timer = 0.4;
      }
    } else if (tech.state === 'turn_left') {
      tech.timer -= dt;
      if (tech.timer <= 0) {
        tech.facing = -1;
        tech.state = 'patrol_left';
      }
    }

    // Schrittdynamik (Walk Cycle)
    let bobY = 0;
    let legFwd = 0;
    let legBack = 0;
    let armSwing = 0;

    if (isWalking) {
      bobY = Math.abs(Math.sin(tech.walkPhase)) * 1.5;
      legFwd = Math.sin(tech.walkPhase) * 4.2;
      legBack = -Math.sin(tech.walkPhase) * 4.2;
      armSwing = Math.sin(tech.walkPhase) * 2;
    } else {
      legFwd = 1.5;
      legBack = -1.5;
    }

    ctx.save();
    ctx.translate(Math.round(tech.x), Math.round(tech.y - bobY));
    ctx.scale(tech.facing, 1);

    // Beine & Arbeitsschuhe
    ctx.fillStyle = '#060d16';
    // Hinteres Bein
    ctx.fillRect(Math.round(legBack - 1), -8, 2.5, 8);
    ctx.fillStyle = '#020617';
    ctx.fillRect(Math.round(legBack - 1.5), 0, 3.5, 2);

    // Vorderes Bein
    ctx.fillStyle = '#0a1422';
    ctx.fillRect(Math.round(legFwd - 1), -8, 2.5, 8);
    ctx.fillStyle = '#020617';
    ctx.fillRect(Math.round(legFwd - 1.5), 0, 3.5, 2);

    // Körper & Warnweste (High-Vis Vest)
    ctx.fillStyle = '#09131e';
    ctx.fillRect(-2.5, -20, 5, 12);
    // Orange/Gelbe Warnweste
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(-2.5, -17, 5, 4);
    ctx.fillStyle = '#020617';
    ctx.fillRect(-0.5, -17, 1, 4); // Reißverschluss
    // Werkzeuggürtel mit Öse
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(-3, -9, 6, 2);
    p(ctx, 1, -8, '#e2e8f0');

    // Kopf & Schutzhelm mit Stirnleuchte
    ctx.fillStyle = '#09131e';
    ctx.beginPath();
    ctx.arc(0, -23, 2.8, 0, Math.PI * 2);
    ctx.fill();

    // Gelber Industrie-Schutzhelm
    ctx.fillStyle = '#eab308';
    ctx.beginPath();
    ctx.arc(0, -24.5, 3.2, Math.PI, 0);
    ctx.fill();
    ctx.fillRect(-3.5, -24.5, 7, 1.2);
    p(ctx, 2.5, -24.5, '#ffffff'); // Stirnlampe

    // Arm mit Halogen-Inspektionsleuchte
    const handX = 4 + armSwing * 0.5;
    const handY = -13;

    ctx.fillStyle = '#0a1422';
    ctx.fillRect(0, -18, 3, 3);
    ctx.fillRect(2, -16, handX - 1, 3);

    // Taschenlampen-Gehäuse
    ctx.fillStyle = '#475569';
    ctx.fillRect(handX, handY - 1.5, 4, 3);
    ctx.fillStyle = '#fef08a';
    ctx.fillRect(handX + 3.5, handY - 1, 1, 2);

    // DYNAMISCHER TASCHENLAMPEN-KEGEL (VOLUMETRISCH)
    const beamAngle = tech.lightAngle;
    const beamLen = 58;
    const targetX = handX + 4 + Math.cos(beamAngle) * beamLen;
    const targetY = handY + Math.sin(beamAngle) * beamLen;

    const tGrad = ctx.createRadialGradient(handX + 4, handY, 1, targetX, targetY, 32);
    tGrad.addColorStop(0, 'rgba(254, 240, 138, 0.85)');
    tGrad.addColorStop(0.3, 'rgba(254, 240, 138, 0.45)');
    tGrad.addColorStop(0.7, 'rgba(254, 240, 138, 0.12)');
    tGrad.addColorStop(1, 'rgba(254, 240, 138, 0)');
    ctx.fillStyle = tGrad;

    ctx.beginPath();
    ctx.moveTo(handX + 4, handY);
    ctx.lineTo(targetX, targetY - 16);
    ctx.lineTo(targetX, targetY + 16);
    ctx.closePath();
    ctx.fill();

    // Beleuchteter Auftreff-Fleck auf den Containern / Regalen
    ctx.save();
    const spotGrad = ctx.createRadialGradient(targetX, targetY, 0, targetX, targetY, 18);
    spotGrad.addColorStop(0, 'rgba(254, 240, 138, 0.35)');
    spotGrad.addColorStop(1, 'rgba(254, 240, 138, 0)');
    ctx.fillStyle = spotGrad;
    ctx.beginPath();
    ctx.ellipse(targetX, targetY, 8, 16, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // DATAPAD-NOTIZEN BEI INSPEKTION
    const isInspecting = (tech.state === 'inspect_crate' || tech.state === 'inspect_rack') && tech.timer < 2.2 && tech.timer > 0.4;
    if (isInspecting) {
      // Hinterer Arm hält Datapad
      ctx.fillStyle = '#0a1422';
      ctx.fillRect(-2, -14, 3, 4);
      // Leuchtendes Datapad
      ctx.fillStyle = '#38bdf8';
      ctx.fillRect(-1, -12, 3.5, 5);
      p(ctx, 0, -11, '#ffffff');
      p(ctx, 1, -9, '#22c55e');

      // Subtiler Display-Glow auf Brust und Helm
      ctx.save();
      const padGlow = ctx.createRadialGradient(0, -11, 0, 0, -11, 10);
      padGlow.addColorStop(0, 'rgba(56, 189, 248, 0.45)');
      padGlow.addColorStop(1, 'rgba(56, 189, 248, 0)');
      ctx.fillStyle = padGlow;
      ctx.beginPath();
      ctx.arc(0, -11, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.restore();
  }

  // --- 6c. TRIGGER-FUNKTION FÜR DIE MARINE-PATROUILLE -----------------------
  function triggerMarinePatrol() {
    if (!fx.marines) return;
    fx.marines.active = true;
    fx.marines.x = 100;
    fx.marines.walkPhase = 0;
    fx.marines.cooldownTimer = 0;
  }

  // --- 6d. EINZELNER HUMAN MARINE (POWERED COMBAT SUIT) ------------
  function drawSingleMarine(ctx, mx, my, walkPhase, isLeader, marineIndex) {
    // Schrittdynamik (Disciplined Military March Cadence)
    const bobY = Math.abs(Math.sin(walkPhase * 2)) * 1.3;
    const stride = Math.sin(walkPhase);
    const legFwd = stride * 4.8;
    const legBack = -stride * 4.8;
    const weaponBob = Math.sin(walkPhase * 2) * 0.8;

    ctx.save();
    ctx.translate(Math.round(mx), Math.round(my - bobY));

    // A. Weicher Boden-Kontaktschatten
    ctx.save();
    ctx.fillStyle = 'rgba(2, 6, 12, 0.45)';
    ctx.beginPath();
    ctx.ellipse(0, bobY, 9, 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // B. Reaktorrumpf & Lebenserhaltungs-Rucksack (Thruster Backpack)
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-7, -24, 4, 13);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(-6, -23, 2.5, 11);
    // Kaltfusions-Exhausts / Energie-Kerne
    p(ctx, -7, -21, '#0284c7');
    p(ctx, -7, -15, '#0284c7');

    // C. Hinteres Bein (Schattiert)
    ctx.fillStyle = '#09131e';
    ctx.fillRect(Math.round(legBack - 2), -10, 3.5, 5); // Oberschenkel
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(Math.round(legBack - 2.5), -5, 4, 6);  // Schienbeinpanzer & Stiefel

    // D. Vorderes Bein (Hauptbeleuchtet)
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(Math.round(legFwd - 2), -10, 3.5, 5);  // Oberschenkel
    ctx.fillStyle = '#334155';
    ctx.fillRect(Math.round(legFwd - 2.5), -5, 4, 5);   // Schienbein
    ctx.fillStyle = '#475569';
    ctx.fillRect(Math.round(legFwd - 1.5), -5, 2, 2);   // Kniescheiben-Verstärkung
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(Math.round(legFwd - 2.5), 0, 5, 2.5);  // Panzerschuh (Sabaton) mit Profil
    p(ctx, Math.round(legFwd + 1.5), 0, '#64748b');

    // E. Torso & Kürass (Brustpanzer)
    ctx.fillStyle = '#0a101d';
    ctx.fillRect(-3.5, -12, 7, 3);
    // Schwerer Brustharnisch
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(-4, -22, 8, 10);
    ctx.fillStyle = '#334155';
    ctx.fillRect(-3, -21, 6, 8);
    // Fraktions-Bruststreifen (Human Vanguard Blue)
    ctx.fillStyle = '#2563eb';
    ctx.fillRect(-3, -18, 6, 2);
    p(ctx, -1, -17, '#60a5fa');
    // Gürtel mit Munitionstaschen
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-4.5, -13, 9, 2);
    p(ctx, -2, -13, '#94a3b8');
    p(ctx, 1, -13, '#94a3b8');

    // F. Hinterer Schulterpanzer (Pauldron)
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.arc(-2, -22, 3.5, Math.PI * 0.8, Math.PI * 1.8);
    ctx.fill();

    // G. Kopf & Vollhelm mit beleuchtetem Visier
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.arc(0, -25.5, 3.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#334155';
    ctx.beginPath();
    ctx.arc(0.5, -26, 3.2, 0, Math.PI * 2);
    ctx.fill();
    // Stirnpanzer-Wulst
    ctx.fillStyle = '#475569';
    ctx.fillRect(-1.5, -28, 4.5, 1.5);

    // Cyan leuchtendes Visier (Human Marine Visor)
    ctx.fillStyle = '#38bdf8';
    ctx.fillRect(1.5, -26, 2.5, 1.8);
    p(ctx, 2.5, -25.5, '#ffffff');
    glow(ctx, 3, -25, 5, 'rgba(56, 189, 248, 0.55)', 0.7);

    // Leader-Details (Antenne & Sergeant-Insignie)
    if (isLeader) {
      // Taktische Funkantenne
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-1.5, -29);
      ctx.lineTo(-2.5, -34);
      ctx.stroke();
      // Blinkendes Antennen-Positionslicht
      const beaconOn = (Math.sin(fx.time * 6) + 1) * 0.5 > 0.4;
      p(ctx, -2.5, -34, beaconOn ? '#ef4444' : '#7f1d1d');
    }

    // H. C-14 Impaler Gauss Rifle (in Händen auf Patrouillen-Haltung)
    const gunY = -17 + weaponBob;
    // Waffenkörper
    ctx.fillStyle = '#090f1a';
    ctx.fillRect(0, gunY, 11, 4.5);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(1, gunY + 0.8, 9, 2.5);
    // Lauf & Mündungsbremse
    ctx.fillStyle = '#334155';
    ctx.fillRect(10, gunY + 1.2, 4, 1.6);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(13, gunY + 0.8, 1.5, 2.4);
    // Trommelmagazin / Munitionskasten
    ctx.fillStyle = '#090f1a';
    ctx.fillRect(2, gunY + 4, 3.5, 2.5);
    // Taktischer Ziellaser / Mündungs-Emitter-Dot
    p(ctx, 14, gunY + 1.5, '#38bdf8');

    // I. Vorderer Schulterpanzer (Dominanter runder Pauldron)
    ctx.fillStyle = '#334155';
    ctx.beginPath();
    ctx.arc(1, -21, 4.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#475569';
    ctx.beginPath();
    ctx.arc(0.5, -21.5, 3.4, 0, Math.PI * 2);
    ctx.fill();
    // Pauldron-Lichtkante
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(0, -24.5, 2.5, 1);

    if (isLeader) {
      // Gelber Sergeant-Rangstreifen auf dem Pauldron
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(1, -22, 2.5, 1.2);
    } else if (marineIndex === 2) {
      // Weißer Trupp-Identifikationsstreifen
      ctx.fillStyle = '#e2e8f0';
      ctx.fillRect(1, -22, 2.5, 1);
    }

    // Arm der die Waffe führt
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, -18, 3.5, 3);
    ctx.fillStyle = '#334155';
    ctx.fillRect(2.5, -17, 3, 2.5);

    ctx.restore();
  }

  // --- 6e. MARSCHIERENDE MARINE-PATROUILLE (Human Fireteam) ---------
  function renderMarinePatrol(ctx, dt) {
    const mp = fx.marines;
    if (!mp) return;

    if (mp.active) {
      mp.x += mp.speed * dt;
      mp.walkPhase += dt * 7.5;

      const squad = [
        { isLeader: true,  xOff: 0,   yOff: 1 },
        { isLeader: false, xOff: -26, yOff: -1 },
        { isLeader: false, xOff: -52, yOff: 2 },
        { isLeader: false, xOff: -78, yOff: 0 },
      ];

      const baseY = 338;

      // Zeichne jeden Marine der Marschstaffel (von hinten nach vorne für saubere Tiefenstaffelung)
      for (let i = squad.length - 1; i >= 0; i--) {
        const m = squad[i];
        const mx = mp.x + m.xOff;
        const my = baseY + m.yOff;
        if (mx >= 176 && mx <= BASE_WIDTH + 45) {
          const individualPhase = mp.walkPhase + i * 0.35;
          drawSingleMarine(ctx, mx, my, individualPhase, m.isLeader, i);
        }
      }

      // Prüfen ob die gesamte Staffel den rechten Bildschirmrand verlassen hat
      const lastMarineX = mp.x + squad[squad.length - 1].xOff;
      if (lastMarineX > BASE_WIDTH + 50) {
        mp.active = false;
        // Pause von 25 bis 42 Sekunden, bis die nächste Patrouille durchmarschiert ("ab und an")
        mp.cooldownTimer = 25 + Math.random() * 17;
      }
    } else {
      mp.cooldownTimer -= dt;
      if (mp.cooldownTimer <= 0) {
        mp.active = true;
        mp.x = 100;
        mp.walkPhase = 0;
      }
    }
  }

  // --- 7. TIEFEN-AKTIVITÄT (TURBINE, BAY 05 SCISSOR LIFT, CRYO VENT) ---------
  function renderDeepBayActivity(ctx, dt) {
    ctx.save();

    // 1. Rotierende Decken-Abluftturbine in der Wand (cx: 75, cy: 95, r: 24)
    const tRot = fx.time * 3.2;
    ctx.strokeStyle = '#243042';
    ctx.lineWidth = 2.5;
    for (let a = 0; a < Math.PI * 2; a += Math.PI / 3) {
      const curA = a + tRot;
      ctx.beginPath();
      ctx.moveTo(75, 95);
      ctx.quadraticCurveTo(
        75 + Math.cos(curA + 0.4) * 14,
        95 + Math.sin(curA + 0.4) * 14,
        75 + Math.cos(curA) * 22,
        95 + Math.sin(curA) * 22
      );
      ctx.stroke();
    }
    p(ctx, 75, 95, '#ffffff');

    // 2. Scherenhebebühne in Bay 05 — Warnblitzer
    const liftBlink = Math.sin(fx.time * 6) > 0.3;
    if (liftBlink) {
      p(ctx, 888, 240, '#fbbf24');
      glow(ctx, 888, 240, 7, 'rgba(251, 191, 36, 0.65)', 0.8);
    }

    // 3. Bay 05 Deckenkran-Laufkatze (Hintergrund-I-Träger bei y = 162)
    const bc = fx.bay05Crane;
    if (bc) {
      bc.x += bc.speed * bc.dir * dt;
      if (bc.x > 942) { bc.x = 942; bc.dir = -1; }
      else if (bc.x < 838) { bc.x = 838; bc.dir = 1; }
      bc.cableAngle = Math.sin(fx.time * 1.6) * 0.06;

      const trolleyX = bc.x, trolleyY = 162;
      // Laufkatzen-Chassis
      drawBeveledBox(ctx, trolleyX - 6, trolleyY - 3, 12, 5, '#1e293b', '#475569', '#020617', 0.8);
      // Blinkendes gelbes Warnlicht
      const tBlink = Math.sin(fx.time * 6.5) > 0.2;
      if (tBlink) {
        p(ctx, trolleyX, trolleyY - 4, '#fbbf24');
        glow(ctx, trolleyX, trolleyY - 4, 5, 'rgba(251, 191, 36, 0.5)', 0.6);
      }
      // Abgehängte Doppelseile mit sanftem Schwung
      const hookX = trolleyX + Math.sin(bc.cableAngle) * 28;
      const hookY = trolleyY + 28;
      line(ctx, trolleyX - 3, trolleyY + 2, hookX - 3, hookY, '#475569', 0.8);
      line(ctx, trolleyX + 3, trolleyY + 2, hookX + 3, hookY, '#475569', 0.8);
      // Schwebendes Munitions-/Triebwerksmodul
      drawBeveledBox(ctx, hookX - 7, hookY, 14, 7, '#0f172a', '#eab308', '#020617', 0.8);
      rect(ctx, hookX - 5, hookY + 2, 10, 3, '#ca8a04');
    }

    // 4. Periodische Kryogene Überdruck-Entlüftung (Cryo Relief Vents // 648, 246)
    fx.cryoTimer -= dt;
    if (fx.cryoTimer <= 0) {
      fx.cryoTimer = 20.0 + Math.random() * 6.0;
      // Zünde expandierende Wolke von 18 Partikeln
      for (let k = 0; k < 18; k++) {
        fx.cryoSteamPuffs.push({
          x: 648,
          y: 246 + (Math.random() - 0.5) * 4,
          vx: 14 + Math.random() * 18,
          vy: -3.5 + (Math.random() - 0.5) * 6,
          radius: 1.5,
          maxRadius: 16 + Math.random() * 10,
          alpha: 0.75 + Math.random() * 0.2,
          decay: 0.28 + Math.random() * 0.12,
        });
      }
    }

    // Sanfte Dauer-Wisp-Hauch am Überdruckventil
    if (Math.random() < 0.22) {
      fx.cryoSteamPuffs.push({
        x: 648,
        y: 246,
        vx: 4 + Math.random() * 5,
        vy: -2.5 - Math.random() * 2,
        radius: 1.2,
        maxRadius: 5.5,
        alpha: 0.35,
        decay: 0.45,
      });
    }

    // Cryo-Steam Partikel rendern & physikalisch simulieren
    for (let ci = fx.cryoSteamPuffs.length - 1; ci >= 0; ci--) {
      const cp = fx.cryoSteamPuffs[ci];
      cp.x += cp.vx * dt;
      cp.y += cp.vy * dt;
      cp.vx *= 0.94;
      cp.vy -= 4.0 * dt; // Steigt durch Auftrieb nach oben
      cp.radius += (cp.maxRadius - cp.radius) * 0.05;
      cp.alpha -= cp.decay * dt;

      if (cp.alpha <= 0) {
        fx.cryoSteamPuffs.splice(ci, 1);
        continue;
      }

      ctx.save();
      ctx.globalAlpha = Math.max(0, cp.alpha);
      const cvGrad = ctx.createRadialGradient(cp.x, cp.y, 0, cp.x, cp.y, cp.radius);
      cvGrad.addColorStop(0, 'rgba(240, 249, 255, 0.85)');
      cvGrad.addColorStop(0.4, 'rgba(186, 230, 253, 0.45)');
      cvGrad.addColorStop(1, 'rgba(186, 230, 253, 0)');
      ctx.fillStyle = cvGrad;
      ctx.beginPath();
      ctx.arc(cp.x, cp.y, cp.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // 5. Sul-Xeno-Bio-Containment Pod (Biolumineszenz-Puls & schwebende Bio-Sporen)
    const sulPulse = (Math.sin(fx.time * 2.2) + 1) * 0.5;
    const bioGlow = ctx.createRadialGradient(588, 278, 2, 588, 278, 20);
    bioGlow.addColorStop(0, `rgba(192, 132, 252, ${0.4 + sulPulse * 0.35})`);
    bioGlow.addColorStop(0.6, `rgba(147, 51, 234, ${0.15 + sulPulse * 0.15})`);
    bioGlow.addColorStop(1, 'rgba(147, 51, 234, 0)');
    ctx.fillStyle = bioGlow;
    ctx.beginPath();
    ctx.arc(588, 278, 20, 0, Math.PI * 2);
    ctx.fill();

    // Schwebende Bio-Sporen / Mikro-Blasen im Probenzylinder
    for (let bi = 0; bi < 4; bi++) {
      const bubbleY = 292 - ((fx.time * 10 + bi * 8) % 26);
      const bubbleX = 582 + Math.sin(bubbleY * 0.25 + bi * 1.5) * 4 + bi * 2.2;
      p(ctx, bubbleX, bubbleY, bi % 2 === 0 ? '#f0abfc' : '#c084fc');
    }

    // Quarantäne-Statusanzeige auf dem Sockel
    const bioSecBlink = Math.sin(fx.time * 3) > 0;
    p(ctx, 588, 241, bioSecBlink ? '#22c55e' : '#14532d');

    ctx.restore();
  }

  // --- 6f. REPARATUR- & SCHWEIß-DROHNE (Artikuliertes High-Tech Modell // DRN-04) ---
  function renderWeldingDrone(ctx, dt) {
    const drone = fx.drone;
    drone.timer -= dt;

    if (drone.timer <= 0) {
      if (drone.mode === 'patrol') {
        drone.mode = 'weld';
        drone.weldTimer = 3.2;
        drone.timer = 3.2;
        // Wählt eine realistische Schweißstelle am Hangar-Gerüst / Piranha-Träger
        drone.targetX = 510 + Math.random() * 240;
        drone.targetY = 120 + Math.random() * 60;
        if (els.droneStatus) els.droneStatus.textContent = 'WELDING // REPAIR';
      } else {
        drone.mode = 'patrol';
        drone.targetX = 460 + Math.random() * 320;
        drone.targetY = 95 + Math.random() * 85;
        drone.timer = 5.0 + Math.random() * 5.0;
        if (els.droneStatus) els.droneStatus.textContent = 'PATROL // SCANNING';
      }
    }

    // Kinematik & Geschmeidige Annäherung
    const dx = drone.targetX - drone.x;
    const dy = drone.targetY - drone.y;
    drone.x += dx * 0.045;
    drone.y += dy * 0.045;

    // Dynamischer Rollwinkel (Drohne neigt sich in Flugrichtung)
    const targetTilt = Math.max(-0.24, Math.min(0.24, dx * 0.007));
    drone.tilt += (targetTilt - drone.tilt) * 0.12;

    // Schwebe-Oszillation (Hover Bobbing)
    const hoverY = drone.y + Math.sin(fx.time * 3.6) * 3.2;

    // Ein- und Ausfahren des Schweißarms (0.0 = eingeklappt, 1.0 = voll ausgefahren)
    const targetArm = (drone.mode === 'weld') ? 1.0 : 0.0;
    drone.armExtension += (targetArm - drone.armExtension) * 0.08;

    // Schwenkbare VTOL-Triebwerke (Gimbal-Neigung zum Stabilisieren)
    const targetThruster = -drone.tilt * 0.75 + Math.sin(fx.time * 4) * 0.04;
    drone.thrusterTilt += (targetThruster - drone.thrusterTilt) * 0.14;

    // Strobe-Timer für Antikollisionsleuchte
    drone.strobeTimer = (drone.strobeTimer + dt * 1.6) % 1.0;
    const strobeFlash = drone.strobeTimer < 0.15 ? (1.0 - drone.strobeTimer / 0.15) : 0;

    // Zeichne Drohnen-Körper im rotierten & verschobenen Koordinatensystem
    ctx.save();
    ctx.translate(drone.x, hoverY);
    ctx.rotate(drone.tilt);

    // =========================================================================
    // 1. ZWEI SCHWENKBARE VTOL-TRIEBWERKSGONDELN (Links & Rechts)
    // =========================================================================
    [-1, 1].forEach(side => {
      ctx.save();
      const pylonX = side * 18, pylonY = -2;

      // Carbon-Trägerausleger vom Rumpf zum Triebwerk
      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(side * 8, 0);
      ctx.lineTo(pylonX, pylonY);
      ctx.stroke();

      // Drehservo-Gelenk
      ctx.fillStyle = '#0f172a';
      ctx.beginPath();
      ctx.arc(pylonX, pylonY, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#64748b';
      ctx.beginPath();
      ctx.arc(pylonX, pylonY, 2, 0, Math.PI * 2);
      ctx.fill();
      p(ctx, pylonX, pylonY, '#ffffff');

      // Schwenkbare Triebwerksgondel
      ctx.translate(pylonX, pylonY);
      ctx.rotate(drone.thrusterTilt);

      // Nacelle-Gondelkörper
      const nacGrad = ctx.createLinearGradient(-4.5, 0, 4.5, 0);
      nacGrad.addColorStop(0, '#0f172a');
      nacGrad.addColorStop(0.3, '#334155');
      nacGrad.addColorStop(0.7, '#64748b');
      nacGrad.addColorStop(1, '#1e293b');
      ctx.fillStyle = nacGrad;
      ctx.fillRect(-4.5, -8, 9, 15);

      // Obere Einlauflippe & Turbinengitter
      ctx.fillStyle = '#020617';
      ctx.fillRect(-4.5, -9, 9, 2);
      p(ctx, -2, -8, '#94a3b8');
      p(ctx, 1, -8, '#94a3b8');

      // Untere Titan-Düse mit Anlauffarben (Temper Blue & Purple)
      ctx.fillStyle = '#1e1b4b';
      ctx.fillRect(-4, 7, 8, 3);
      ctx.fillStyle = '#0284c7';
      ctx.fillRect(-3, 9, 6, 1.5);

      // Ionen-Schubflamme (Pulsierender Cyan-Strahl)
      const flameLen = 8 + Math.sin(fx.time * 26 + side * 2.5) * 3.5 + Math.abs(dx) * 0.04;
      const fGrad = ctx.createLinearGradient(0, 10, 0, 10 + flameLen);
      fGrad.addColorStop(0, '#ffffff');
      fGrad.addColorStop(0.2, state.redAlert ? '#ef4444' : '#00f0ff');
      fGrad.addColorStop(0.65, state.redAlert ? 'rgba(239, 68, 68, 0.45)' : 'rgba(56, 189, 248, 0.45)');
      fGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
      ctx.fillStyle = fGrad;
      ctx.beginPath();
      ctx.moveTo(-3, 10);
      ctx.lineTo(3, 10);
      ctx.lineTo(0, 10 + flameLen);
      ctx.closePath();
      ctx.fill();

      // Äußere Ionen-Aura
      const aGrad = ctx.createRadialGradient(0, 10, 1, 0, 12, 10);
      aGrad.addColorStop(0, state.redAlert ? 'rgba(239, 68, 68, 0.4)' : 'rgba(0, 240, 255, 0.4)');
      aGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = aGrad;
      ctx.beginPath();
      ctx.arc(0, 12, 10, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    });

    // =========================================================================
    // 2. HAUPTRUMPF & FACETTIERTE PANZERUNG (Chassis)
    // =========================================================================
    // Facettierte Rumpfform mit abgeschrägten Ecken
    const hullGrad = ctx.createLinearGradient(0, -10, 0, 12);
    hullGrad.addColorStop(0, '#1e293b');
    hullGrad.addColorStop(0.4, '#334155');
    hullGrad.addColorStop(0.85, '#1e293b');
    hullGrad.addColorStop(1, '#090f17');
    ctx.fillStyle = hullGrad;

    ctx.beginPath();
    ctx.moveTo(-13, -9);
    ctx.lineTo(13, -9);
    ctx.lineTo(17, -5);
    ctx.lineTo(17, 5);
    ctx.lineTo(12, 10);
    ctx.lineTo(-12, 10);
    ctx.lineTo(-17, 5);
    ctx.lineTo(-17, -5);
    ctx.closePath();
    ctx.fill();

    // Kantenschlag & Specular-Highlight (Licht von oben)
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-13, -9);
    ctx.lineTo(13, -9);
    ctx.lineTo(17, -5);
    ctx.stroke();

    ctx.strokeStyle = '#020617';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(17, 5);
    ctx.lineTo(12, 10);
    ctx.lineTo(-12, 10);
    ctx.lineTo(-17, 5);
    ctx.stroke();

    // Zentrales Brustpanzer-Segment
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-10, -5, 20, 10);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(-9, -4, 18, 8);

    // Dzus-Befestigungsnieten auf den Ecken
    p(ctx, -8, -3, '#ffffff');
    p(ctx, 7, -3, '#ffffff');
    p(ctx, -8, 3, '#64748b');
    p(ctx, 7, 3, '#64748b');

    // Kühlrippen / Radiator-Schlitze auf dem oberen Rücken
    ctx.fillStyle = '#020617';
    ctx.fillRect(-7, -8, 14, 1);
    ctx.fillRect(-6, -6.5, 12, 1);
    p(ctx, -4, -8, '#38bdf8');
    p(ctx, 3, -8, '#38bdf8');

    // Taktischer Stencil & Kennzeichnung
    ctx.font = '700 4px monospace';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('DRN-04', -13, -1);

    // Hazard-Streifen auf dem unteren Schutzpuffer
    ctx.save();
    ctx.beginPath();
    ctx.rect(-10, 8, 20, 2.5);
    ctx.clip();
    ctx.fillStyle = '#eab308';
    ctx.fillRect(-10, 8, 20, 2.5);
    ctx.fillStyle = '#090d16';
    for (let hx = -14; hx < 14; hx += 4) {
      ctx.beginPath();
      ctx.moveTo(hx, 11);
      ctx.lineTo(hx + 2.5, 8);
      ctx.lineTo(hx + 4, 8);
      ctx.lineTo(hx + 1.5, 11);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();

    // =========================================================================
    // 3. MULTI-SENSOR-ARRAY & INDIKATOR-LEUCHTEN
    // =========================================================================
    // A. Primäres Optik-Sensorauge (Zentraler Kamerasphäroid)
    const eyeX = -1, eyeY = 1;
    ctx.fillStyle = '#020617';
    ctx.beginPath();
    ctx.arc(eyeX, eyeY, 4.5, 0, Math.PI * 2);
    ctx.fill();

    const eyeGrad = ctx.createRadialGradient(eyeX, eyeY, 0, eyeX, eyeY, 4);
    const eyeCol = state.redAlert ? '#ef4444' : '#00f0ff';
    eyeGrad.addColorStop(0, '#ffffff');
    eyeGrad.addColorStop(0.35, eyeCol);
    eyeGrad.addColorStop(0.85, state.redAlert ? '#7f1d1d' : '#0369a1');
    eyeGrad.addColorStop(1, '#020617');
    ctx.fillStyle = eyeGrad;
    ctx.beginPath();
    ctx.arc(eyeX, eyeY, 3.5, 0, Math.PI * 2);
    ctx.fill();
    p(ctx, eyeX - 1, eyeY - 1, '#ffffff'); // Glaskanten-Glanz

    // B. Flankierende Indikator-LEDs
    // Linke Status-LED (Grün = Link OK, Rot = Alert)
    const statusCol = state.redAlert ? '#ef4444' : '#22c55e';
    p(ctx, -7, 1, statusCol);
    glow(ctx, -7, 1, 4, statusCol, 0.8);

    // Rechte Telemetrie-LED (Pulsierendes Blau)
    const bluePulse = (Math.sin(fx.time * 8) + 1) * 0.5;
    p(ctx, 6, 1, `rgba(56, 189, 248, ${0.4 + 0.6 * bluePulse})`);

    // C. Blinkende Antikollisions-Warnleuchte (Top Strobe Beacon)
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-1.5, -11, 3, 2); // Sockel
    ctx.fillStyle = strobeFlash > 0.1 ? '#ffffff' : '#f59e0b';
    ctx.fillRect(-1, -12.5, 2, 1.8);
    if (strobeFlash > 0.05) {
      glow(ctx, 0, -12, 14 * strobeFlash, 'rgba(254, 240, 138, 0.95)', strobeFlash);
    }

    // D. Nach unten gerichteter Halogen-Arbeitsscheinwerfer
    const spotGrad = ctx.createLinearGradient(0, 9, 0, 48);
    spotGrad.addColorStop(0, 'rgba(255, 255, 255, 0.45)');
    spotGrad.addColorStop(0.25, state.redAlert ? 'rgba(239, 68, 68, 0.22)' : 'rgba(56, 189, 248, 0.22)');
    spotGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = spotGrad;
    ctx.beginPath();
    ctx.moveTo(-6, 9);
    ctx.lineTo(6, 9);
    ctx.lineTo(16, 52);
    ctx.lineTo(-16, 52);
    ctx.closePath();
    ctx.fill();

    // =========================================================================
    // 4. MEHRGLIEDRIGER ARTIKULIERTER ROBOTER-SCHWEISSARM
    // =========================================================================
    // Schulter-Lagerbock am Rumpfboden
    const shoulderX = 3, shoulderY = 9;
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.arc(shoulderX, shoulderY, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#64748b';
    p(ctx, shoulderX, shoulderY, '#f8fafc');

    // Arm-Kinematik interpoliert mit drone.armExtension
    const armExt = drone.armExtension; // 0.0 (eingeklappt) bis 1.0 (voll ausgefahren)

    // Gelenkwinkel
    const shoulderAngle = -0.25 + armExt * 0.95;  // Schwenkt nach vorne-unten
    const elbowAngle = 0.4 + armExt * 0.75;        // Knickt zum Schweißpunkt
    const wristAngle = -0.3 + armExt * 0.4;

    const arm1Len = 12;
    const elbowX = shoulderX + Math.sin(shoulderAngle) * arm1Len;
    const elbowY = shoulderY + Math.cos(shoulderAngle) * arm1Len;

    const arm2Len = 14;
    const totalAng2 = shoulderAngle + elbowAngle;
    const wristX = elbowX + Math.sin(totalAng2) * arm2Len;
    const wristY = elbowY + Math.cos(totalAng2) * arm2Len;

    const totalAng3 = totalAng2 + wristAngle;
    const torchLen = 8;
    const torchTipX = wristX + Math.sin(totalAng3) * torchLen;
    const torchTipY = wristY + Math.cos(totalAng3) * torchLen;

    // Segment 1: Oberarm mit Hydraulikzylinder
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(shoulderX, shoulderY);
    ctx.lineTo(elbowX, elbowY);
    ctx.stroke();

    // Hydraulikstange (glänzender Chromzylinder)
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(shoulderX + 1, shoulderY + 2);
    ctx.lineTo(elbowX - 1, elbowY - 2);
    ctx.stroke();

    // Ellenbogengelenk
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(elbowX, elbowY, 2.5, 0, Math.PI * 2);
    ctx.fill();
    p(ctx, elbowX, elbowY, '#94a3b8');

    // Segment 2: Unterarm mit Sicherheitsmarkierung
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 2.4;
    ctx.beginPath();
    ctx.moveTo(elbowX, elbowY);
    ctx.lineTo(wristX, wristY);
    ctx.stroke();
    // Gelber Isolatorring
    p(ctx, (elbowX + wristX) * 0.5, (elbowY + wristY) * 0.5, '#eab308');

    // Handgelenk-Kopplung & Brennerkopf (Welding Torch)
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(wristX, wristY);
    ctx.lineTo(torchTipX, torchTipY);
    ctx.stroke();

    // Brenner-Keramikisolator & Messing-Gasdüse
    ctx.fillStyle = '#eab308';
    ctx.beginPath();
    ctx.arc(wristX, wristY, 2, 0, Math.PI * 2);
    ctx.fill();

    // Wolfram-Elektrodenspitze
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(torchTipX - Math.sin(totalAng3) * 2, torchTipY - Math.cos(totalAng3) * 2);
    ctx.lineTo(torchTipX, torchTipY);
    ctx.stroke();

    // Flexible schwarze Strom-/Gas-Versorgungsleitung (Kabel-Bogen vom Rumpf zum Brenner)
    ctx.strokeStyle = '#020617';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-6, 8);
    ctx.quadraticCurveTo(-2, elbowY + 4, wristX - 2, wristY);
    ctx.stroke();

    ctx.restore(); // restore drone local coordinate system

    // =========================================================================
    // 5. LICHTBOGEN & SCHWEISS-EFFEKTE (In Weltkoordinaten)
    // =========================================================================
    // Weltkoordinaten der Elektrodenspitze berechnen
    const cosT = Math.cos(drone.tilt), sinT = Math.sin(drone.tilt);
    const worldTorchX = drone.x + (torchTipX * cosT - torchTipY * sinT);
    const worldTorchY = hoverY + (torchTipX * sinT + torchTipY * cosT);

    if (drone.mode === 'weld' && drone.armExtension > 0.65) {
      drone.weldTimer -= dt;

      // Zielschnittstelle für den Lichtbogen
      const targetSurfaceX = worldTorchX + (Math.random() - 0.5) * 4;
      const targetSurfaceY = worldTorchY + 6 + Math.random() * 3;

      // Jagged Electric Arc (Hochfrequenter zackiger Lichtbogen)
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.moveTo(worldTorchX, worldTorchY);
      const midX = (worldTorchX + targetSurfaceX) * 0.5 + (Math.random() - 0.5) * 6;
      const midY = (worldTorchY + targetSurfaceY) * 0.5 + (Math.random() - 0.5) * 4;
      ctx.lineTo(midX, midY);
      ctx.lineTo(targetSurfaceX, targetSurfaceY);
      ctx.stroke();

      // Sekundärer violetter/cyanfarbener Plasmablitz
      ctx.strokeStyle = state.redAlert ? '#fca5a5' : '#67e8f9';
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(worldTorchX, worldTorchY);
      ctx.lineTo(midX + (Math.random() - 0.5) * 4, midY + (Math.random() - 0.5) * 4);
      ctx.lineTo(targetSurfaceX, targetSurfaceY);
      ctx.stroke();

      // Schweißfunken erzeugen
      if (Math.random() < 0.78) {
        spawnSparks(targetSurfaceX, targetSurfaceY, 8);
      }

      // Blendender radialer Schweißblitz & Lens Flare
      if (Math.random() < 0.90) {
        ctx.save();
        const flashRadius = 140 + Math.random() * 60;
        const flash = ctx.createRadialGradient(targetSurfaceX, targetSurfaceY, 0, targetSurfaceX, targetSurfaceY, flashRadius);
        flash.addColorStop(0, '#ffffff');
        flash.addColorStop(0.12, state.redAlert ? '#ef4444' : '#38bdf8');
        flash.addColorStop(0.45, state.redAlert ? 'rgba(239, 68, 68, 0.22)' : 'rgba(56, 189, 248, 0.22)');
        flash.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = flash;
        ctx.beginPath();
        ctx.arc(targetSurfaceX, targetSurfaceY, flashRadius, 0, Math.PI * 2);
        ctx.fill();

        // Anamorphic Lens Flare Streak (Horizontaler Schweiß-Glanzstreifen)
        const streakGrad = ctx.createLinearGradient(targetSurfaceX - 70, targetSurfaceY, targetSurfaceX + 70, targetSurfaceY);
        streakGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
        streakGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.75)');
        streakGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = streakGrad;
        ctx.fillRect(targetSurfaceX - 70, targetSurfaceY - 1, 140, 2);

        ctx.restore();
      }
    }

    // =========================================================================
    // 6. DYNAMISCHE FUNKENPHYSIK (Mit Schwerkraft & Bodenkollision)
    // =========================================================================
    for (let i = fx.sparks.length - 1; i >= 0; i--) {
      const s = fx.sparks[i];
      s.x += s.vx;
      s.y += s.vy;
      s.vy += 0.24; // Schwerkraft
      s.life -= s.decay * dt;

      // Bodenabprall bei Y = 435 (Hangar-Boden)
      if (s.y >= 435 && s.vy > 0) {
        s.vy = -s.vy * 0.45;
        s.vx *= 0.68;
        s.y = 435;
      }

      if (s.life <= 0) {
        fx.sparks.splice(i, 1);
        continue;
      }

      // Glühende Funkenpartikel
      ctx.fillStyle = s.life > 0.55 ? '#ffffff' : (s.life > 0.25 ? '#fbbf24' : '#ef4444');
      ctx.globalAlpha = Math.max(0, s.life);
      ctx.fillRect(Math.round(s.x), Math.round(s.y), s.size, s.size);
      // Glüh-Spur
      ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
      ctx.fillRect(Math.round(s.x - s.vx * 0.35), Math.round(s.y - s.vy * 0.35), 1, 1);
      ctx.globalAlpha = 1;
    }
  }

  // --- 8. A9 PIRANHA: HYDRAULIK-ATMEN, PRE-FLIGHT AVIONICS & FLIR-TRACKING ---
  function renderPiranhaIdle(ctx, dt) {
    const cx = 720, cy = 340;
    const t = fx.time;
    // Hydraulik-Atmen: 1.5px sanftes Heben/Senken im Dock
    const heave = Math.sin(t * 0.7) * 1.4;

    // Pre-Flight Avionik-Prüfzyklus alle 18 Sekunden (Elevons-Flap-Check & 30mm Elevation)
    const avionicsCycle = t % 18.0;
    const isAvionicsTest = avionicsCycle < 3.8;
    let flapDeflect = 0;
    let gunPitch = 0;
    if (isAvionicsTest) {
      flapDeflect = Math.sin((avionicsCycle / 3.8) * Math.PI * 2) * 2.4;
      gunPitch = Math.sin((avionicsCycle / 3.8) * Math.PI) * 1.6;
    }

    // 1. Winglet Elevons / Querruder-Hydraulik an den Flügelspitzen
    const portWingX = cx - 84, portWingY = cy + 14 + heave;
    const stbdWingX = cx + 172, stbdWingY = cy - 36 + heave;
    
    // Backbord-Elevon
    ctx.save();
    ctx.translate(portWingX, portWingY);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-6, -2, 12, 4); // Hydraulik-Schacht
    ctx.fillStyle = '#64748b';
    ctx.fillRect(-5, -1 + flapDeflect, 10, 2.5); // Ruderklappe
    if (Math.abs(flapDeflect) > 0.8) {
      // Glänzender Hydraulik-Zylinder
      line(ctx, -2, -1, -2, -1 + flapDeflect, '#cbd5e1', 1.2);
    }
    ctx.restore();

    // Steuerbord-Elevon
    ctx.save();
    ctx.translate(stbdWingX, stbdWingY);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-6, -2, 12, 4);
    ctx.fillStyle = '#475569';
    ctx.fillRect(-5, -1 - flapDeflect, 10, 2.5);
    if (Math.abs(flapDeflect) > 0.8) {
      line(ctx, 2, -1, 2, -1 - flapDeflect, '#cbd5e1', 1.2);
    }
    ctx.restore();

    // 2. Twin 30mm Forward Autocannon Micro-Elevation
    const gunX = cx - 152, gunY = cy + 30 + heave;
    ctx.save();
    ctx.translate(gunX, gunY);
    // Doppel-Geschützrohre mit Mündungsfeuerdämpfern
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-12, -2 - gunPitch, 14, 2);
    ctx.fillRect(-12, 1 - gunPitch, 14, 2);
    p(ctx, -12, -2 - gunPitch, '#94a3b8');
    p(ctx, -12, 1 - gunPitch, '#94a3b8');
    ctx.restore();

    // 3. FLIR Bug-Sensorturm mit hybridem Maus- & Autonomous-Tracking
    if (state.flirEnabled) {
      const flirBaseX = cx - 166, flirBaseY = cy + 12 + heave;
      // Vektor zur Mausposition oder autonomer Scan
      const isMouseActive = Math.hypot(fx.targetPointerX, fx.targetPointerY) > 0.04;
      let targetAngle = 0;
      if (isMouseActive) {
        const mouseWorldX = (fx.targetPointerX + 0.5) * BASE_WIDTH;
        const mouseWorldY = (fx.targetPointerY + 0.5) * BASE_HEIGHT;
        targetAngle = Math.atan2(mouseWorldY - flirBaseY, mouseWorldX - flirBaseX);
      } else {
        // Autonomer Such-Scan im Hangar (schwenkt langsam nach links über die Crew)
        targetAngle = Math.PI * 0.96 + Math.sin(t * 0.45) * 0.45 + Math.sin(t * 1.1) * 0.12;
      }

      // Geschmeidige Dämpfung
      let angleDiff = targetAngle - fx.flirAngle;
      while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
      while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
      fx.flirAngle += angleDiff * 0.08;

      ctx.save();
      ctx.translate(flirBaseX, flirBaseY);
      ctx.rotate(fx.flirAngle);

      // Sphärischer FLIR-Sensorkopf
      const flirGrad = ctx.createRadialGradient(0, 0, 1, 0, 0, 9);
      flirGrad.addColorStop(0, '#94a3b8');
      flirGrad.addColorStop(0.6, '#334155');
      flirGrad.addColorStop(1, '#0f172a');
      ctx.fillStyle = flirGrad;
      ctx.beginPath();
      ctx.arc(0, 0, 9, 0, Math.PI * 2);
      ctx.fill();

      // Goldener Vergütungsring um die FLIR-Optik
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(4, 0, 5.2, 0, Math.PI * 2);
      ctx.stroke();

      // Vergütete Kameralinse
      const lensCol = state.redAlert ? '#ef4444' : '#00f0ff';
      ctx.fillStyle = lensCol;
      ctx.beginPath();
      ctx.arc(4, 0, 4.5, 0, Math.PI * 2);
      ctx.fill();
      p(ctx, 5, -1, '#ffffff');

      // Cyan-farbene Laser-Designator-Diode im Zentrum
      p(ctx, 4, 0, '#ffffff');

      ctx.restore();
    }

    // 4. Militärische Luftfahrt-Beleuchtung (Aviation Strobes & Nav Lights)
    if (state.strobeEnabled) {
      // Doppelblitz-Strobe am Heck
      const strobeT = t % 1.4;
      const isStrobe = (strobeT < 0.07) || (strobeT > 0.16 && strobeT < 0.23);
      const strobeX = cx + 253, strobeY = cy - 120 + heave;

      if (isStrobe) {
        ctx.save();
        const sGrad = ctx.createRadialGradient(strobeX, strobeY, 0, strobeX, strobeY, 32);
        sGrad.addColorStop(0, '#ffffff');
        sGrad.addColorStop(0.3, 'rgba(255, 255, 255, 0.85)');
        sGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = sGrad;
        ctx.beginPath();
        ctx.arc(strobeX, strobeY, 32, 0, Math.PI * 2);
        ctx.fill();

        // Anamorpher Linsenstreifen
        ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
        ctx.fillRect(strobeX - 35, strobeY - 1, 70, 2);
        ctx.restore();
      }

      // Nav-Lights (Konstant Backbord Rot / Steuerbord Grün)
      const portX = cx - 46, portY = cy - 13 + heave;
      glow(ctx, portX, portY, 8, 'rgba(239, 68, 68, 0.7)', 0.8);
      p(ctx, portX, portY, '#ffffff');

      const stbdX = cx + 24, stbdY = cy + 35 + heave;
      glow(ctx, stbdX, stbdY, 8, 'rgba(34, 197, 94, 0.7)', 0.8);
      p(ctx, stbdX, stbdY, '#ffffff');
    }
  }

  // --- 8b. FLATTERNDE EINSATZBEFEHLE AM CATWALK-GELÄNDER ----------------------
  function renderFlutteringPapers(ctx, dt) {
    const clipX = 134, clipY = 216;
    const t = fx.time;
    // Durchzug der Decken-Turbinen lässt das Dokument-Papier vibrieren
    const flutter = Math.sin(t * 8.5) * 1.3 + Math.sin(t * 14.5) * 0.7;

    ctx.save();
    // Papierblatt mit dynamisch aufgebogener unterer Kante
    ctx.fillStyle = '#f8fafc';
    ctx.beginPath();
    ctx.moveTo(clipX + 1, clipY + 2);
    ctx.lineTo(clipX + 10, clipY + 2);
    ctx.lineTo(clipX + 10, clipY + 11);
    ctx.lineTo(clipX + 8 + flutter * 0.4, clipY + 13 - Math.max(0, flutter));
    ctx.lineTo(clipX + 1, clipY + 13);
    ctx.closePath();
    ctx.fill();

    // Rote Prioritäts-Stempel-Markierung & gedruckte Einsatzzeilen
    line(ctx, clipX + 2, clipY + 4, clipX + 8, clipY + 4, '#475569', 0.8);
    line(ctx, clipX + 2, clipY + 6, clipX + 7, clipY + 6, '#475569', 0.8);
    line(ctx, clipX + 2, clipY + 8, clipX + 9, clipY + 8, '#dc2626', 0.8);

    // Klemmen-Highlight
    p(ctx, clipX + 5, clipY - 1, '#cbd5e1');
    ctx.restore();
  }

  // --- 8. DYNAMISCHES RIM-LIGHTING -----------------------------------------
  function renderDynamicRimLight(ctx, px, py) {
    const cx = 720, cy = 340;
    const intensity = Math.min(1, Math.hypot(px, py) * 2.2 + 0.35);
    const rimCol = state.redAlert ? 'rgba(248, 113, 113, ' : 'rgba(255, 255, 255, ';

    ctx.save();
    ctx.translate(cx, cy);

    if (px < 0) {
      const a = Math.min(0.9, -px * 1.8 * intensity);
      ctx.strokeStyle = rimCol + a + ')';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(-165, -4);
      ctx.lineTo(-145, -30);
      ctx.lineTo(-95, -50);
      ctx.lineTo(-40, -58);
      ctx.stroke();

      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(8, -92);
      ctx.lineTo(-225, -74);
      ctx.stroke();
    } else {
      const a = Math.min(0.9, px * 1.8 * intensity);
      ctx.strokeStyle = rimCol + a + ')';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(228, -62);
      ctx.lineTo(252, -114);
      ctx.lineTo(268, -108);
      ctx.stroke();

      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(110, -40);
      ctx.lineTo(138, -18);
      ctx.stroke();
    }

    const glintX = -30 + px * 140;
    const gGrad = ctx.createRadialGradient(glintX, -18, 0, glintX, -18, 38);
    gGrad.addColorStop(0, rimCol + (0.5 * intensity) + ')');
    gGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = gGrad;
    ctx.beginPath();
    ctx.arc(glintX, -18, 38, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  // --- 8b. DYNAMISCH DREHENDER HAUPTROTOR & VOLUMETRISCHER SCHATTENWURF -------
  function renderRotatingRotor(ctx, dt) {
    if (state.animationsEnabled) {
      // Ganz langsame, majestische Leerlauf-Drehung (ca. 16.5 Sekunden pro Umdrehung)
      fx.rotorAngle = (fx.rotorAngle + dt * 0.38) % (Math.PI * 2);
    }

    const mastX = 735;
    const mastY = 248;
    const numBlades = 5;
    const bladeStep = (Math.PI * 2) / numBlades;

    // Flachere 2.5D-Ellipsenachse (Ry stark komprimiert, da nicht mehr steil von oben geschaut wird)
    // und nach hinten geneigter Pitch-Winkel (+0.065 rad = Rumpf- & Rotoraft-Tilt)
    const Rx = 232;
    const Ry = 24;
    const pitch = 0.065; // Nach hinten geneigt (Bug leicht angehoben, Heck nach unten)
    const cosP = Math.cos(pitch);
    const sinP = Math.sin(pitch);

    function getBladePoints(centerX, centerY, radiusX, radiusY, angle) {
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);

      // Elliptische 2.5D-Projektion rotiert um den Neigungswinkel
      const uTip = cosA * radiusX;
      const vTip = sinA * radiusY;
      const tipX = centerX + (uTip * cosP - vTip * sinP);
      const tipY = centerY + (uTip * sinP + vTip * cosP);

      const rootR = 14;
      const uRoot = cosA * rootR;
      const vRoot = sinA * (rootR * (radiusY / radiusX));
      const rootX = centerX + (uRoot * cosP - vRoot * sinP);
      const rootY = centerY + (uRoot * sinP + vRoot * cosP);

      return { rootX, rootY, tipX, tipY, angle };
    }

    // =========================================================================
    // A. WEICHER BODEN- & DOCKING-PLATTFORM-SCHATTEN (Tiefe Ebene)
    // =========================================================================
    ctx.save();
    const gMastX = mastX + 12;
    const gMastY = mastY + 142; // Auf Höhe der Docking-Schienen & Boden (Y ≈ 390)
    const gRx = 244;
    const gRy = 26;

    for (let i = 0; i < numBlades; i++) {
      const bAngle = fx.rotorAngle + i * bladeStep;
      const b = getBladePoints(gMastX, gMastY, gRx, gRy, bAngle);
      const dx = b.tipX - b.rootX;
      const dy = b.tipY - b.rootY;
      const len = Math.hypot(dx, dy);
      if (len < 1) continue;
      const nx = -dy / len;
      const ny = dx / len;
      const sw = 14; // Breiterer, weicherer Halbschatten

      ctx.save();
      ctx.fillStyle = 'rgba(2, 6, 18, 0.14)';
      ctx.beginPath();
      ctx.moveTo(b.rootX - nx * sw * 0.4, b.rootY - ny * sw * 0.4);
      ctx.lineTo(b.tipX - nx * sw * 0.6, b.tipY - ny * sw * 0.6);
      ctx.lineTo(b.tipX + nx * sw * 0.6, b.tipY + ny * sw * 0.6);
      ctx.lineTo(b.rootX + nx * sw * 0.4, b.rootY + ny * sw * 0.4);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // Zentraler Bodenschatten der Rotornabe
    ctx.fillStyle = 'rgba(2, 6, 18, 0.18)';
    ctx.beginPath();
    ctx.ellipse(gMastX, gMastY, 20, 6.5, pitch, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // =========================================================================
    // B. PRÄZISER RUMPF- & KANZEL-SCHATTEN (Körper-Oberfläche)
    // =========================================================================
    ctx.save();
    const fMastX = mastX + 6;
    const fMastY = mastY + 24; // Direkt auf Cockpitdach, Triebwerkshuckel und Flügel (Y ≈ 272)
    const fRx = 224;
    const fRy = 22;

    // Wir clippen den Rumpfschatten auf den Bereich des A9 Piranha (X: 510..950, Y: 250..375)
    ctx.save();
    ctx.beginPath();
    ctx.rect(510, 250, 440, 125);
    ctx.clip();

    for (let i = 0; i < numBlades; i++) {
      const bAngle = fx.rotorAngle + i * bladeStep;
      const b = getBladePoints(fMastX, fMastY, fRx, fRy, bAngle);
      const dx = b.tipX - b.rootX;
      const dy = b.tipY - b.rootY;
      const len = Math.hypot(dx, dy);
      if (len < 1) continue;
      const nx = -dy / len;
      const ny = dx / len;
      const sw = 12;

      // Kernschatten (Umbra)
      ctx.fillStyle = 'rgba(2, 6, 18, 0.28)';
      ctx.beginPath();
      ctx.moveTo(b.rootX - nx * sw * 0.35, b.rootY - ny * sw * 0.35);
      ctx.lineTo(b.tipX - nx * sw * 0.45, b.tipY - ny * sw * 0.45);
      ctx.lineTo(b.tipX + nx * sw * 0.45, b.tipY + ny * sw * 0.45);
      ctx.lineTo(b.rootX + nx * sw * 0.35, b.rootY + ny * sw * 0.35);
      ctx.closePath();
      ctx.fill();

      // Weichere Randzone (Penumbra)
      ctx.fillStyle = 'rgba(2, 6, 18, 0.10)';
      ctx.beginPath();
      ctx.moveTo(b.rootX - nx * sw * 0.6, b.rootY - ny * sw * 0.6);
      ctx.lineTo(b.tipX - nx * sw * 0.75, b.tipY - ny * sw * 0.75);
      ctx.lineTo(b.tipX + nx * sw * 0.75, b.tipY + ny * sw * 0.75);
      ctx.lineTo(b.rootX + nx * sw * 0.6, b.rootY + ny * sw * 0.6);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore(); // clip
    ctx.restore();

    // =========================================================================
    // C. DIE DREHENDEN 5 ROTORBLÄTTER MIT 2.5D-LICHT & TITANKANTEN
    // =========================================================================
    // Sortiere die Blätter nach Y (Tiefenordnung: hintere Blätter zuerst zeichnen)
    const bladeList = [];
    for (let i = 0; i < numBlades; i++) {
      const bAngle = fx.rotorAngle + i * bladeStep;
      const b = getBladePoints(mastX, mastY, Rx, Ry, bAngle);
      bladeList.push(b);
    }
    bladeList.sort((a, b) => a.tipY - b.tipY);

    bladeList.forEach((b) => {
      const dx = b.tipX - b.rootX;
      const dy = b.tipY - b.rootY;
      const len = Math.hypot(dx, dy);
      if (len < 1) return;

      const ang = Math.atan2(dy, dx);

      // EIGENACHSEN-ROTATION (BLADE PITCHING / ANSTELLWINKEL-DREHUNG)
      // Jedes Rotorblatt dreht sich im Flug um seine eigene Längsachse (Pitch / Swashplate-Steuerung).
      // Im 2.5D-Blickwinkel kippt die Profilsehne je nach Umlaufwinkel von flacher Draufsicht (Breite = 7.8px)
      // bis zur messerscharfen Kantenansicht (Breite = 2.4px), während die relative Profildicke variiert.
      const pitchCycle = b.angle + 0.35;
      const pitchFactor = Math.sin(pitchCycle); // -1 (stark angestellt) bis +1 (flach/angestellt)
      const absPitch = Math.abs(pitchFactor);
      const effectiveChord = 2.4 + 5.6 * (0.35 + 0.65 * absPitch); // Dynamische Sehnenbreite (Profil-Kippung um Eigenachse)
      const tipW = effectiveChord;

      // Asymmetrische Wölbung (Camber & Anstellwinkel-Neigung der Vorderkante)
      const leadingEdgeOffset = (pitchFactor >= 0 ? -1 : 1) * (1 - absPitch) * 1.8;

      // Lichtwinkel relativ zu den Deckenleuchten (Highlight bei Ausrichtung nach vorne-links)
      const lightFactor = Math.max(0, Math.sin(b.angle + 1.2));

      ctx.save();
      ctx.translate(b.rootX, b.rootY);
      ctx.rotate(ang);

      // 1. Blade-Flügelprofil mit metallischem Hangar-Farbverlauf & Eigenrotations-Schattierung
      const bGrad = ctx.createLinearGradient(0, -tipW * 0.5 + leadingEdgeOffset, 0, tipW * 0.5 + leadingEdgeOffset);
      if (absPitch < 0.35) {
        // Messerscharfe Kantenansicht: Profilkante glänzt metallisch im Deckenlicht
        bGrad.addColorStop(0, '#cbd5e1');
        bGrad.addColorStop(0.5, '#64748b');
        bGrad.addColorStop(1, '#0f172a');
      } else {
        // Flächigere Ober-/Unterseitenansicht
        bGrad.addColorStop(0, lightFactor > 0.4 ? '#94a3b8' : '#475569');
        bGrad.addColorStop(0.3, '#334155');
        bGrad.addColorStop(0.7, '#1e293b');
        bGrad.addColorStop(1, '#090f17');
      }
      ctx.fillStyle = bGrad;

      ctx.beginPath();
      ctx.moveTo(0, -tipW * 0.35 + leadingEdgeOffset * 0.5);
      ctx.lineTo(len, -tipW * 0.5 + leadingEdgeOffset);
      ctx.lineTo(len, tipW * 0.5 + leadingEdgeOffset);
      ctx.lineTo(0, tipW * 0.35 + leadingEdgeOffset * 0.5);
      ctx.closePath();
      ctx.fill();

      // 2. Karbongewebe-Mikroschraffur entlang der Blattsehne (nur sichtbar wenn Blatt nicht auf Kante steht)
      if (absPitch > 0.25) {
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.08 * absPitch})`;
        ctx.lineWidth = 1;
        for (let bx = 18; bx < len - 26; bx += 7) {
          ctx.beginPath();
          ctx.moveTo(bx, -tipW * 0.35 + leadingEdgeOffset);
          ctx.lineTo(bx + 3, tipW * 0.35 + leadingEdgeOffset);
          ctx.stroke();
        }
      }

      // 3. Glänzende Titan-Erosionsschutzleiste an der Anströmkante (folgt der Eigenrotations-Kante)
      const specAlpha = (0.4 + 0.6 * lightFactor) * (0.6 + 0.4 * (1 - absPitch));
      ctx.strokeStyle = `rgba(241, 245, 249, ${specAlpha})`;
      ctx.lineWidth = absPitch < 0.35 ? 1.8 : 1.4;
      ctx.beginPath();
      ctx.moveTo(0, -tipW * 0.35 + leadingEdgeOffset * 0.5);
      ctx.lineTo(len, -tipW * 0.5 + leadingEdgeOffset);
      ctx.stroke();

      // Zinkchromat-Primer-Kante
      ctx.strokeStyle = 'rgba(202, 138, 4, 0.45)';
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(0, -tipW * 0.35 + leadingEdgeOffset * 0.5 + 1);
      ctx.lineTo(len, -tipW * 0.5 + leadingEdgeOffset + 1);
      ctx.stroke();

      // 4. Gelb-schwarze Hazard-Streifen an der Blattspitze
      const hazLen = 22;
      const hazX = len - hazLen;
      const hazY = -tipW * 0.5 + leadingEdgeOffset;
      for (let hx = 0; hx < hazLen; hx += 5) {
        ctx.fillStyle = (Math.floor(hx / 5) % 2 === 0) ? '#eab308' : '#090f17';
        ctx.fillRect(hazX + hx, hazY, 5, Math.max(2, tipW));
      }

      // 5. Blattwurzel-Manschette & Schmierbolzen
      ctx.fillStyle = '#475569';
      ctx.fillRect(-2, -tipW * 0.35, 6, Math.max(3, tipW * 0.7));
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, -tipW * 0.2, 3, Math.max(2, tipW * 0.4));

      ctx.restore();
    });

    // =========================================================================
    // D. DREHENDE ROTORKOPF-KAPPE (Hub Mast Cap)
    // =========================================================================
    ctx.save();
    ctx.translate(mastX, mastY);

    // Kappen-Sockel (perspektivisch flacher)
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.ellipse(0, 0, 10, 3.2, pitch, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#334155';
    ctx.beginPath();
    ctx.ellipse(0, -1.5, 8.5, 2.7, pitch, 0, Math.PI * 2);
    ctx.fill();

    // Zentraler Mastbolzen mit Rotations-Glint
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.ellipse(0, -2.5, 4.2, 1.5, pitch, 0, Math.PI * 2);
    ctx.fill();

    p(ctx, 0, -2.5, '#ffffff');
    ctx.restore();
  }

  // --- 9. HOLOGRAPHISCHES COCKPIT-HUD ---------------------------------------
  function renderHolographicHUD(ctx, px, py) {
    const cx = 720, cy = 340;
    const hudCol = state.redAlert ? '#ef4444' : '#00f0ff';
    const hudSoft = state.redAlert ? 'rgba(239, 68, 68, 0.6)' : 'rgba(0, 240, 255, 0.6)';

    ctx.save();
    ctx.translate(cx, cy);

    const hudX = -110, hudY = -26;
    const tilt = px * 0.45;

    ctx.save();
    ctx.translate(hudX, hudY);
    ctx.rotate(tilt);

    ctx.strokeStyle = hudCol;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-12, 0); ctx.lineTo(-4, 0);
    ctx.moveTo(4, 0); ctx.lineTo(12, 0);
    ctx.moveTo(0, -3); ctx.lineTo(0, 3);
    ctx.stroke();

    ctx.strokeStyle = hudSoft;
    ctx.strokeRect(-8, -6, 16, 2);
    ctx.strokeRect(-8, 4, 16, 2);

    ctx.restore();

    ctx.font = '6px monospace';
    ctx.fillStyle = hudCol;
    ctx.fillText('RDY', hudX - 12, hudY - 9);
    ctx.fillText('A9-01', hudX + 2, hudY - 9);

    const lockPulse = 0.6 + Math.sin(fx.time * 6) * 0.4;
    ctx.strokeStyle = state.redAlert ? `rgba(239,68,68,${lockPulse})` : `rgba(56,189,248,${lockPulse})`;
    ctx.strokeRect(-162, 4, 12, 12);

    ctx.restore();
  }

  // --- 10. HITZEFLIMMERN / THERMAL HAZE -------------------------------------
  function renderThermalHaze(ctx) {
    const tx = 836, ty = 302;
    const bands = 14;

    ctx.save();
    ctx.globalCompositeOperation = 'overlay';

    for (let i = 0; i < bands; i++) {
      const phase = fx.time * 18 - i * 0.42;
      const waveY = ty - 12 + i * 2.8 + Math.sin(phase) * 3;
      const waveX = tx + i * 4.2 + Math.cos(phase * 0.8) * 2;
      const waveW = 14 + i * 3.5;
      const alpha = (1 - i / bands) * 0.22;

      ctx.fillStyle = i % 2 === 0
        ? `rgba(255, 255, 255, ${alpha})`
        : (state.redAlert ? `rgba(239, 68, 68, ${alpha * 0.8})` : `rgba(56, 189, 248, ${alpha * 0.7})`);

      ctx.beginPath();
      ctx.ellipse(waveX, waveY, waveW, 3.5 + Math.sin(phase) * 1.5, Math.PI * 0.06, 0, Math.PI * 2);
      ctx.fill();
    }

    const sx = 84, sy = 158;
    for (let i = 0; i < 8; i++) {
      const phase = fx.time * 12 - i * 0.5;
      const wy = sy - i * 3 + Math.sin(phase) * 2;
      ctx.fillStyle = `rgba(251, 191, 36, ${(1 - i / 8) * 0.12})`;
      ctx.beginPath();
      ctx.ellipse(sx + Math.cos(phase) * 3, wy, 8 + i * 2, 2.5, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Subtile chromatische Aberration an den heißen Turbinenflanken (1-1.5px RGB-Shift)
    ctx.globalCompositeOperation = 'screen';
    const caOffset = 1.2 + Math.sin(fx.time * 8) * 0.3;
    ctx.fillStyle = 'rgba(239, 68, 68, 0.16)';
    ctx.fillRect(tx - 6 - caOffset, ty - 8, 2, 24);
    ctx.fillStyle = 'rgba(56, 189, 248, 0.16)';
    ctx.fillRect(tx - 6 + caOffset, ty - 8, 2, 24);

    ctx.restore();
  }

  // --- 11. WET FLOOR & BODEN-REFLEXIONEN (HD-2D Specular Puddle Pass) --------
  function renderFloorReflections(ctx) {
    const floorTop = 425;

    // 1. Red Alert Alarm-Beacon Reflexionssäule
    if (state.redAlert) {
      const flash = (Math.sin(fx.time * 8) + 1) * 0.5;
      const bGrad = ctx.createLinearGradient(460, floorTop, 460, 560);
      bGrad.addColorStop(0, `rgba(239, 68, 68, ${0.32 * flash})`);
      bGrad.addColorStop(1, 'rgba(239, 68, 68, 0)');
      ctx.fillStyle = bGrad;
      ctx.fillRect(446, floorTop, 28, 135);
    }

    // 2. A9 Piranha Sensor-Optik Reflexionssäule
    const eyeRefX = 555;
    const eyeColor = state.redAlert ? 'rgba(239, 68, 68, ' : 'rgba(0, 240, 255, ';
    const eGrad = ctx.createLinearGradient(eyeRefX, floorTop + 10, eyeRefX, 550);
    eGrad.addColorStop(0, eyeColor + '0.24)');
    eGrad.addColorStop(1, eyeColor + '0)');
    ctx.fillStyle = eGrad;
    ctx.fillRect(eyeRefX - 8, floorTop + 10, 16, 95);

    // 3. Drohnen-Schweißfunken & Ionen-Flammen Bodenreflexion
    if (fx.drone.mode === 'weld') {
      ctx.save();
      const wGrad = ctx.createRadialGradient(fx.drone.x, 475, 0, fx.drone.x, 475, 110);
      wGrad.addColorStop(0, 'rgba(255, 255, 255, 0.42)');
      wGrad.addColorStop(0.3, state.redAlert ? 'rgba(239, 68, 68, 0.28)' : 'rgba(56, 189, 248, 0.28)');
      wGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.scale(1, 0.35);
      ctx.fillStyle = wGrad;
      ctx.beginPath();
      ctx.arc(fx.drone.x, 475 / 0.35, 110, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else {
      // Wenn Drohne patrouilliert: Zarte Cyan-Spiegelung der Ionen-Triebwerke
      const dRefX = fx.drone.x;
      if (dRefX > 50 && dRefX < BASE_WIDTH - 50) {
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        const dGrad = ctx.createRadialGradient(dRefX, 470, 0, dRefX, 470, 36);
        dGrad.addColorStop(0, 'rgba(56, 189, 248, 0.18)');
        dGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.scale(1, 0.3);
        ctx.fillStyle = dGrad;
        ctx.beginPath();
        ctx.arc(dRefX, 470 / 0.3, 36, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    // 4. Ground Rover Reflexionen: Rotierende Amber-Rundumleuchte & Scheinwerfer
    if (state.craneEnabled && fx.rover.x > -60 && fx.rover.x < BASE_WIDTH + 60) {
      const rx = fx.rover.x + 38;
      const rBeaconFlash = 0.5 + Math.sin(fx.rover.beaconAngle) * 0.5;

      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const rGrad = ctx.createRadialGradient(rx, 490, 0, rx, 490, 48);
      rGrad.addColorStop(0, `rgba(251, 191, 36, ${0.28 * rBeaconFlash})`);
      rGrad.addColorStop(1, 'rgba(245, 158, 11, 0)');
      ctx.scale(1, 0.32);
      ctx.fillStyle = rGrad;
      ctx.beginPath();
      ctx.arc(rx, 490 / 0.32, 48, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Scheinwerferkegel auf dem nassen Boden vor dem Rover
      const headX = fx.rover.speed < 0 ? fx.rover.x : fx.rover.x + 76;
      const headDir = fx.rover.speed < 0 ? -1 : 1;
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const hGrad = ctx.createLinearGradient(headX, 488, headX + headDir * 55, 488);
      hGrad.addColorStop(0, 'rgba(254, 243, 199, 0.22)');
      hGrad.addColorStop(1, 'rgba(254, 243, 199, 0)');
      ctx.fillStyle = hGrad;
      ctx.beginPath();
      ctx.moveTo(headX, 485);
      ctx.lineTo(headX + headDir * 55, 480);
      ctx.lineTo(headX + headDir * 55, 496);
      ctx.lineTo(headX, 491);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // 5. Gitterrost-Texturierung (Wet Grating Ridges)
    ctx.save();
    ctx.fillStyle = 'rgba(5, 10, 18, 0.24)';
    for (let gy = 432; gy <= 548; gy += 6) {
      ctx.fillRect(40, gy, BASE_WIDTH - 80, 1.2);
    }
    ctx.restore();
  }

  // --- 12. CINEMATIC BLOOM PASS --------------------------------------------
  function renderBloomPass(mainCtx) {
    bloomCtx.clearRect(0, 0, 512, 288);

    bloomCtx.save();
    bloomCtx.scale(0.5, 0.5);

    const sensorX = 555, sensorY = 352;
    const eyeCol = state.redAlert ? '#ef4444' : '#00f0ff';
    const bg1 = bloomCtx.createRadialGradient(sensorX, sensorY, 0, sensorX, sensorY, 32);
    bg1.addColorStop(0, eyeCol);
    bg1.addColorStop(1, 'rgba(0,0,0,0)');
    bloomCtx.fillStyle = bg1;
    bloomCtx.beginPath();
    bloomCtx.arc(sensorX, sensorY, 32, 0, Math.PI * 2);
    bloomCtx.fill();

    if (fx.drone.mode === 'weld') {
      const bg2 = bloomCtx.createRadialGradient(fx.drone.x + 4, fx.drone.y + 18, 0, fx.drone.x + 4, fx.drone.y + 18, 110);
      bg2.addColorStop(0, '#ffffff');
      bg2.addColorStop(0.3, state.redAlert ? '#ef4444' : '#38bdf8');
      bg2.addColorStop(1, 'rgba(0,0,0,0)');
      bloomCtx.fillStyle = bg2;
      bloomCtx.beginPath();
      bloomCtx.arc(fx.drone.x + 4, fx.drone.y + 18, 110, 0, Math.PI * 2);
      bloomCtx.fill();

      bloomCtx.fillStyle = '#ffffff';
      for (const s of fx.sparks) {
        if (s.life > 0.45) {
          bloomCtx.fillRect(s.x - 1, s.y - 1, 4, 4);
        }
      }
    }

    bloomCtx.fillStyle = state.redAlert ? 'rgba(239, 68, 68, 0.4)' : 'rgba(0, 240, 255, 0.35)';
    bloomCtx.fillRect(20, 154, 82, 58);
    bloomCtx.fillRect(106, 174, 58, 42);

    if (state.redAlert) {
      const flash = (Math.sin(fx.time * 8) + 1) * 0.5;
      const bg3 = bloomCtx.createRadialGradient(460, 42, 0, 460, 42, 90);
      bg3.addColorStop(0, `rgba(239, 68, 68, ${0.8 * flash})`);
      bg3.addColorStop(1, 'rgba(0,0,0,0)');
      bloomCtx.fillStyle = bg3;
      bloomCtx.beginPath();
      bloomCtx.arc(460, 42, 90, 0, Math.PI * 2);
      bloomCtx.fill();
    }

    bloomCtx.restore();

    mainCtx.save();
    mainCtx.globalCompositeOperation = 'screen';
    mainCtx.filter = 'blur(8px)';
    mainCtx.drawImage(bloomCanvas, 0, 0, BASE_WIDTH, BASE_HEIGHT);
    mainCtx.filter = 'blur(18px)';
    mainCtx.globalAlpha = 0.65;
    mainCtx.drawImage(bloomCanvas, 0, 0, BASE_WIDTH, BASE_HEIGHT);
    mainCtx.restore();
  }

  // --- 13. VOLUMETRISCHE GOD-RAYS (Atmosphärische Decken-Lichtkegel) ----------
  function renderVolumetricGodRays(ctx, dt) {
    if (!state.godRaysEnabled) return;

    ctx.save();
    ctx.globalCompositeOperation = 'screen';

    for (let i = 0; i < fx.godRays.length; i++) {
      const ray = fx.godRays[i];
      const breath = 0.8 + Math.sin(fx.time * ray.speed + ray.phase) * 0.2;

      // Kran-Okklusion: wenn der Deckenkran durch den Lichtkegel fährt, wirft die Fracht einen Schatten
      let craneShadow = 1.0;
      if (state.craneEnabled) {
        const craneX = fx.crane.x;
        const midRayX = ray.topX + (ray.slant * 0.3);
        const dist = Math.abs(craneX - midRayX);
        if (dist < 80) {
          craneShadow = 0.35 + (dist / 80) * 0.65;
        }
      }

      const topY = 0;
      const botY = 560;
      const x1 = ray.topX;
      const x2 = ray.topX + ray.topW;
      const x3 = ray.topX + ray.slant + ray.botW;
      const x4 = ray.topX + ray.slant;

      if (!ray._grad || ray._lastRedAlert !== state.redAlert) {
        ray._lastRedAlert = state.redAlert;
        ray._grad = ctx.createLinearGradient(
          ray.topX + ray.topW * 0.5, topY,
          ray.topX + ray.slant + ray.botW * 0.5, botY
        );
        ray._grad.addColorStop(0, state.redAlert ? 'rgba(254, 202, 202, 1.0)' : 'rgba(255, 255, 255, 1.0)');
        ray._grad.addColorStop(0.2, state.redAlert ? 'rgba(239, 68, 68, 1.0)' : 'rgba(254, 243, 199, 1.0)');
        ray._grad.addColorStop(0.65, state.redAlert ? 'rgba(220, 38, 38, 0.55)' : 'rgba(186, 230, 253, 0.50)');
        ray._grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      }

      const baseAlpha = (state.redAlert ? 0.08 : 0.055) * breath * craneShadow;
      ctx.save();
      ctx.globalAlpha = baseAlpha;
      ctx.fillStyle = ray._grad;
      ctx.beginPath();
      ctx.moveTo(x1, topY);
      ctx.lineTo(x2, topY);
      ctx.lineTo(x3, botY);
      ctx.lineTo(x4, botY);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // Feine beleuchtete Staubpartikel im Lichtkegel
      const moteCount = 4;
      for (let m = 0; m < moteCount; m++) {
        const prog = ((fx.time * 0.07 + m / moteCount + ray.phase * 0.1) % 1);
        const my = topY + prog * (botY - topY);
        const mx = ray.topX + (ray.topW * 0.5) + (prog * ray.slant) + Math.sin(fx.time + m) * (ray.botW * 0.25);
        const mAlpha = Math.sin(prog * Math.PI) * 0.45 * breath * craneShadow;
        if (mAlpha > 0.05) {
          ctx.fillStyle = state.redAlert ? `rgba(254, 202, 202, ${mAlpha})` : `rgba(255, 255, 255, ${mAlpha})`;
          ctx.fillRect(Math.round(mx), Math.round(my), 1.5, 1.5);
        }
      }
    }

    ctx.restore();
  }

  // --- 14. VORDERGRUND LENS BOKEH (Kinoreife Unschärfe mit zentrischen Gradient-Kugeln) ---------
  function renderBokehDiscs(ctx, dt) {
    if (!state.bokehEnabled) return;

    ctx.save();
    ctx.globalCompositeOperation = 'screen';

    // Hilfsfunktion: Zeichnet optisch weiche, zentrisch verlaufende Bokeh-Kugeln (Airy Disc / Lens Blur)
    function drawConcentricBokehSphere(ctx, cx, cy, r, baseRgb, rimRgb, alpha, tier, phase, sx, sy) {
      if (!ctx || !r || r <= 0.5 || !alpha || alpha <= 0.005 || isNaN(alpha)) return;
      const safeAlpha = Math.max(0, Math.min(1, Number(alpha) || 0));

      const lut = getBokehSphereLUT(tier, baseRgb, rimRgb);
      if (lut && ctx.drawImage) {
        ctx.save();
        ctx.translate(cx, cy);
        if (sx !== 1 || sy !== 1) {
          ctx.scale(sx || 1, sy || 1);
        }
        ctx.globalAlpha = safeAlpha;
        const scale = r / 40;
        const size = 160 * scale;
        ctx.drawImage(lut, -size * 0.5, -size * 0.5, size, size);
        ctx.restore();
        return;
      }

      ctx.save();
      ctx.translate(cx, cy);
      if (sx !== 1 || sy !== 1) {
        ctx.scale(sx || 1, sy || 1);
      }

      // 1. Weicher äußerer Unschärfe-Halo (Gaussian Blur Aura um die Kugel)
      const haloR = r * (tier === 'macro' ? 1.55 : (tier === 'mid' ? 1.35 : 1.2));
      const haloGrad = ctx.createRadialGradient(0, 0, r * 0.2, 0, 0, haloR);
      haloGrad.addColorStop(0, `rgba(${baseRgb}, ${(safeAlpha * 0.38).toFixed(3)})`);
      haloGrad.addColorStop(0.55, `rgba(${rimRgb}, ${(safeAlpha * 0.16).toFixed(3)})`);
      haloGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = haloGrad;
      ctx.beginPath();
      ctx.arc(0, 0, haloR, 0, Math.PI * 2);
      ctx.fill();

      // 2. Zentrische Haupt-Bokeh-Kugel mit kontinuierlichem Gradienten & hellem Airy-Beugungsrand
      const mainGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      mainGrad.addColorStop(0.0, `rgba(255, 255, 255, ${(safeAlpha * 0.65).toFixed(3)})`);
      mainGrad.addColorStop(0.25, `rgba(${baseRgb}, ${(safeAlpha * 0.52).toFixed(3)})`);
      mainGrad.addColorStop(0.60, `rgba(${baseRgb}, ${(safeAlpha * 0.32).toFixed(3)})`);
      mainGrad.addColorStop(0.84, `rgba(${rimRgb}, ${(safeAlpha * 0.68).toFixed(3)})`); // Konzentrischer heller Beugungsrand
      mainGrad.addColorStop(0.95, `rgba(${rimRgb}, ${(safeAlpha * 0.35).toFixed(3)})`);
      mainGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');                                    // 100% Weich auslaufende Kante (Blur)
      ctx.fillStyle = mainGrad;
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();

      // 3. Konzentrischer innerer Fokus-Kern
      const coreR = r * (tier === 'macro' ? 0.36 : (tier === 'mid' ? 0.44 : 0.65));
      const coreGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, coreR);
      coreGrad.addColorStop(0.0, `rgba(255, 255, 255, ${(safeAlpha * 0.85).toFixed(3)})`);
      coreGrad.addColorStop(0.55, `rgba(${baseRgb}, ${(safeAlpha * 0.45).toFixed(3)})`);
      coreGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(0, 0, coreR, 0, Math.PI * 2);
      ctx.fill();

      // 4. Chromatische Aberrations-Kugel (Zentrisch minimal versetzter Farbsaum)
      if (tier !== 'sparkle') {
        const caDist = tier === 'macro' ? 2.4 : 1.2;
        const caX = Math.cos(phase) * caDist;
        const caY = Math.sin(phase) * caDist;
        const caGrad = ctx.createRadialGradient(caX, caY, r * 0.5, caX, caY, r * 1.06);
        caGrad.addColorStop(0.0, 'rgba(0, 0, 0, 0)');
        caGrad.addColorStop(0.72, 'rgba(0, 0, 0, 0)');
        caGrad.addColorStop(0.90, `rgba(${rimRgb}, ${(safeAlpha * 0.45).toFixed(3)})`);
        caGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = caGrad;
        ctx.beginPath();
        ctx.arc(caX, caY, r * 1.06, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }

    for (let i = 0; i < fx.bokehDiscs.length; i++) {
      const d = fx.bokehDiscs[i];
      d.x += (d.vx + Math.sin(fx.time * 0.45 + d.phase) * 2.2) * dt;
      d.y += d.vy * dt;

      if (d.y < -70) {
        d.y = BASE_HEIGHT + 45;
        d.x = Math.random() * BASE_WIDTH;
      }
      if (d.x < -70) d.x = BASE_WIDTH + 45;
      if (d.x > BASE_WIDTH + 70) d.x = -45;

      // 1. Licht-Reaktivität: Glimmt warm auf, wenn Partikel durch Decken-God-Rays schweben
      let lightBoost = 1.0;
      for (let gi = 0; gi < fx.godRays.length; gi++) {
        const ray = fx.godRays[gi];
        const rayCenterAtY = ray.topX + (d.y / 576) * ray.slant;
        const dist = Math.abs(d.x - rayCenterAtY);
        if (dist < 65) {
          lightBoost += (1.0 - dist / 65) * 0.95;
        }
      }

      const alphaPulse = d.baseAlpha * lightBoost * (0.82 + Math.sin(fx.time * 1.6 + d.phase) * 0.28);
      const sx = d.aspect || 1.12;
      const sy = 0.94;

      let baseRgb, rimRgb;
      if (state.redAlert) {
        baseRgb = '248, 113, 113';
        rimRgb = '239, 68, 68';
      } else if (d.type === 'amber') {
        baseRgb = '251, 191, 36';
        rimRgb = '245, 158, 11';
      } else if (d.type === 'cyan') {
        baseRgb = '56, 189, 248';
        rimRgb = '14, 165, 233';
      } else {
        baseRgb = '254, 240, 138';
        rimRgb = '251, 191, 36';
      }

      drawConcentricBokehSphere(ctx, d.x, d.y, d.radius, baseRgb, rimRgb, alphaPulse, d.tier, d.phase + fx.time * 0.2, sx, sy);
    }

    ctx.restore();
  }

  // --- 15. ANAMORPHIC LENS FLARES (Kino-Lichtstreifen) -----------------------
  function renderAnamorphicFlares(ctx) {
    ctx.save();
    ctx.globalCompositeOperation = 'screen';

    // 1. Horizontale Anamorphe Flares beim Schweißen der Drohne
    if (fx.drone.mode === 'weld' && fx.drone.weldTimer > 0) {
      const wx = fx.drone.x + 4, wy = fx.drone.y + 18;
      const pulse = 0.7 + Math.sin(fx.time * 25) * 0.3;
      const flareW = (180 + Math.sin(fx.time * 18) * 35) * pulse;

      const fGrad = ctx.createLinearGradient(wx - flareW, wy, wx + flareW, wy);
      const flareCol = state.redAlert ? 'rgba(239, 68, 68, ' : 'rgba(56, 189, 248, ';
      fGrad.addColorStop(0, flareCol + '0)');
      fGrad.addColorStop(0.35, flareCol + '0.28)');
      fGrad.addColorStop(0.48, 'rgba(255, 255, 255, 0.9)');
      fGrad.addColorStop(0.5, '#ffffff');
      fGrad.addColorStop(0.52, 'rgba(255, 255, 255, 0.9)');
      fGrad.addColorStop(0.65, flareCol + '0.28)');
      fGrad.addColorStop(1, flareCol + '0)');

      ctx.fillStyle = fGrad;
      ctx.fillRect(wx - flareW, wy - 1, flareW * 2, 2.4);

      // Zentraler kleiner runder Haloring
      ctx.strokeStyle = state.redAlert ? 'rgba(239, 68, 68, 0.6)' : 'rgba(56, 189, 248, 0.6)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(wx, wy, 8 + Math.sin(fx.time * 15) * 2, 0, Math.PI * 2);
      ctx.stroke();
    }

    // 2. Anamorpher Flare auf der Piranha-Sensor-Optik während des Scans
    if (fx.sensor.scanActiveTimer > 0 || state.redAlert) {
      const sx = 555, sy = 352;
      const sW = 75 + Math.sin(fx.time * 12) * 20;
      const sGrad = ctx.createLinearGradient(sx - sW, sy, sx + sW, sy);
      const sCol = state.redAlert ? 'rgba(239, 68, 68, ' : 'rgba(0, 240, 255, ';
      sGrad.addColorStop(0, sCol + '0)');
      sGrad.addColorStop(0.5, sCol + '0.7)');
      sGrad.addColorStop(1, sCol + '0)');
      ctx.fillStyle = sGrad;
      ctx.fillRect(sx - sW, sy - 0.8, sW * 2, 1.6);
    }

    ctx.restore();
  }

  // --- 16. TEAL & ORANGE CINEMA COLOR GRADING & VIGNETTE --------------------
  function renderCinematicGrading(ctx) {
    if (!state.cinemaGradingEnabled) return;

    ctx.save();
    // 1. Teal-in-Shadows Farbabstimmung (Sci-Fi Blockbuster Grade)
    if (!_cachedTealGrad) {
      _cachedTealGrad = ctx.createLinearGradient(0, 0, 0, BASE_HEIGHT);
      _cachedTealGrad.addColorStop(0, 'rgba(4, 24, 38, 0.07)');
      _cachedTealGrad.addColorStop(0.6, 'rgba(0, 0, 0, 0)');
      _cachedTealGrad.addColorStop(1, 'rgba(2, 14, 24, 0.09)');
    }
    ctx.fillStyle = _cachedTealGrad;
    ctx.fillRect(0, 0, BASE_WIDTH, BASE_HEIGHT);

    // 3. Warm Amber Highlight-Balance um das Montagegerüst & Hero
    if (!_cachedWarmGrad) {
      _cachedWarmGrad = ctx.createRadialGradient(
        560, 320, 40,
        560, 320, 340
      );
      _cachedWarmGrad.addColorStop(0, 'rgba(251, 191, 36, 0.035)');
      _cachedWarmGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    }
    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = _cachedWarmGrad;
    ctx.fillRect(0, 0, BASE_WIDTH, BASE_HEIGHT);

    ctx.restore();
  }

  // --- 17. NIGHT SHIFT / PRE-DAWN MODUS (Atmosphärische Nachtbeleuchtung) ----
  function renderNightShiftLighting(ctx) {
    if (!state.nightShift) return;

    ctx.save();
    // 1. Deep Midnight-Blue Ambient Wash (Senkt die Tagesausleuchtung ab)
    ctx.fillStyle = 'rgba(4, 9, 22, 0.46)';
    ctx.fillRect(0, 0, BASE_WIDTH, BASE_HEIGHT);

    // 2. High-Contrast Illumination Pools (Glühende Arbeitsbereiche & Instrumente)
    ctx.globalCompositeOperation = 'screen';

    // A. Cockpit-Canopy Instrument-Emission
    if (!_cachedCockGlow) {
      _cachedCockGlow = ctx.createRadialGradient(610, 310, 2, 610, 310, 85);
      _cachedCockGlow.addColorStop(0, 'rgba(0, 240, 255, 0.38)');
      _cachedCockGlow.addColorStop(0.5, 'rgba(0, 240, 255, 0.12)');
      _cachedCockGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    }
    ctx.fillStyle = _cachedCockGlow;
    ctx.beginPath();
    ctx.arc(610, 310, 85, 0, Math.PI * 2);
    ctx.fill();

    // B. Warme Natrium-Wartungsleuchte am Flight-Kit-Koffer & Becher des Piloten
    if (!_cachedPilotLamp) {
      _cachedPilotLamp = ctx.createRadialGradient(545, 425, 2, 545, 425, 55);
      _cachedPilotLamp.addColorStop(0, 'rgba(251, 191, 36, 0.42)');
      _cachedPilotLamp.addColorStop(0.6, 'rgba(245, 158, 11, 0.14)');
      _cachedPilotLamp.addColorStop(1, 'rgba(0, 0, 0, 0)');
    }
    ctx.fillStyle = _cachedPilotLamp;
    ctx.beginPath();
    ctx.arc(545, 425, 55, 0, Math.PI * 2);
    ctx.fill();

    // C. Werkbank-Arbeitsplatzbeleuchtung am Werkzeugwagen des Mechanikers
    if (!_cachedMechLamp) {
      _cachedMechLamp = ctx.createRadialGradient(285, 500, 2, 285, 500, 60);
      _cachedMechLamp.addColorStop(0, 'rgba(253, 224, 71, 0.35)');
      _cachedMechLamp.addColorStop(0.6, 'rgba(234, 179, 8, 0.12)');
      _cachedMechLamp.addColorStop(1, 'rgba(0, 0, 0, 0)');
    }
    ctx.fillStyle = _cachedMechLamp;
    ctx.beginPath();
    ctx.arc(285, 500, 60, 0, Math.PI * 2);
    ctx.fill();

    // D. Kaltes Oberlicht / Mondlicht-Schleier durch die Deckenfenster
    if (!_cachedMoonShaft) {
      _cachedMoonShaft = ctx.createLinearGradient(480, 0, 540, BASE_HEIGHT);
      _cachedMoonShaft.addColorStop(0, 'rgba(147, 197, 253, 0.16)');
      _cachedMoonShaft.addColorStop(0.5, 'rgba(96, 165, 250, 0.07)');
      _cachedMoonShaft.addColorStop(1, 'rgba(0, 0, 0, 0)');
    }
    ctx.fillStyle = _cachedMoonShaft;
    ctx.beginPath();
    ctx.moveTo(420, 0);
    ctx.lineTo(540, 0);
    ctx.lineTo(620, BASE_HEIGHT);
    ctx.lineTo(500, BASE_HEIGHT);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  // --- HAUPT-FX RENDER-LOOP -------------------------------------------------

  function renderLiveFX(dt) {
    const bgCtx = fx.bgCtx;
    const ctx = fx.ctx;

    if (bgCtx) {
      bgCtx.setTransform(1, 0, 0, 1, 0, 0);
      bgCtx.clearRect(0, 0, WIDTH, HEIGHT);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, WIDTH, HEIGHT);

    if (!state.animationsEnabled) {
      ctx.save();
      ctx.scale(renderScale, renderScale);
      renderRotatingRotor(ctx, 0);
      ctx.restore();
      return;
    }

    fx.time += dt;

    // Parallax & Kamera-Atmung (Cinematic Micro-Drift)
    updateParallax(dt);

    // --- A. BACKGROUND-FX (Hinter dem A9 Piranha & Montagegerüst) -----------
    if (bgCtx) {
      bgCtx.save();
      bgCtx.scale(renderScale, renderScale);

      // 1. Schwenkender Sicherheitsscheinwerfer an der Wand
      renderSweepingSpotlight(bgCtx, dt);

      // 1b. Volumetrische God-Rays (Lichtkegel von den Deckenoberlichtern)
      renderVolumetricGodRays(bgCtx, dt);

      // 2. Tiefen-Aktivität (Ventilator-Turbine, Bay 05 Scherenbühne, Kryo-Tank-Dunst)
      renderDeepBayActivity(bgCtx, dt);

      // 3. Ferner Boden-Rover (fährt HINTER dem A9 Piranha & Montagegerüst)
      if (state.craneEnabled) {
        renderGroundRover(bgCtx, dt);
      }

      // 4. Patrouillierender Techniker im fernen Hangar (mit Taschenlampe & Datapad)
      renderPatrolTechnician(bgCtx, dt);

      // 5. Marschierende Marine-Patrouille (Human Fireteam im fernen Hangar)
      renderMarinePatrol(bgCtx, dt);

      // 6. Catwalk Encounter (Apostate vs. Defective Loaf auf den Hintergrund-Trägern, HINTER der Schweißdrohne)
      renderCatwalkEncounter(bgCtx, dt);

      // 6b. Schweiß- & Reparaturdrohne (fliegt & schweißt VOR den Catwalk-Trägern / HINTER dem A9 Piranha)
      renderWeldingDrone(bgCtx, dt);

      // 6c. Human Vanguard Panzer Live-Effekte (Hitzeflimmern & Standby-Beacons)
      renderPanzerLiveFX(bgCtx, dt);

      // 6d. Alien-Forschungszelle mit gefangenem Sul Loaf im Stasistank & Xenobiologe
      renderAlienResearchCell(bgCtx, dt);

      bgCtx.restore();
    }

    // --- B. HERO- & VORDERGRUND-FX (Auf/vor dem A9 Piranha) ------------------
    ctx.save();
    ctx.scale(renderScale, renderScale);
    const W = BASE_WIDTH, H = BASE_HEIGHT;

    // 0. A9 Piranha rotierender Hauptrotor & volumetrische Schatten
    renderRotatingRotor(ctx, dt);

    // 1. Automatisierter Deckenkran mit Fracht & God-Ray-Schatten
    if (state.craneEnabled) {
      renderGantryCrane(ctx, dt);
    }

    // 2. Techniker auf dem Catwalk (vorne links) — in bay_layer_monitors gebacken
    // renderCatwalkTechnician(ctx);

    // 2b. Shade-Infiltrator auf dem Catwalk (taktische Zieloptik-Kalibrierung vor Dropzone)
    renderCatwalkShade(ctx, dt);

    // 2c. Deck Officer am 3D-Holo-Tisch auf dem Catwalk (animiert mit Blick auf Holo-Globus)
    renderDeckOfficer(ctx, dt);

    // 2c2. Flatternde Einsatzbefehle am Catwalk-Geländer
    renderFlutteringPapers(ctx, dt);

    // 2d. Human Vanguard Marine Sentry im Vordergrund (Wache mit C-14 Impaler & Scanning)
    renderMarineSentry(ctx, dt);

    // 2e. Hangar-Mechaniker am Werkzeugwagen (Hydro-Ratsche & Arbeitsbewegungen)
    renderHangarMechanic(ctx, dt);

    // 2f. A9 Piranha Pilot beim Pre-Flight Walkaround (Datapad-Bedienung & Sichtprüfung)
    renderA9Pilot(ctx, dt);

    // 2g. Dampf aus dem Thermosbecher des Piloten
    renderThermosSteam(ctx, dt);

    // 2h. High-Detail Tesla Walker im Vordergrund (rechts unten)
    renderTeslaWalker(ctx, dt);

    // 2i. High-Detail Hyper Tesla Walker (beobachtet den Tesla Bot)
    renderHyperTesla(ctx, dt);

    // 2j. High-Detail Devil Patrol (verspielt, Flammenpuffs & Stampf-Event)
    renderDevilPatrol(ctx, dt);

    // 3. 3D-Hologramm-Globus auf dem Catwalk
    if (state.holoEnabled) {
      renderHoloGlobe(ctx);
    }

    // 5. Wabernder Bodennebel (Cryo-Haze)
    if (state.fogEnabled) {
      renderGroundFog(ctx, dt);
    }

    // 6. Boden-Reflexionen (Wet-Floor Pass)
    if (state.floorReflectEnabled) {
      renderFloorReflections(ctx);
    }

    // 7. Staubmotes
    ctx.fillStyle = '#e0f2fe';
    for (const m of fx.dustMotes) {
      m.x += m.vx;
      m.y += m.vy;
      if (m.y > BASE_HEIGHT) { m.y = 20; m.x = 80 + Math.random() * 864; }
      if (m.x < 20 || m.x > BASE_WIDTH - 20) m.vx *= -1;
      ctx.globalAlpha = m.alpha * (0.6 + Math.sin(fx.time * 2.2 + m.x) * 0.4);
      ctx.fillRect(Math.round(m.x), Math.round(m.y), m.size, m.size);
    }
    ctx.globalAlpha = 1;

    // 8. Dampfstoß
    for (let i = fx.steamPuffs.length - 1; i >= 0; i--) {
      const p = fx.steamPuffs[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.97;
      p.vy *= 0.97;
      p.radius += (p.maxRadius - p.radius) * 0.055;
      p.alpha -= p.decay * dt;

      if (p.alpha <= 0) {
        fx.steamPuffs.splice(i, 1);
        continue;
      }

      ctx.save();
      ctx.globalAlpha = Math.max(0, p.alpha);
      const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius);
      grad.addColorStop(0, 'rgba(248, 250, 252, 0.75)');
      grad.addColorStop(0.45, 'rgba(254, 243, 199, 0.35)');
      grad.addColorStop(1, 'rgba(203, 213, 225, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    if (Math.random() < 0.002 && fx.steamPuffs.length === 0) {
      spawnSteamBurst();
    }

    // 10. A9 Piranha: Hydraulik-Atmen, FLIR-Maus-Tracking & Strobes
    renderPiranhaIdle(ctx, dt);

    // 11. Sensor-Optik & Aktiver Laser-Scan
    const sensorX = 555, sensorY = 352;
    const eyePulse = 0.5 + Math.sin(fx.time * 3) * 0.5;
    const eyeColor = state.redAlert ? '#ef4444' : '#00f0ff';
    const eyeGlowColor = state.redAlert ? 'rgba(239, 68, 68, 0.55)' : 'rgba(0, 240, 255, 0.5)';

    ctx.save();
    const eyeGlow = ctx.createRadialGradient(sensorX, sensorY, 0, sensorX, sensorY, 26);
    eyeGlow.addColorStop(0, eyeColor);
    eyeGlow.addColorStop(0.4, eyeGlowColor);
    eyeGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = eyeGlow;
    ctx.globalAlpha = eyePulse;
    ctx.beginPath();
    ctx.arc(sensorX, sensorY, 26, 0, Math.PI * 2);
    ctx.fill();

    if (fx.sensor.scanActiveTimer > 0 || state.redAlert) {
      if (fx.sensor.scanActiveTimer > 0) fx.sensor.scanActiveTimer -= dt;
      fx.sensor.scanSweep += dt * 1.5;

      const sweepAng = Math.PI * 0.85 + Math.sin(fx.sensor.scanSweep) * 0.4;
      const targetDist = 390;
      const scanEndX = sensorX - Math.cos(sweepAng) * targetDist;
      const scanEndY = sensorY + Math.sin(sweepAng) * (targetDist * 0.5);

      ctx.strokeStyle = state.redAlert ? 'rgba(239, 68, 68, 0.9)' : 'rgba(0, 240, 255, 0.9)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(sensorX, sensorY);
      ctx.lineTo(scanEndX, scanEndY);
      ctx.stroke();

      const fanGrad = ctx.createRadialGradient(sensorX, sensorY, 10, scanEndX, scanEndY, 150);
      fanGrad.addColorStop(0, state.redAlert ? 'rgba(239, 68, 68, 0.3)' : 'rgba(0, 240, 255, 0.3)');
      fanGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = fanGrad;
      ctx.beginPath();
      ctx.moveTo(sensorX, sensorY);
      ctx.lineTo(scanEndX - 45, scanEndY - 35);
      ctx.lineTo(scanEndX + 45, scanEndY + 35);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = state.redAlert ? '#ef4444' : '#00f0ff';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.arc(scanEndX, scanEndY, 14 + Math.sin(fx.time * 8) * 3, 0, Math.PI * 2);
      ctx.stroke();
      line(ctx, scanEndX - 22, scanEndY, scanEndX + 22, scanEndY, ctx.strokeStyle, 1);
      line(ctx, scanEndX, scanEndY - 22, scanEndX, scanEndY + 22, ctx.strokeStyle, 1);

      ctx.fillStyle = ctx.strokeStyle;
      ctx.font = '10px monospace';
      ctx.fillText(`TRG: [${Math.round(scanEndX)}, ${Math.round(scanEndY)}]`, scanEndX + 16, scanEndY - 8);
    }
    ctx.restore();

    // 12. Holographisches Cockpit-HUD
    renderHolographicHUD(ctx, fx.pointerX, fx.pointerY);

    // 13. Dynamisches Rim-Lighting
    if (state.rimLightEnabled) {
      renderDynamicRimLight(ctx, fx.pointerX, fx.pointerY);
    }

    // 14. Hitzeflimmern
    if (state.thermalHazeEnabled) {
      renderThermalHaze(ctx);
    }

    // 15. Live Monitor-Anzeigen
    // Monitor 1: Haupt-Diagnosekonsole (A9 Piranha T2 Telemetrie)
    const mon1X = 24, mon1Y = 158, mon1W = 74, mon1H = 50;
    fx.wireframeYaw += dt * 1.4;

    ctx.save();
    ctx.beginPath();
    ctx.rect(mon1X, mon1Y, mon1W, mon1H);
    ctx.clip();
    ctx.fillStyle = '#02111a';
    ctx.fill();

    // Reale Ingame-Header & Card-Stats
    ctx.font = '900 4px monospace';
    ctx.fillStyle = state.redAlert ? '#ef4444' : '#38bdf8';
    ctx.fillText('A9 PIRANHA [T2]', mon1X + 4, mon1Y + 7);

    ctx.font = '700 3px monospace';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('150 HP // 20 GAS', mon1X + 4, mon1Y + 12);

    // Rotierendes 3D Drahtgittermodell
    const cosY = Math.cos(fx.wireframeYaw), sinY = Math.sin(fx.wireframeYaw);
    const mCenterX = mon1X + mon1W / 2, mCenterY = mon1Y + mon1H / 2 + 1;

    ctx.strokeStyle = state.redAlert ? '#ef4444' : '#00f0ff';
    ctx.lineWidth = 1.2;
    for (const edge of HELI_MODEL.edges) {
      const p0 = HELI_MODEL.nodes[edge[0]];
      const p1 = HELI_MODEL.nodes[edge[1]];
      const x0r = p0[0] * cosY - p0[2] * sinY;
      const x1r = p1[0] * cosY - p1[2] * sinY;
      const y0r = p0[1];
      const y1r = p1[1];

      ctx.beginPath();
      ctx.moveTo(mCenterX + x0r * 0.72, mCenterY + y0r * 0.72);
      ctx.lineTo(mCenterX + x1r * 0.72, mCenterY + y1r * 0.72);
      ctx.stroke();
    }

    // Status-Zeile am unteren Rand
    ctx.font = '700 3px monospace';
    ctx.fillStyle = state.redAlert ? '#f87171' : '#22c55e';
    ctx.fillText(state.redAlert ? 'ALERT: SCRAMBLE' : 'FLIGHT READY // DOCK 04', mon1X + 4, mon1Y + mon1H - 7);

    const barProg = (Math.sin(fx.time * 2) + 1) * 0.5;
    ctx.fillStyle = state.redAlert ? '#ef4444' : '#38bdf8';
    ctx.fillRect(mon1X + 4, mon1Y + mon1H - 5, Math.round((mon1W - 8) * barProg), 2.5);
    ctx.restore();

    // Monitor 2: Ressourcen-Dashboard (Plasma Vessel, Fugazi & Vanguard-Status)
    const mon2X = 110, mon2Y = 178, mon2W = 50, mon2H = 34;
    ctx.save();
    ctx.beginPath();
    ctx.rect(mon2X, mon2Y, mon2W, mon2H);
    ctx.clip();
    ctx.fillStyle = '#040d14';
    ctx.fill();

    // Reale Ingame-Wirtschaftsanzeige
    ctx.font = '700 3.2px monospace';
    ctx.fillStyle = '#38bdf8';
    ctx.fillText('VANGUARD HUB', mon2X + 3, mon2Y + 6);

    ctx.font = '700 3px monospace';
    ctx.fillStyle = '#00f0ff';
    ctx.fillText('PLASMA 20/100', mon2X + 3, mon2Y + 12);

    // Animierter Plasma-Füllstandsbalken (20% mit feinem Pulsieren)
    ctx.fillStyle = '#0a1926';
    ctx.fillRect(mon2X + 3, mon2Y + 13.5, mon2W - 6, 3);
    const plasmaP = 0.20 + Math.sin(fx.time * 3) * 0.015;
    ctx.fillStyle = '#00f0ff';
    ctx.fillRect(mon2X + 3, mon2Y + 13.5, Math.round((mon2W - 6) * plasmaP), 3);

    // Fugazi-Währung (Standard 300 Fugazi)
    ctx.font = '700 3px monospace';
    ctx.fillStyle = '#fbbf24';
    ctx.fillText('300 ⬡ FUGAZI', mon2X + 3, mon2Y + 21.5);

    // Aktiver Einsatzstatus
    ctx.font = '600 2.6px monospace';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('STATUS: ONLINE', mon2X + 3, mon2Y + 26);

    // Live Audio-/Sensor-Wellenform am Boden des Monitors
    ctx.strokeStyle = state.redAlert ? '#ef4444' : '#38bdf8';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    for (let wx = 0; wx < mon2W - 6; wx += 2) {
      const wy = Math.sin(fx.time * 12 + wx * 0.6) * 2;
      if (wx === 0) ctx.moveTo(mon2X + 3 + wx, mon2Y + mon2H - 3.5 + wy);
      else ctx.lineTo(mon2X + 3 + wx, mon2Y + mon2H - 3.5 + wy);
    }
    ctx.stroke();
    ctx.restore();

    // Monitor 3: Sektor-Radar & Taktikkarte (Human City, Combat Grade S+)
    const mon3X = 115, mon3Y = 129, mon3S = 32;
    ctx.save();
    ctx.beginPath();
    ctx.rect(mon3X, mon3Y, mon3S, mon3S);
    ctx.clip();
    ctx.fillStyle = '#011214';
    ctx.fill();

    // Taktischer Rang [S+]
    ctx.font = '700 3px monospace';
    ctx.fillStyle = '#22c55e';
    ctx.fillText('WEST [S+]', mon3X + 2, mon3Y + 5.5);

    const rCenterX = mon3X + mon3S / 2, rCenterY = mon3Y + mon3S / 2 + 1;
    const radarAng = fx.time * 3.5;
    ctx.strokeStyle = state.redAlert ? '#ef4444' : '#00f0ff';
    ctx.beginPath();
    ctx.arc(rCenterX, rCenterY, mon3S / 2 - 5, 0, Math.PI * 2);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(rCenterX, rCenterY);
    ctx.lineTo(rCenterX + Math.cos(radarAng) * 11, rCenterY + Math.sin(radarAng) * 11);
    ctx.stroke();
    ctx.fillStyle = '#fbbf24';
    ctx.fillRect(rCenterX + 3, rCenterY - 3, 2.5, 2.5);

    // Kartensektor-Bezeichnung
    ctx.font = '700 2.6px monospace';
    ctx.fillStyle = state.redAlert ? '#f87171' : '#38bdf8';
    ctx.fillText('HUMAN CITY', mon3X + 2, mon3Y + mon3S - 2);
    ctx.restore();

    // 16. Roter Alarm-Beacon
    if (state.redAlert) {
      const beaconX = 460, beaconY = 42;
      const flash = (Math.sin(fx.time * 8) + 1) * 0.5;
      ctx.save();
      const bGlow = ctx.createRadialGradient(beaconX, beaconY, 0, beaconX, beaconY, 150);
      bGlow.addColorStop(0, `rgba(239, 68, 68, ${0.55 * flash})`);
      bGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = bGlow;
      ctx.beginPath();
      ctx.arc(beaconX, beaconY, 150, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // 16.5 Anamorphe Lens Flares (Panavision Streaks auf Highlights)
    renderAnamorphicFlares(ctx);

    // 16.7 Vordergrund Lens Bokeh Discs
    renderBokehDiscs(ctx, dt);

    // 17. Cinematic Bloom Pass
    if (state.bloomEnabled) {
      renderBloomPass(ctx);
    }

    // 18. Teal & Orange Cinema Color Grading & Diorama-Vignette
    renderCinematicGrading(ctx);

    // 19. Night Shift / Pre-Dawn Lighting Pass
    if (state.nightShift) {
      renderNightShiftLighting(ctx);
    }

    ctx.restore();
  }



    function updateParallax(dt) {}
    renderLiveFX(dt);
  }


  triggerCatwalkEncounter() {
    const enc = this.fx?.catwalkEncounter;
    if (!enc) return;
    enc.state = 'apostate_crawl_upper';
    enc.timer = 0;
    enc.apostate.x = BASE_WIDTH + 35;
    enc.apostate.baseY = 82;
    enc.apostate.facing = -1;
    enc.apostate.walkPhase = 0;
    enc.apostate.recoilX = 0;
    enc.apostate.jitterX = 0;
    enc.apostate.lookAngle = 0;
    enc.apostate.eyePulse = 1.0;
    enc.apostate.pauseTimer = 0;
    enc.apostate.struggleShift = 0;
    enc.apostate.jumpTimer = 0;
    enc.apostate._lowerPauseDone = false;

    enc.defective.active = false;
    enc.defective.x = -40;
    enc.defective.baseY = 82;
    enc.defective.facing = 1;
    enc.defective.walkPhase = 0;
    enc.defective.tentacleReach = 0;
    enc.defective.anchorTension = 0;

    enc.spores = [];
    enc.clawSparks = [];
    enc.tetherPulses = [];
  }

  triggerDevilPatrolFire() {
    const dp = this.fx?.devilPatrol;
    if (!dp) return;
    if (dp.state === 'accidental_fire') return;
    dp.state = 'accidental_fire';
    dp.timer = 7.5;
    dp.fireX = dp.x + 18 * dp.facing;
    dp.fireActive = false;
    dp.fireIntensity = 0;
    dp.panicTimer = 0;
    dp.stompCount = 0;
    dp.stompY = 0;
  }
}

